import React, { useState, useEffect, useRef } from "react";
import { Search, Plus, Upload, Download, ChevronLeft, ChevronRight, X, CircleUser, Briefcase, Wallet, FolderOpen, Phone, GraduationCap, Building, Lock, Settings, CheckCircle, FileText, Edit, AlertTriangle, MapPin, Calendar, Shield, ShieldOff, Trash2, Eye, Camera, XCircle, ClipboardList, ClipboardCheck, ScanFace, Heart, History, KeyRound, Users } from "lucide-react";
import api, { BASE } from "../api";
import { useHashParam, readHashParam } from "../utils/hashState";
import { uploadUrl } from "../uploadUrl";
import { K } from "../theme";
import { Av, Bg, Bt, Cd, Inp, Sl, Pr, Sec, Tbl, SearchSelect, MultiSelect } from "../components";
import { ReportingManager } from "../components/ReportingManager";
import { FormRenderer } from "../components/FormRenderer";
import { useFormCatalogue } from "../formCatalog";
import { EmployeeTeamTree } from "../components/EmployeeTeamTree";
import { EmployeeGhiTab } from "../components/EmployeeGhiTab";
import { EmployeeDocsSend } from "../components/EmployeeDocsSend";
import { dayOutcome, dayOutcomeText, halfLeaveText, absentOtherHalfText, absentCount, leaveCount } from "../utils/attendanceDay";
import { EmployeeTrainingReviewTab } from "../components/EmployeeTrainingReviewTab";
import { EmployeeProbationTab } from "../components/EmployeeProbationTab";
import { FormModal } from "../components/FormModal";
import { notify, confirmDialog, promptDialog } from "../notify";
import { validateFaceDetectable } from "../utils/faceValidate";
import { EmployeeSalaryStructure } from "../components/EmployeeSalaryStructure";
import { EmployeeSalarySheet } from "../components/EmployeeSalarySheet";
import { CredentialsModal, credsFrom } from "../components/CredentialsModal";
import { BulkSalaryUploadModal } from "../components/BulkSalaryUploadModal";
import { fmtDMY } from "../dateFormat";
import { usePermissions } from "../usePermissions";

// employee_code is optional — if blank, backend auto-generates AABBBXXXXX.
// employee_type — Full Time / Part Time / Contract / Intern / Consultant.
// Blank → defaults to "Full Time" so an old sheet without the column
// still uploads without the backend "Employee type is required" error.
// present_address / permanent_address map to address_current / address_permanent
// on the employees row (schema names kept for backward-compat).
// join_date is deliberately NOT a bulk column. A spreadsheet gives no way to
// tell 3/4/2023 apart from April 3rd vs March 4th — Excel and Postgres each
// guess differently, and a wrong guess writes a plausible but incorrect date
// with nothing to flag it. Joining dates are set per employee on the profile,
// where the date picker is unambiguous.
const BULK_EMP_HEADERS = ["employee_code", "name", "role", "designation", "department", "office", "company", "employee_type", "gender", "phone", "email", "personal_email", "present_address", "permanent_address", "city", "state", "pin_code"];

const RL_DEFAULT = [{id:"ceo",t:"CEO",lv:1,c:"#FFD700",la:999},{id:"coo",t:"COO",lv:2,c:"#F0BE2E",la:999},{id:"cto",t:"CTO",lv:2,c:"#5AA0F0",la:999},{id:"cfo",t:"CFO",lv:2,c:"#2BD49D",la:999},{id:"chro",t:"CHRO",lv:2,c:"#A378F0",la:999},{id:"vp",t:"VP",lv:3,c:"#F078A2",la:30},{id:"ch",t:"Centre Head",lv:3,c:"#F06268",la:20},{id:"dir",t:"Director",lv:4,c:"#5AA0F0",la:15},{id:"sm",t:"Sr Manager",lv:5,c:"#5F6CF0",la:10},{id:"mgr",t:"Manager",lv:5,c:"#2AD4BC",la:5},{id:"am",t:"Asst Manager",lv:6,c:"#7E89F4",la:3},{id:"tl",t:"Team Lead",lv:6,c:"#30D4EC",la:2},{id:"se",t:"Sr Engineer",lv:7,c:"#C5CBD8",la:0},{id:"eng",t:"Engineer",lv:7,c:"#C5CBD8",la:0},{id:"je",t:"Jr Engineer",lv:8,c:"#8A92A6",la:0},{id:"asc",t:"Associate",lv:8,c:"#8A92A6",la:0},{id:"int",t:"Intern",lv:9,c:"#5B6377",la:0},{id:"ea",t:"Exec Asst",lv:7,c:"#C5CBD8",la:0},{id:"rec",t:"Receptionist",lv:9,c:"#5B6377",la:0},{id:"ob",t:"Office Boy",lv:10,c:"#5B6377",la:0},{id:"sec",t:"Security",lv:10,c:"#5B6377",la:0},{id:"drv",t:"Driver",lv:10,c:"#5B6377",la:0},{id:"acc",t:"Accountant",lv:7,c:"#2BD49D",la:0},{id:"hre",t:"HR Exec",lv:7,c:"#A378F0",la:2},{id:"rcr",t:"Recruiter",lv:7,c:"#F0BE2E",la:0}];
const DEPTS_DEFAULT = ["Engineering", "Product", "Design", "Marketing", "Sales", "HR", "Finance", "Operations", "Legal", "Support", "Admin", "Facilities"];
const OFC_DEFAULT = [{ id: 1, nm: "HQ — New Delhi", city: "New Delhi" }, { id: 2, nm: "Tech Park — Bangalore", city: "Bangalore" }, { id: 3, nm: "Faridabad Hub", city: "Faridabad" }, { id: 4, nm: "Mumbai Branch", city: "Mumbai" }];

const BulkEmployeeModal = ({ roles, offices, departments, companies, onDone, onClose }) => {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [parsing, setParsing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, fail: 0 });
  const [results, setResults] = useState(null);
  const [showLogins, setShowLogins] = useState(false);
  const [err, setErr] = useState("");
  // Existing employees — used to detect duplicate-email / duplicate-code
  // collisions BEFORE hitting the backend one-by-one. Fetched once on
  // mount; small payload (email + employee_id + name only).
  const [existingByEmail, setExistingByEmail] = useState(new Map());
  const [existingCodes, setExistingCodes] = useState(new Set());
  useEffect(() => {
    api.get('/api/employees?page=1&limit=5000').then(d => {
      const arr = d.data || d || [];
      const em = new Map();
      const codes = new Set();
      arr.forEach(e => {
        if (e.em) em.set(String(e.em).toLowerCase(), { id: e.id, name: e.nm });
        if (e.id) codes.add(String(e.id).toUpperCase());
      });
      setExistingByEmail(em);
      setExistingCodes(codes);
    }).catch(() => {});
  }, []);
  const downloadSample = () => {
    const exOfc = offices[0]?.nm || offices[0]?.name || "HQ";
    const exCo = companies[0]?.name || "";
    const exDept = departments[0]?.name || "Technology";
    const exRole = roles[0]?.t || roles[0]?.title || "Employee";
    // employee_code left blank in samples so admin sees the auto-generate
    // path; can be filled in if they want to pin a specific ID like ZU12345.
    // employee_type — sample shows Full Time / Contract so admin sees the enum.
    const rows = [BULK_EMP_HEADERS.join(","),
    `"","Ravi Sharma","${exRole}","Sr Software Engineer","${exDept}","${exOfc}","${exCo}","Full Time","M","9876543210","ravi@tc.in","ravi@gmail.com","B-12 Sector 5, Rohini","B-12 Sector 5, Rohini","New Delhi","Delhi","110001"`,
    `"","Priya Patel","${exRole}","Product Analyst","${exDept}","${exOfc}","${exCo}","Contract","F","9876543211","priya@tc.in","priya@gmail.com","456 Oak St, HSR Layout","78 MG Road, Ahmedabad","Bangalore","Karnataka","560001"`
    ].join("\n");
    const blob = new Blob([rows], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "employees_sample.csv"; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };
  const parseCSV = (text) => {
    const lines = text.split(/\r?\n/).filter(l => l.trim().length);
    if (!lines.length) return { rows: [], headers: [] };
    const parseLine = (line) => { const out = []; let cur = ""; let inQ = false; for (let i = 0; i < line.length; i++) { const ch = line[i]; if (ch === '"') { if (inQ && line[i + 1] === '"') { cur += '"'; i++; } else inQ = !inQ; } else if (ch === ',' && !inQ) { out.push(cur); cur = ""; } else cur += ch; } out.push(cur); return out; };
    const headers = parseLine(lines[0]).map(h => h.trim().toLowerCase().replace(/^\uFEFF/, ""));
    const rows = lines.slice(1).map(line => { const parts = parseLine(line); const o = {}; headers.forEach((h, i) => { o[h] = (parts[i] || "").trim(); }); return o; });
    return { rows, headers };
  };
  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setFileName(f.name); setParsing(true); setErr(""); setResults(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const { rows: parsed, headers } = parseCSV(ev.target.result);
        if (!parsed.length) { setErr("No rows found in CSV."); setRows([]); return; }
        // Detect a stale header. If the sheet was downloaded before we
        // added employee_type + present_address + permanent_address,
        // the columns after `company` all shift left by one \u2014 gender
        // ends up in join_date, phone in gender, etc. Rather than let
        // that fail row-by-row on the backend, warn immediately.
        const missing = ['employee_type', 'present_address', 'permanent_address'].filter(h => !headers.includes(h));
        if (missing.length) {
          setErr(`Your CSV is missing column(s): ${missing.join(', ')}. Download the fresh sample from Step 1 above \u2014 the format changed. (Data preview may show wrong columns because values shifted left.)`);
        }
        setRows(parsed);
      } catch (ex) { setErr("Failed to parse CSV: " + (ex.message || ex)); }
      finally { setParsing(false); }
    };
    reader.onerror = () => { setErr("Failed to read file."); setParsing(false); };
    reader.readAsText(f);
  };
  // Full pre-flight validator — mirrors every check /api/employees POST
  // does server-side, so admin sees ALL problems + values that failed
  // BEFORE any row leaves the browser. Ordered by the failure severity
  // admin would want to fix first.
  const VALID_TYPES = new Set(['full time','fulltime','ft','part time','parttime','pt','contract','contractor','intern','internship','consultant','consulting']);
  const rowIssues = (r) => {
    const issues = [];
    // Required by backend
    if (!r.name || !r.name.trim()) issues.push('name missing');
    if (!r.role || !r.role.trim()) issues.push('role missing');
    // .trim() on the STORED value too — several offices/roles/departments in
    // this org's data carry stray leading/trailing whitespace from however
    // they were entered (e.g. "Parmartham Boys Hostel " with a trailing
    // space), invisible in the UI but enough to break an exact-match
    // comparison against a CSV value that was already trimmed on parse.
    else if (!roles.find(x => ((x.t || x.title || "") + "").trim().toLowerCase() === r.role.toLowerCase())) {
      issues.push(`role "${r.role}" not found in system`);
    }
    if (!r.department || !r.department.trim()) issues.push('department missing');
    else if (!departments.find(x => ((x.name || x) + "").trim().toLowerCase() === r.department.toLowerCase())) {
      issues.push(`department "${r.department}" not found`);
    }
    if (!r.office || !r.office.trim()) issues.push('office missing');
    else if (!offices.find(x => { const n = (x.nm || x.name || "").trim().toLowerCase(); const c = (x.city || "").trim().toLowerCase(); const q = r.office.toLowerCase(); return q && (n === q || c === q); })) {
      issues.push(`office "${r.office}" not found`);
    }
    if (!r.company || !r.company.trim()) issues.push('company missing');
    else if (!companies.find(x => ((x.name || "") + "").trim().toLowerCase() === r.company.toLowerCase())) {
      issues.push(`company "${r.company}" not found`);
    }
    if (!r.email || !r.email.trim()) issues.push('email missing');
    else {
      const em = r.email.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) {
        issues.push(`email "${em}" is not a valid email address`);
      } else if (existingByEmail.has(em.toLowerCase())) {
        const clash = existingByEmail.get(em.toLowerCase());
        issues.push(`email "${em}" already used by ${clash.name} (${clash.id})`);
      }
    }
    // join_date is intentionally not validated here — it is no longer a bulk
    // column. A leftover column from an older sheet is ignored, and flagged
    // as a notice below rather than an issue, so it doesn't block the upload.
    // gender — accepted as M/F/Other/Male/Female — normalised in resolveRow
    if (r.gender) {
      const g = r.gender.trim().toLowerCase();
      if (!['m','f','o','male','female','other'].includes(g)) {
        issues.push(`gender "${r.gender}" is not valid — use M / F / Other`);
      }
    }
    // employee_type — optional (defaults to Full Time); if supplied, check value
    if (r.employee_type && r.employee_type.trim() && !VALID_TYPES.has(r.employee_type.trim().toLowerCase())) {
      issues.push(`employee_type "${r.employee_type}" not recognised — use Full Time / Part Time / Contract / Intern / Consultant`);
    }
    // employee_code — optional; if supplied, check for collision against existing
    if (r.employee_code && r.employee_code.trim()) {
      const code = r.employee_code.trim().toUpperCase();
      if (existingCodes.has(code)) {
        issues.push(`employee_code "${code}" already exists — leave blank to auto-generate, or pick a different code`);
      }
    }
    // pin_code — optional; if supplied, must be 6 digits (Indian PIN)
    if (r.pin_code && r.pin_code.trim() && !/^\d{6}$/.test(r.pin_code.trim())) {
      issues.push(`pin_code "${r.pin_code}" must be 6 digits`);
    }
    // phone — optional; if supplied, allow +, digits, spaces, hyphens — must have >= 10 digits
    if (r.phone && r.phone.trim()) {
      const digits = r.phone.replace(/\D/g, '');
      if (digits.length < 10 || digits.length > 15) {
        issues.push(`phone "${r.phone}" must be 10–15 digits`);
      }
    }
    return issues;
  };
  // File-level checks: duplicates within the same file. Row-level rowIssues
  // catches value/format/existing-collision; this catches "same email appears
  // twice in this upload" — where the first would succeed and the second
  // would 409 on the backend without a clear explanation.
  const fileIssues = () => {
    const out = [];
    const emailSeen = new Map(); // lowercased email → row number
    const codeSeen = new Map();
    rows.forEach((r, i) => {
      const rowNum = i + 2;
      const em = (r.email || '').trim().toLowerCase();
      if (em) {
        if (emailSeen.has(em)) out.push(`Duplicate email "${em}" — appears in rows ${emailSeen.get(em)} and ${rowNum}`);
        else emailSeen.set(em, rowNum);
      }
      const code = (r.employee_code || '').trim().toUpperCase();
      if (code) {
        if (codeSeen.has(code)) out.push(`Duplicate employee_code "${code}" — appears in rows ${codeSeen.get(code)} and ${rowNum}`);
        else codeSeen.set(code, rowNum);
      }
    });
    // De-duplicate the messages themselves (a triple would otherwise fire
    // "row 2 and 3" plus "row 2 and 4" plus "row 3 and 4").
    return [...new Set(out)];
  };
  const resolveRow = (r) => {
    // .trim() the stored side too — see the matching note in rowIssues()
    // above. Without it, a row that passed validation could still resolve
    // to no match here (or vice versa) whenever an office/role/department/
    // company name carries stray leading/trailing whitespace.
    const roleMatch = roles.find(x => ((x.t || x.title || "") + "").trim().toLowerCase() === (r.role || "").toLowerCase());
    const deptMatch = departments.find(x => ((x.name || x) + "").trim().toLowerCase() === (r.department || "").toLowerCase());
    const ofcMatch = offices.find(x => { const n = (x.nm || x.name || "").trim().toLowerCase(); const c = (x.city || "").trim().toLowerCase(); const q = (r.office || "").toLowerCase(); return q && (n === q || c === q); });
    const coMatch = companies.find(x => ((x.name || "") + "").trim().toLowerCase() === (r.company || "").toLowerCase());
    const out = { name: r.name };
    // employee_code (optional) → backend expects `employee_id`. Blank = auto-generate.
    if (r.employee_code) out.employee_id = r.employee_code.trim().toUpperCase();
    if (roleMatch) out.role_id = roleMatch.id;
    if (r.designation) out.designation = r.designation;
    if (deptMatch) out.department = deptMatch.name; else if (r.department) out.department = r.department;
    if (ofcMatch) out.office_id = parseInt(ofcMatch.id);
    if (coMatch) out.company_id = parseInt(coMatch.id);
    // join_date is deliberately NOT forwarded, even if an old sheet still has
    // the column. A spreadsheet date is ambiguous (3/4/2023 is either 3 April
    // or 4 March depending on who reads it) and passing the raw string on let
    // Postgres pick — which silently stored the wrong date whenever both parts
    // were ≤ 12. Joining dates are set on the profile instead.
    if (r.gender) { const g = r.gender.trim().toLowerCase(); out.gender = g.startsWith("m") ? "M" : g.startsWith("f") ? "F" : "O"; }
    // employee_type is a required backend field. Legacy sheets without the
    // column would fail with "Employee type is required" — default to
    // "Full Time" when blank. Normalise common casing to the canonical enum
    // values the schema accepts.
    const rawType = (r.employee_type || '').trim();
    const typeMap = { 'full time':'Full Time', 'fulltime':'Full Time', 'ft':'Full Time',
                      'part time':'Part Time', 'parttime':'Part Time', 'pt':'Part Time',
                      'contract':'Contract', 'contractor':'Contract',
                      'intern':'Intern', 'internship':'Intern',
                      'consultant':'Consultant', 'consulting':'Consultant' };
    out.employee_type = rawType ? (typeMap[rawType.toLowerCase()] || rawType) : 'Full Time';
    ["phone", "email", "personal_email", "city", "state", "pin_code"].forEach(k => { if (r[k]) out[k] = r[k]; });
    // present_address / permanent_address are UI-facing names for the
    // schema's address_current / address_permanent columns. Also accept
    // the legacy `address_current` column name in case HR is uploading
    // an older sheet.
    if (r.present_address)   out.address_current   = r.present_address;
    else if (r.address_current) out.address_current = r.address_current;
    if (r.permanent_address) out.address_permanent = r.permanent_address;
    return out;
  };
  const doUpload = async () => {
    if (!rows.length) return;
    setUploading(true); setErr(""); setProgress({ done: 0, total: rows.length, fail: 0 });
    const errs = []; const newLogins = []; let ok = 0; let fail = 0;
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      if (!r.name) { errs.push({ row: i + 2, err: "name is required" }); fail++; setProgress({ done: i + 1, total: rows.length, fail }); continue; }
      try { const created = await api.post("/api/employees", resolveRow(r)); ok++; const c = credsFrom(created, r.name); if (c) newLogins.push(c); }
      catch (ex) { errs.push({ row: i + 2, err: ex.message || "Failed" }); fail++; }
      setProgress({ done: i + 1, total: rows.length, fail });
    }
    setUploading(false); setResults({ ok, fail, errs, newLogins }); if (newLogins.length) setShowLogins(true);
    if (fail === 0) notify(`${ok} employee${ok === 1 ? "" : "s"} added successfully`, { title: "Saved", variant: "success" });
    else if (ok > 0) notify(`${ok} added, ${fail} failed — see details below`, { title: "Partial Failure", variant: "error" });
    else notify(`Upload failed — ${fail} row${fail === 1 ? "" : "s"} had errors`, { title: "Error", variant: "error" });
    if (ok > 0) onDone?.();
  };
  return <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
    {showLogins && results?.newLogins?.length > 0 && <CredentialsModal items={results.newLogins} title="New logins created" onClose={() => setShowLogins(false)} />}
    <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-4 sm:p-6 w-full max-w-[936px] max-h-[85vh] overflow-auto shadow-[0_20px_60px_rgba(0,0,0,0.15)]" onMouseDown={e => e.stopPropagation()}>
      <div className="flex justify-between mb-4"><span className="text-base font-extrabold text-t0">Bulk Employee Upload</span><button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button></div>
      <div className="p-3 bg-bg2 border border-b0 rounded-[10px] mb-[10px]">
        <div className="text-[14px] font-bold text-t0 mb-1">Step 1 — Download Sample</div>
        <div className="text-[14px] text-t2 mb-2">Download the template, fill employee details using the exact column names, then upload.</div>
        <Bt v="secondary" icon={Download} onClick={downloadSample}>Download Sample CSV</Bt>
      </div>
      <div className="p-3 bg-bg2 border border-b0 rounded-[10px] mb-[10px]">
        <div className="text-[14px] font-bold text-t0 mb-1">Step 2 — Upload CSV</div>
        <div className="text-[14px] text-t2 mb-2">Columns: {BULK_EMP_HEADERS.join(", ")}</div>
        <div className="text-[12px] text-t3 mb-2"><b>employee_code</b> — leave blank to auto-generate; supply a value to pin a specific ID like <span className="font-mono">ZU12345</span>. <b>employee_type</b> — Full Time / Part Time / Contract / Intern / Consultant; blank defaults to Full Time. <b>present_address</b> and <b>permanent_address</b> — free text; leave blank if not known.</div>
        <label className="inline-flex items-center gap-[6px] px-[14px] py-[7px] rounded-[10px] bg-transparent text-t1 border border-b2 text-[14px] font-semibold cursor-pointer">
          <Upload size={12} />{fileName || "Choose CSV File"}
          <input type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" />
        </label>
        {parsing && <div className="text-[14px] text-t2 mt-2">Parsing file...</div>}
      </div>
      {rows.length > 0 && (() => {
        // Pre-flight validation. Runs once against the currently loaded
        // rows and lists every problem, so the admin can fix the whole
        // sheet in one pass rather than clicking Upload and hitting them
        // one-by-one via backend 400s.
        const issuesList = rows.map((r, i) => ({ row: i + 2, name: r.name || '', issues: rowIssues(r) }))
                               .filter(x => x.issues.length > 0);
        const fileErrs = fileIssues();
        const badRoleValues = [...new Set(issuesList.flatMap(x => x.issues.filter(i => i.startsWith('role ')).map(i => i.match(/"([^"]+)"/)?.[1]).filter(Boolean)))];
        return <>
          <div className="p-3 bg-bg2 border border-b0 rounded-[10px] mb-[10px]">
            <div className="text-[14px] font-bold text-t0 mb-[6px]">Preview ({rows.length} row{rows.length === 1 ? "" : "s"})</div>
            <div className="max-h-[180px] overflow-auto border border-b0 rounded-lg bg-bg1">
              <table className="w-full border-collapse text-[14px]">
                <thead><tr className="bg-bg2">{["Code", "Name", "Role", "Dept", "Office", "Company", "Email"].map(h => <th key={h} className="px-2 py-[6px] text-left text-[12px] text-t3 border-b border-b0">{h}</th>)}</tr></thead>
                <tbody>{rows.slice(0, 20).map((r, i) => {
                  const bad = rowIssues(r).length > 0;
                  return <tr key={i} className="border-b border-b0" style={bad ? { background: 'rgba(240,90,90,0.06)' } : undefined}>
                    <td className="px-2 py-[5px] text-t0 font-mono text-[12px]">{r.employee_code || <span className="text-t3">auto</span>}</td>
                    <td className="px-2 py-[5px] text-t0">{r.name || "—"}</td>
                    <td className="px-2 py-[5px] text-t1">{r.role || "—"}</td>
                    <td className="px-2 py-[5px] text-t1">{r.department || "—"}</td>
                    <td className="px-2 py-[5px] text-t1">{r.office || "—"}</td>
                    <td className="px-2 py-[5px] text-t1">{r.company || "—"}</td>
                    <td className="px-2 py-[5px] text-t1">{r.join_date || "—"}</td>
                    <td className="px-2 py-[5px] text-t1">{r.email || "—"}</td>
                  </tr>;
                })}</tbody>
              </table>
            </div>
            {rows.length > 20 && <div className="text-[12px] text-t3 mt-[6px]">Showing first 20 of {rows.length} rows.</div>}
          </div>
          {fileErrs.length > 0 && (
            <div className="p-3 bg-rb border border-rd rounded-[10px] mb-[10px]">
              <div className="text-[14px] font-bold text-r mb-1">File-level problem{fileErrs.length === 1 ? '' : 's'}</div>
              <div className="text-[14px] text-t2 mb-2">These affect the whole sheet — fix in your CSV and re-upload.</div>
              <div className="max-h-[100px] overflow-auto text-[14px] font-mono text-t1 bg-bg1 border border-rd rounded p-2">
                {fileErrs.map((m, i) => <div key={i}>{m}</div>)}
              </div>
            </div>
          )}
          {issuesList.length > 0 && (
            <div className="p-3 bg-rb border border-rd rounded-[10px] mb-[10px]">
              <div className="text-[14px] font-bold text-r mb-1">
                {issuesList.length} row{issuesList.length === 1 ? '' : 's'} need attention before upload
              </div>
              <div className="text-[14px] text-t2 mb-2">
                Fix these in your CSV and re-upload. Upload is disabled until every row is clean.
              </div>
              <div className="max-h-[140px] overflow-auto text-[14px] font-mono text-t1 bg-bg1 border border-rd rounded p-2">
                {issuesList.slice(0, 25).map((x, i) => (
                  <div key={i}>Row {x.row} {x.name && `(${x.name})`}: {x.issues.join(' · ')}</div>
                ))}
                {issuesList.length > 25 && <div className="text-t3">…and {issuesList.length - 25} more.</div>}
              </div>
              {badRoleValues.length > 0 && (
                <div className="text-[14px] text-t2 mt-2">
                  <b>Valid roles in your system:</b> {roles.map(r => r.t || r.title).slice(0, 30).join(', ')}
                  {roles.length > 30 && '…'}. Add missing roles in Settings &gt; Roles first, or use exact spelling.
                </div>
              )}
            </div>
          )}
        </>;
      })()}
      {uploading && <div className="p-[10px] bg-bg2 border border-b0 rounded-[10px] mb-[10px] text-[14px] text-t1">Uploading {progress.done}/{progress.total}... {progress.fail > 0 && <span className="text-r">({progress.fail} failed)</span>}</div>}
      {results && <div className="p-[10px] bg-bg2 border border-b0 rounded-[10px] mb-[10px] text-[14px]">
        <div className="font-bold text-t0 mb-1">Upload Complete</div>
        <div className="text-g">{results.ok} added successfully</div>
        {results.fail > 0 && <div className="text-r">{results.fail} failed</div>}
        {results.newLogins?.length > 0 && <div className="mt-2"><Bt v="secondary" s="sm" icon={KeyRound} onClick={() => setShowLogins(true)}>View / copy the {results.newLogins.length} new login{results.newLogins.length === 1 ? "" : "s"}</Bt></div>}
        {results.errs.slice(0, 5).map((e, i) => <div key={i} className="text-[12px] text-r mt-[2px]">Row {e.row}: {e.err}</div>)}
      </div>}
      {err && <div className="text-[14px] text-r mb-2">{err}</div>}
      {(() => {
        const hasRowIssues = rows.length > 0 && rows.some(r => rowIssues(r).length > 0);
        const hasFileIssues = rows.length > 0 && fileIssues().length > 0;
        const hasIssues = hasRowIssues || hasFileIssues;
        return <div className="flex gap-[6px] mt-3">
          <Bt full icon={Upload} onClick={doUpload} disabled={!rows.length || uploading || hasIssues}>
            {uploading ? `Uploading ${progress.done}/${progress.total}...`
              : hasIssues ? 'Fix errors above to enable upload'
              : `Upload ${rows.length || ""} Employee${rows.length === 1 ? "" : "s"}`.trim()}
          </Bt>
          <Bt v="secondary" full onClick={onClose}>Close</Bt>
        </div>;
      })()}
    </div>
  </div>;
};

// Regularization history for one employee. Admin/HR only — rendered on the
// Settings tab of the Employee Profile. Fetches all regularization rows the
// employee has ever had (any status, any period), with the pre-regularization
// snapshot alongside so HR can reconstruct "what the day looked like before
// Export modal — picks filters, then hits GET /api/employees/export.
// The backend re-applies the caller's centre scope, so a Centre Head or
// HR Head restricted to specific offices/companies only ever exports
// employees within their scope — the UI doesn't need to gate that.
const ExportEmployeesModal = ({ companies = [], offices = [], role, onClose }) => {
  const [f, setF] = useState({
    from_join_date: '', to_join_date: '',
    company: 'all', office: 'all', status: 'active',
  });
  const [downloading, setDownloading] = useState(false);
  const [err, setErr] = useState('');

  const download = () => {
    setDownloading(true); setErr('');
    const qs = new URLSearchParams();
    if (f.from_join_date) qs.set('from_join_date', f.from_join_date);
    if (f.to_join_date)   qs.set('to_join_date',   f.to_join_date);
    if (f.company !== 'all') qs.set('company', f.company);
    if (f.office  !== 'all') qs.set('office',  f.office);
    if (f.status  !== 'all') qs.set('status',  f.status === 'inactive' ? 'Inactive' : 'Active');
    qs.set('token', localStorage.getItem('hrms_token'));
    const url = `${BASE}/api/employees/export?${qs.toString()}`;
    // Open in new tab — browser handles the download attachment header.
    window.open(url, '_blank');
    setTimeout(() => { setDownloading(false); onClose(); }, 400);
  };

  const lbl = "text-[14px] text-t2 mb-[3px]";
  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-4 sm:p-5 w-full max-w-[796px]" onMouseDown={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="text-[17px] font-extrabold text-t0 flex items-center gap-2">
            <Download size={16} /> Download Employees
          </div>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18}/></button>
        </div>
        <div className="text-[14px] text-t3 mb-3">
          Downloads an Excel of the employees you have access to, filtered by the values below.
          {["centre_head", "hr_head", "centre_hr", "accounts"].includes(role) && <> Your centre / company scope is
            applied server-side — the download only includes rows you can see in the list above.</>}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div>
            <div className={lbl}>Joining Date — from</div>
            <Inp type="date" value={f.from_join_date} onChange={v => setF(p => ({ ...p, from_join_date: v }))} />
          </div>
          <div>
            <div className={lbl}>Joining Date — to</div>
            <Inp type="date" value={f.to_join_date} onChange={v => setF(p => ({ ...p, to_join_date: v }))} />
          </div>
          <div>
            <div className={lbl}>Company / Brand</div>
            <Sl options={[{ v: 'all', l: 'All (I have access to)' }, ...companies.map(c => ({ v: String(c.id), l: c.name }))]}
                value={f.company} onChange={v => setF(p => ({ ...p, company: v }))} />
          </div>
          <div>
            <div className={lbl}>Location / Office</div>
            <Sl options={[{ v: 'all', l: 'All (I have access to)' }, ...offices.map(o => ({ v: String(o.id), l: (o.nm || o.name) + (o.city ? ' — ' + o.city : '') }))]}
                value={f.office} onChange={v => setF(p => ({ ...p, office: v }))} />
          </div>
          <div>
            <div className={lbl}>Status</div>
            <Sl options={[
              { v: 'active',   l: 'Active only' },
              { v: 'inactive', l: 'Inactive only' },
              { v: 'all',      l: 'Active + Inactive' },
            ]} value={f.status} onChange={v => setF(p => ({ ...p, status: v }))} />
          </div>
        </div>

        {err && <div className="text-[14px] text-r mb-2">{err}</div>}
        <div className="flex gap-2">
          <Bt full icon={Download} onClick={download} disabled={downloading}>
            {downloading ? 'Downloading…' : 'Download Excel'}
          </Bt>
          <Bt v="secondary" full onClick={onClose}>Cancel</Bt>
        </div>
      </div>
    </div>
  );
};

// the overwrite".
// Compact banner at the top of the employee drawer. Three states:
//   * No invite yet         → "Send Intake Link" button
//   * Pending (unsent yet)  → shows the URL to copy + Cancel; nothing to review
//   * Submitted             → "Review submission" opens ReviewIntakeDrawer
//   * Approved / Rejected   → shows the outcome + timestamp; button to start a
//                              fresh cycle (rejected → Send new invite;
//                              approved → hidden since employee record already
//                              carries the intake data)
const IntakeInviteCard = ({ employeeId, employeeName }) => {
  const [invites, setInvites] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const [reviewing, setReviewing] = React.useState(null); // submission id
  const [copyText, setCopyText] = React.useState('');
  const [confirmCancel, setConfirmCancel] = React.useState(null); // id awaiting 2nd click

  const load = () => api.get(`/api/employees/${employeeId}/intake-invites`)
    .then(d => setInvites(d.invites || []))
    .catch(() => setInvites([]));
  // Wrap in a statement-body arrow so useEffect never receives the
  // Promise `load()` returns. React would try to call it as a cleanup
  // function on unmount and blow up with "n is not a function".
  React.useEffect(() => { load(); }, [employeeId]);

  const flash = (m, isErr = false) => { setMsg((isErr ? 'Error: ' : '') + m); setTimeout(() => setMsg(''), 3500); };

  const sendInvite = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/api/employees/${employeeId}/intake-invite`, {});
      setCopyText(r.url);
      const successMsg = r.emailed_to ? `Invite sent to ${r.emailed_to}. Link is also copied below.` : 'Invite created. Copy the link below to share with the employee.';
      flash(successMsg);
      notify(successMsg, { title: 'Invite Sent', variant: 'success' });
      load();
    } catch (e) { flash(e.message || 'Failed', true); notify(e.message || 'Failed to send intake invite', { title: 'Error', variant: 'error' }); }
    finally { setBusy(false); }
  };

  // Two-click cancel — first click arms the button (turns amber, label
  // changes), second click within 4 seconds actually cancels. Avoids the
  // native confirm dialog whose OK/Cancel wording confuses users on a
  // button that itself says Cancel.
  const cancelInvite = async (id) => {
    if (confirmCancel !== id) {
      setConfirmCancel(id);
      setTimeout(() => setConfirmCancel(cur => cur === id ? null : cur), 4000);
      return;
    }
    setConfirmCancel(null);
    setBusy(true);
    try { await api.post(`/api/employees/${employeeId}/intake-invites/${id}/cancel`, {}); flash('Invite cancelled. The link is now dead.'); notify('Invite cancelled', { title: 'Cancelled', variant: 'success' }); load(); }
    catch (e) { flash(e.message || 'Cancel failed', true); notify(e.message || 'Cancel failed', { title: 'Error', variant: 'error' }); }
    finally { setBusy(false); }
  };

  const copy = (url) => {
    setCopyText(url);
    try { navigator.clipboard.writeText(url); flash('Link copied to clipboard'); } catch (_) { flash('Select the box below and copy manually', true); }
  };

  if (invites == null) return null;

  const active = invites.find(i => i.status === 'Pending' || i.status === 'Submitted');
  const submitted = invites.find(i => i.status === 'Submitted');
  const latestReviewed = invites.find(i => i.status === 'Approved' || i.status === 'Rejected');
  const shareUrl = active?.token
    ? `${window.location.origin}/#intake?token=${active.token}`
    : '';

  return (
    <div className="mb-4 p-3 rounded-xl border border-b0 bg-bg2">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-bold text-t0">Employee Intake</div>
          <div className="text-[14px] text-t3 mt-[2px]">
            {submitted ? (
              <>{employeeName} submitted their intake on {fmtDMY(submitted.submitted_at)}. Review + approve to merge into the record.</>
            ) : active ? (
              <>Invite is <b className="text-a">pending</b>, sent {fmtDMY(active.created_at)}. Expires {fmtDMY(active.expires_at)}.</>
            ) : latestReviewed ? (
              <>Last intake was <b className={latestReviewed.status === 'Approved' ? 'text-g' : 'text-r'}>{latestReviewed.status}</b> by {latestReviewed.reviewed_by} on {fmtDMY(latestReviewed.reviewed_at)}.</>
            ) : (
              <>Share a one-time link and let {employeeName} fill their own details + upload docs. HR reviews before it goes on the record.</>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {submitted && <Bt v="success" s="sm" icon={CheckCircle} onClick={() => setReviewing(submitted.id)}>Review Submission</Bt>}
          {!active && !submitted && <Bt s="sm" icon={Upload} onClick={sendInvite} disabled={busy}>{busy ? 'Sending…' : 'Send Intake Link'}</Bt>}
          {active && !submitted && <>
            <Bt v="secondary" s="sm" onClick={() => copy(shareUrl)}>Copy Link</Bt>
            <Bt v={confirmCancel === active.id ? 'warning' : 'danger'} s="sm" icon={X}
                onClick={() => cancelInvite(active.id)} disabled={busy}>
              {confirmCancel === active.id ? 'Click again to confirm' : 'Cancel'}
            </Bt>
          </>}
        </div>
      </div>
      {(active && !submitted) && (
        <div className="mt-2">
          <input readOnly value={shareUrl} className="w-full px-2 py-1 text-[14px] font-mono bg-bg1 border border-b0 rounded" onFocus={e => e.target.select()} />
          <div className="text-[12px] text-t3 mt-1">
            Employee opens this URL, fills the form once, and it becomes dead the moment they submit. Single-use so they can't quietly rewrite details later.
          </div>
        </div>
      )}
      {copyText && !active && (
        <div className="mt-2"><input readOnly value={copyText} className="w-full px-2 py-1 text-[14px] font-mono bg-bg1 border border-b0 rounded" onFocus={e => e.target.select()} /></div>
      )}
      {msg && <div className={`mt-2 text-[14px] font-semibold ${msg.startsWith('Error') ? 'text-r' : 'text-g'}`}>{msg}</div>}
      {reviewing && <IntakeReviewDrawer id={reviewing} onClose={() => setReviewing(null)} onDone={() => { setReviewing(null); load(); }} />}
    </div>
  );
};

// Review drawer — full-screen overlay showing every field + doc the
// employee submitted. HR approves (merges into employee record) or rejects
// with a reason. Approve/reject go through the backend transaction.
const IntakeReviewDrawer = ({ id, onClose, onDone }) => {
  const [sub, setSub] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [rejectMode, setRejectMode] = React.useState(false);
  const [reason, setReason] = React.useState('');
  const [msg, setMsg] = React.useState('');

  React.useEffect(() => {
    api.get(`/api/intake-submissions/${id}`).then(setSub).catch(() => setSub({ error: 'Failed to load' }));
  }, [id]);

  const flash = (m, isErr = false) => { setMsg((isErr ? 'Error: ' : '') + m); setTimeout(() => setMsg(''), 4000); };

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/api/intake-submissions/${id}/approve`, {});
      notify('Submission approved and merged into employee record', { title: 'Approved', variant: 'success' });
      onDone();
    } catch (e) { flash(e.message || 'Approve failed', true); notify(e.message || 'Approve failed', { title: 'Error', variant: 'error' }); }
    finally { setBusy(false); }
  };
  const doReject = async () => {
    if (reason.trim().length < 5) { flash('Reason must be at least 5 characters', true); return; }
    setBusy(true);
    try {
      await api.post(`/api/intake-submissions/${id}/reject`, { reason: reason.trim() });
      notify('Submission rejected', { title: 'Rejected', variant: 'success' });
      onDone();
    } catch (e) { flash(e.message || 'Reject failed', true); notify(e.message || 'Reject failed', { title: 'Error', variant: 'error' }); }
    finally { setBusy(false); }
  };

  if (!sub) return null;
  if (sub.error) return <div className="fixed inset-0 animate-fadeIn bg-black/40 z-[220] flex items-center justify-center p-4" onClick={onClose}><div className="bg-bg1 rounded-xl p-6 text-r text-[14px]">{sub.error}</div></div>;

  const d = sub.submission_data || {};
  const docs = sub.doc_paths || {};
  const F = ({ l, v }) => <div className="flex justify-between py-[4px] border-b border-b0 gap-3"><span className="text-[14px] text-t3">{l}</span><span className="text-[14px] text-t0 font-semibold text-right break-all max-w-[60%]">{v || '—'}</span></div>;
  const Section = ({ title, children }) => <div className="mb-4"><div className="text-[14px] font-bold text-t2 uppercase tracking-wide mb-1">{title}</div>{children}</div>;

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/50 z-[220] flex items-stretch justify-center p-3" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 rounded-2xl w-full max-w-[1047px] max-h-[92vh] overflow-auto shadow-2xl" onMouseDown={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-b0 sticky top-0 bg-bg1 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[16px] font-extrabold text-t0">Intake Review — {sub.employee_name} ({sub.employee_id})</div>
            <div className="text-[14px] text-t3 mt-[2px]">Submitted {fmtDMY(sub.submitted_at)} {(sub.submitted_at + '').slice(11, 16)}</div>
          </div>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer p-1"><X size={18} /></button>
        </div>

        <div className="p-5">
          <Section title="Basic">
            <F l="Salutation" v={d.basic?.salutation} />
            <F l="First Name" v={d.basic?.first_name} />
            <F l="Last Name"  v={d.basic?.last_name} />
            <F l="Date of Birth" v={fmtDMY(d.basic?.dob)} />
            <F l="Gender" v={d.basic?.gender} />
            <F l="Marital Status" v={d.basic?.marital_status} />
            <F l="Father's Name" v={d.basic?.father_name} />
          </Section>
          <Section title="Contact">
            <F l="Personal Email" v={d.contact?.personal_email} />
            <F l="Mobile" v={d.contact?.phone} />
            <F l="Present Address" v={d.contact?.address_current} />
            <F l="Permanent Address" v={d.contact?.address_permanent} />
          </Section>
          <Section title="Statutory">
            <F l="PAN" v={d.statutory?.pan_number} />
            <F l="Aadhaar" v={d.statutory?.aadhar_number} />
            <F l="UAN" v={d.statutory?.uan_number} />
          </Section>
          <Section title="Education">
            <F l="Highest Qualification" v={d.education?.highest_qualification} />
            <F l="Degree" v={d.education?.degree} />
            <F l="University" v={d.education?.university} />
            <F l="Passing Year" v={d.education?.passing_year} />
            <F l="Percentage / CGPA" v={d.education?.percentage_cgpa} />
          </Section>
          <Section title="Emergency Contact">
            <F l="Name" v={d.emergency?.contact_name} />
            <F l="Relation" v={d.emergency?.relation} />
            <F l="Phone" v={d.emergency?.phone} />
          </Section>
          <Section title="Documents">
            {Object.keys(docs).length === 0 ? (
              <div className="text-[14px] text-t3 italic">No documents uploaded.</div>
            ) : (
              <div className="grid gap-1">
                {Object.entries(docs).map(([type, path]) => (
                  <div key={type} className="flex items-center justify-between py-[4px]">
                    <span className="text-[14px] text-t1">{type}</span>
                    <a href={`${api.getToken ? '' : ''}${window.location.origin.replace(':3003', ':5000')}/${path}`} target="_blank" rel="noreferrer" className="text-[14px] text-p font-semibold">View</a>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {rejectMode ? (
            <div className="p-3 rounded-lg bg-rb border border-rd mb-3">
              <div className="text-[14px] font-bold text-r mb-1">Reason for rejection</div>
              <textarea rows={3} value={reason} onChange={e => setReason(e.target.value)}
                placeholder="Explain what needs fixing — HR will share this with the employee."
                className="w-full px-2 py-1 bg-bg1 border border-b0 rounded text-[14px] outline-none resize-y" />
              <div className="flex gap-2 mt-2">
                <Bt v="danger" s="sm" onClick={doReject} disabled={busy}>{busy ? 'Rejecting…' : 'Confirm Reject'}</Bt>
                <Bt v="secondary" s="sm" onClick={() => setRejectMode(false)} disabled={busy}>Cancel</Bt>
              </div>
            </div>
          ) : (
            <div className="flex gap-2 pt-3 border-t border-b0">
              <Bt v="success" icon={CheckCircle} onClick={approve} disabled={busy}>{busy ? 'Merging…' : 'Approve — merge into employee record'}</Bt>
              <Bt v="danger" onClick={() => setRejectMode(true)} disabled={busy}>Reject</Bt>
            </div>
          )}
          {msg && <div className={`mt-2 text-[14px] font-semibold ${msg.startsWith('Error') ? 'text-r' : 'text-g'}`}>{msg}</div>}
        </div>
      </div>
    </div>
  );
};

const EmpRegHistoryCard = ({ empId }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let alive = true;
    setRows(null);
    api.get('/api/attendance/regularizations/history?employee_id=' + encodeURIComponent(empId))
      .then(d => { if (alive) setRows(Array.isArray(d) ? d : []); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [empId]);

  const fmt = (t) => t ? new Date(t).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: (typeof window !== 'undefined' && window.__APP_TZ) || "Asia/Kolkata" }) : "—";
  const statusColor = (s) => s === "Approved" ? K.g : s === "Rejected" ? K.r : K.a;

  return <Cd style={{ marginTop: 12 }}>
    <Sec icon={FileText} title={`Regularization History${rows ? ` (${rows.length})` : ""}`} />
    {rows === null ? (
      <div className="text-center py-4 text-t3 text-[14px]">Loading…</div>
    ) : rows.length === 0 ? (
      <div className="text-center py-4 text-t3 text-[14px]">No regularization requests for this employee</div>
    ) : (
      <div className="flex flex-col gap-[6px]">
        {rows.map(r => {
          const snap = r.pre_regularization_snapshot;
          return <div key={r.id} className="p-[10px] rounded-[10px] bg-bg2 border border-b0">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
              <div className="flex items-center gap-2">
                <span className="font-mono text-[14px] font-semibold text-t0">{fmtDMY(r.date)}</span>
                <Bg c={statusColor(r.status)} dot sm>{r.status}</Bg>
              </div>
              <div className="text-[12px] text-t3">{r.approver_name ? `by ${r.approver_name}` : "—"}{r.created_at ? ` · submitted ${fmtDMY(r.created_at)}` : ""}</div>
            </div>
            <div className="text-[14px] text-t1 mb-1">{r.reason || "—"}</div>
            <div className="text-[14px] text-t2">Requested: <strong>{fmt(r.requested_check_in)}</strong> → <strong>{fmt(r.requested_check_out)}</strong></div>
            {r.approval_remarks && <div className="text-[14px] text-t2 mt-1"><span className="text-t3">Approver note:</span> {r.approval_remarks}</div>}
            {snap && (snap.had_record === false
              ? <div className="text-[12px] text-t3 italic mt-1">Before: no attendance record (employee hadn't checked in)</div>
              : <div className="text-[12px] text-t3 mt-1"><span className="font-semibold">Before:</span> {snap.status || "—"}{snap.work_hours != null && ` · ${Number(snap.work_hours).toFixed(2)}h`}{snap.check_in && ` · in ${fmt(snap.check_in)}`}{snap.check_out && ` · out ${fmt(snap.check_out)}`}{snap.is_late && <span className="text-a"> · was late</span>}</div>
            )}
          </div>;
        })}
      </div>
    )}
  </Cd>;
};

// Admin-only "what changed on this profile and by whom" — reads the
// shared audit_log (matched on this employee's id/name appearing in the
// free-text action, since audit_log has no structured entity link), so
// it surfaces every module's actions that touched this person, not just
// Basic Info field edits.
const EmpActivityLogCard = ({ empId }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let alive = true;
    setRows(null);
    api.get('/api/employees/' + encodeURIComponent(empId) + '/activity-log')
      .then(d => { if (alive) setRows(d?.rows || []); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [empId]);

  const fmtWhen = (t) => t ? `${fmtDMY(t)}, ${new Date(t).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}` : "—";

  return <Cd>
    <Sec icon={History} title={`Activity Log${rows ? ` (${rows.length})` : ""}`} />
    <div className="text-[13px] text-t3 -mt-1 mb-3">Every recorded action mentioning this employee, most recent first — up to the last 200.</div>
    {rows === null ? (
      <div className="text-center py-4 text-t3 text-[14px]">Loading…</div>
    ) : rows.length === 0 ? (
      <div className="text-center py-4 text-t3 text-[14px]">No activity recorded for this employee yet.</div>
    ) : (
      <div className="flex flex-col gap-[6px] max-h-[560px] overflow-y-auto">
        {rows.map(r => (
          <div key={r.id} className="p-[10px] rounded-[10px] bg-bg2 border border-b0">
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className="text-[13px] font-bold text-t0">{r.username || "system"}</span>
              <span className="text-[12px] text-t3 font-mono">{fmtWhen(r.timestamp)}</span>
            </div>
            <div className="text-[14px] text-t1">{r.action}</div>
          </div>
        ))}
      </div>
    )}
  </Cd>;
};

// Payroll tab on the employee detail drawer. Two stacked sections:
//   1) Salary revision timeline — every change to basic/HRA/allowances
//      from /api/employees/:id/salary-history. Shows effective_from →
//      effective_to (or "Current"), the breakdown, who changed it, why.
//   2) Past payslips — pulls /api/payroll/slips?employee_id=ID.
//      Each row has period, gross, deductions, net, status, and a PDF
//      download. Employees see their own; HR/admin see anyone in scope.
const EmployeePayrollTab = ({ employeeId, role }) => {
  const [history, setHistory] = useState(null);
  const [slips, setSlips] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get(`/api/employees/${employeeId}/salary-history`).catch(() => []),
      api.get(`/api/payroll/slips?employee_id=${encodeURIComponent(employeeId)}`).catch(() => []),
    ]).then(([h, s]) => {
      if (!alive) return;
      setHistory(Array.isArray(h) ? h : []);
      setSlips(Array.isArray(s) ? s : []);
    }).catch(e => { if (alive) setErr(e.message || 'Failed to load'); });
    return () => { alive = false; };
  }, [employeeId]);

  const fmt = (n) => {
    if (n == null || n === '') return '—';
    return '₹ ' + Number(n).toLocaleString('en-IN');
  };
  const fmtDate = (d) => d ? fmtDMY(d) : '—';

  const downloadSlip = async (period) => {
    try {
      const t = localStorage.getItem('hrms_token');
      const r = await fetch(`${BASE}/api/payroll/slips/${period}/pdf?employee_id=${encodeURIComponent(employeeId)}`,
                            { headers: { Authorization: 'Bearer ' + t } });
      if (!r.ok) throw new Error('Slip not available');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `payslip-${employeeId}-${period}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { notify(e.message || 'Download failed', { title: 'Error', variant: 'error' }); }
  };

  if (err) return <div className="text-[14px] text-r p-3">{err}</div>;
  if (history === null || slips === null) return <div className="text-[14px] text-t3 p-3">Loading payroll…</div>;

  return <div>
    <div className="text-[14px] font-bold text-t0 mb-2">Salary Revision Timeline</div>
    {history.length === 0 ? (
      <div className="text-[14px] text-t3 mb-4">No revisions on record yet.</div>
    ) : (
      <Tbl cols={[
        { k: 'eff', l: 'Effective', r: r => <span className="text-[14px]">{fmtDate(r.effective_from)} → {r.effective_to ? fmtDate(r.effective_to) : <span className="text-g font-semibold">Current</span>}</span> },
        { k: 'ctc', l: 'CTC',   r: r => <span className="text-[14px] font-semibold text-t0">{fmt(r.ctc_annual)}</span> },
        { k: 'bas', l: 'Basic', r: r => <span className="text-[14px] text-t2">{fmt(r.basic_salary)}</span> },
        { k: 'hra', l: 'HRA',   r: r => <span className="text-[14px] text-t2">{fmt(r.hra)}</span> },
        { k: 'all', l: 'Allow', r: r => <span className="text-[14px] text-t2">{fmt(r.allowances)}</span> },
        { k: 'rsn', l: 'Reason', r: r => <span className="text-[12px] text-t3">{r.reason || '—'}</span> },
        { k: 'by',  l: 'By',    r: r => <span className="text-[12px] text-t3">{r.changed_by_name || r.changed_by || 'system'}</span> },
      ]} data={history} />
    )}

    <div className="text-[14px] font-bold text-t0 mt-4 mb-2">Payslips</div>
    {slips.length === 0 ? (
      <div className="text-[14px] text-t3">No payslips yet — first run will populate this list.</div>
    ) : (
      <Tbl cols={[
        { k: 'period', l: 'Period', r: r => <span className="text-[15px] font-bold text-t0">{r.period}</span> },
        { k: 'gross',  l: 'Gross',  r: r => <span className="text-[14px] text-t2">{fmt(r.gross)}</span> },
        { k: 'ded',    l: 'Deductions', r: r => <span className="text-[14px] text-r">{fmt(r.totalDed)}</span> },
        { k: 'net',    l: 'Net Pay',r: r => <span className="text-[14px] font-bold text-g">{fmt(r.net)}</span> },
        { k: 'days',   l: 'Days',   r: r => <span className="text-[12px] text-t3">{r.presentDays}/{r.workDays}{r.lopDays > 0 ? ` · LOP ${r.lopDays}d` : ''}</span> },
        { k: 'st',     l: 'Status', r: r => <Bg c={r.status === 'Paid' ? K.g : r.status === 'Generated' ? K.bl : K.a} sm dot>{r.status || '—'}</Bg> },
        { k: 'a',      l: '',       r: r => <Bt v="ghost" s="xs" icon={FileText} onClick={() => downloadSlip(r.period)}>PDF</Bt> },
      ]} data={slips} />
    )}
  </div>;
};

// Top-of-Employees-page intake tracker for HR. The banner calls out what
// needs reviewing; the modal behind it lists the WHOLE pipeline — invited
// but not yet filled, submitted, approved, rejected, expired — because
// "which employee still hasn't sent their details?" is the question HR
// actually asks, and a review-only queue can't answer it. Also opened
// directly via a notification's #employees?tab=intake&subId=… link
// (the `openId` prop) regardless of what the banner itself would show.
const INTAKE_STATUS = {
  Pending:   { c: K.a,  label: 'Awaiting employee' },
  Submitted: { c: K.bl, label: 'Pending review' },
  Approved:  { c: K.g,  label: 'Approved' },
  Rejected:  { c: K.r,  label: 'Rejected' },
  Expired:   { c: K.t3, label: 'Link expired' },
  Cancelled: { c: K.t3, label: 'Cancelled' },
};
// What HR does next for a row that isn't waiting on them to review.
const INTAKE_NEXT = {
  Pending:   'Waiting on employee',
  Expired:   'Re-invite from profile',
  Cancelled: 'Re-invite from profile',
  Rejected:  'Re-invite from profile',
  Approved:  'Merged into record',
};
const INTAKE_ORDER = ['Submitted', 'Pending', 'Expired', 'Approved', 'Rejected', 'Cancelled'];

const d10 = v => (v ? fmtDMY(v) : '—');

// One row's timeline, so each status reads as a story rather than a label:
// when it was invited, when they sent it, who signed it off.
const intakeTrail = (s) => {
  const parts = [`Invited ${d10(s.created_at)}${s.created_by ? ` by ${s.created_by}` : ''}`];
  if (s.submitted_at) parts.push(`Submitted ${d10(s.submitted_at)}`);
  else if (s.status === 'Pending') parts.push(`Not filled yet · link valid till ${d10(s.expires_at)}`);
  else if (s.status === 'Expired') parts.push(`Never filled · link expired ${d10(s.expires_at)}`);
  if (s.reviewed_at) parts.push(`${s.status} ${d10(s.reviewed_at)}${s.reviewed_by ? ` by ${s.reviewed_by}` : ''}`);
  return parts.join(' · ');
};

const IntakePendingBanner = ({ role, openId }) => {
  const { can: pcan } = usePermissions(true);
  const canReview = pcan('onboarding', null, 'intake_review', 'edit');
  const [subs, setSubs] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [reviewing, setReviewing] = React.useState(null);
  const [filter, setFilter] = React.useState('Submitted');
  const [q, setQ] = React.useState('');

  const load = React.useCallback(() => {
    if (!canReview) return;
    api.get('/api/intake-submissions?status=all')
      .then(d => setSubs(d.rows || d.submissions || []))
      .catch(() => setSubs([]));
  }, [canReview]);
  React.useEffect(load, [load]);

  // A notification's #employees?tab=intake&subId=… link lands here — open
  // that submission's review drawer directly, regardless of whether the
  // rest of the banner would otherwise render (e.g. it's since been pulled
  // out of the "Submitted" queue by another reviewer).
  React.useEffect(() => { if (canReview && openId) setReviewing(openId); }, [canReview, openId]);

  const counts = React.useMemo(() => {
    const c = {};
    subs.forEach(s => { c[s.status] = (c[s.status] || 0) + 1; });
    return c;
  }, [subs]);

  const toReview = counts.Submitted || 0;
  const waiting  = counts.Pending || 0;
  // Expired counts as outstanding too — a link that ran out is an employee
  // who never sent their details, which is exactly what HR needs to chase.
  const stale    = counts.Expired || 0;

  const shown = React.useMemo(() => {
    const term = q.trim().toLowerCase();
    return subs
      .filter(s => filter === 'all' || s.status === filter)
      .filter(s => !term || `${s.employee_id} ${s.employee_name || ''}`.toLowerCase().includes(term));
  }, [subs, filter, q]);

  if (!canReview) return null;
  // Nothing outstanding at all → no banner, UNLESS a notification's deep
  // link is trying to open one specific submission right now (it may have
  // since been pulled out of every bucket the banner cares about).
  if (!toReview && !waiting && !stale) {
    return reviewing ? (
      <IntakeReviewDrawer id={reviewing} onClose={() => setReviewing(null)} onDone={() => { setReviewing(null); load(); }} />
    ) : null;
  }

  return (
    <>
      <div className="mb-3 p-3 rounded-xl border border-ad bg-ab flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[14px] font-bold text-t0 flex items-center gap-2 flex-wrap">
            {toReview > 0
              ? `${toReview} intake submission${toReview === 1 ? '' : 's'} pending review`
              : waiting > 0
                ? `${waiting} intake form${waiting === 1 ? '' : 's'} awaiting employees`
                : `${stale} intake link${stale === 1 ? '' : 's'} expired unfilled`}
            {toReview > 0 && waiting > 0 && <Bg c={K.a} sm dot>{waiting} not filled yet</Bg>}
            {stale > 0 && (toReview > 0 || waiting > 0) && <Bg c={K.t3} sm dot>{stale} expired</Bg>}
          </div>
          <div className="text-[14px] text-t2 mt-[2px]">
            {toReview > 0
              ? 'Employees have filled their own details. Review and approve to merge into their record.'
              : waiting > 0
                ? 'Invites are out but nobody has submitted yet. Open the tracker to see who to chase.'
                : 'These employees never filled their form before the link ran out. Re-invite them from their profile.'}
          </div>
        </div>
        <Bt v="warning" s="sm" icon={ClipboardList} onClick={() => { setFilter(toReview > 0 ? 'Submitted' : waiting > 0 ? 'Pending' : 'Expired'); setOpen(true); }}>
          Track intake status
        </Bt>
      </div>
      {open && (
        <div className="fixed inset-0 animate-fadeIn bg-black/40 z-[210] flex items-center justify-center p-4" onMouseDown={() => setOpen(false)}>
          <div className="animate-modalIn bg-bg1 rounded-xl w-full max-w-[1047px] max-h-[85vh] flex flex-col p-4" onMouseDown={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <div>
                <div className="text-[15px] font-bold text-t0">Employee intake status</div>
                <div className="text-[13px] text-t3 mt-[2px]">Every invite sent, and where each one has got to.</div>
              </div>
              <Bt v="ghost" s="sm" onClick={() => setOpen(false)}>Close</Bt>
            </div>

            <div className="flex items-center gap-2 flex-wrap my-3">
              {[{ k: 'all', l: 'All', c: K.t2, n: subs.length },
                ...INTAKE_ORDER.map(k => ({ k, l: INTAKE_STATUS[k].label, c: INTAKE_STATUS[k].c, n: counts[k] || 0 }))]
                .map(t => (
                  <button
                    key={t.k}
                    onClick={() => setFilter(t.k)}
                    className={`px-[11px] py-[5px] rounded-pill text-[13px] font-semibold border cursor-pointer transition-colors ${filter === t.k ? 'text-white' : 'bg-bg1 text-t2'}`}
                    style={{ background: filter === t.k ? t.c : undefined, borderColor: filter === t.k ? t.c : K.b0 }}
                  >
                    {t.l} <span className={filter === t.k ? 'opacity-80' : 'text-t3'}>{t.n}</span>
                  </button>
                ))}
              <div className="ml-auto">
                <Inp icon={Search} placeholder="Search name or ID..." value={q} onChange={setQ} style={{ width: 220 }} />
              </div>
            </div>

            <div className="flex flex-col gap-2 overflow-auto">
              {shown.length === 0 && (
                <div className="p-6 text-center text-[14px] text-t3">Nothing here.</div>
              )}
              {shown.map(s => {
                const st = INTAKE_STATUS[s.status] || { c: K.t3, label: s.status };
                return (
                  <div key={s.id} className="p-3 rounded-lg border border-b0 bg-bg2 flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="text-[14px] font-bold text-t0 flex items-center gap-2 flex-wrap">
                        {s.employee_id} · {s.employee_name || '—'}
                        <Bg c={st.c} sm dot>{st.label}</Bg>
                      </div>
                      <div className="text-[13px] text-t3 mt-[2px]">{intakeTrail(s)}</div>
                      {s.status === 'Rejected' && s.rejection_reason && (
                        <div className="text-[13px] text-r mt-[2px]">Reason: {s.rejection_reason}</div>
                      )}
                    </div>
                    {s.status === 'Submitted'
                      ? <Bt v="success" s="sm" icon={CheckCircle} onClick={() => setReviewing(s.id)}>Review</Bt>
                      : <span className="text-[13px] text-t3">{INTAKE_NEXT[s.status] || 'No action needed'}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {reviewing && (
        <IntakeReviewDrawer
          id={reviewing}
          onClose={() => setReviewing(null)}
          onDone={() => { setReviewing(null); load(); }}
        />
      )}
    </>
  );
};

// Clamp crop offset so image edges always cover the circle (no white space).
function clampEmpCrop(state) {
  if (!state) return state;
  const cropSize = 220;
  const bs = state.baseScale || 1;
  const z = state.zoom || 1;
  const w = (state.imgW || cropSize) * bs * z;
  const h = (state.imgH || cropSize) * bs * z;
  const maxX = Math.max(0, (w - cropSize) / 2);
  const maxY = Math.max(0, (h - cropSize) / 2);
  return {
    ...state,
    offsetX: Math.max(-maxX, Math.min(maxX, state.offsetX || 0)),
    offsetY: Math.max(-maxY, Math.min(maxY, state.offsetY || 0)),
  };
}

// Photo upload modal for HR/admin setting an employee's photo — file picker
// (with drag/drop) or live camera capture, both feeding the same crop +
// upload pipeline. Own component (rather than an inline render-time IIFE)
// so it can hold its own hooks for the camera stream/video element.
// ─── Dated shifts on the employee's Settings tab ────────────────────────
// "From this date to that date this person works that shift." The default shift above applies on every
// other day. Added here directly, or created automatically when a shift change request is approved.
const EmployeeShiftDates = ({ employeeId, shifts }) => {
  const blank = { shift_id: "", date_from: "", date_to: "", reason: "" };
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(null);   // null = closed; { id?, ...fields }
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/api/shift-requests/assignments/${employeeId}`).then(d => setRows(Array.isArray(d) ? d : [])).catch(() => setRows([]));
  useEffect(() => { setForm(null); load(); }, [employeeId]); // eslint-disable-line
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const today = new Date().toISOString().slice(0, 10);

  const save = async () => {
    if (!form.shift_id) return notify("Choose a shift", { title: "Missing", variant: "error" });
    if (!form.date_from || !form.date_to) return notify("Choose the from and to dates", { title: "Missing", variant: "error" });
    if (form.date_to < form.date_from) return notify("The \"to\" date cannot be before the \"from\" date", { title: "Check dates", variant: "error" });
    setBusy(true);
    try {
      const body = { shift_id: parseInt(form.shift_id), date_from: form.date_from, date_to: form.date_to, reason: form.reason };
      if (form.id) await api.put(`/api/shift-requests/assignments/${employeeId}/${form.id}`, body);
      else await api.post(`/api/shift-requests/assignments/${employeeId}`, body);
      notify(form.id ? "Dated shift updated" : "Dated shift added", { title: "Saved", variant: "success" });
      setForm(null); load();
    } catch (e) { notify(e.message || "Could not save", { title: "Error", variant: "error" }); }
    finally { setBusy(false); }
  };
  const remove = async (r) => {
    if (!(await confirmDialog(`Remove ${r.shift_name} for ${fmtDMY(r.date_from)} – ${fmtDMY(r.date_to)}? Those days go back to the default shift.`))) return;
    try { await api.del(`/api/shift-requests/assignments/${employeeId}/${r.id}`); load(); }
    catch (e) { notify(e.message || "Could not remove", { title: "Error", variant: "error" }); }
  };

  return (
    <div className="mt-3 pt-3 border-t border-b0">
      <div className="flex justify-between items-center flex-wrap gap-2 mb-2">
        <div>
          <div className="text-[14px] font-semibold text-t0">Shift by date</div>
          <div className="text-[12px] text-t3">From this date to that date this person works a different shift. Other days use the default shift above. Past days already recorded are not recalculated.</div>
        </div>
        {!form && <Bt v="secondary" s="sm" onClick={() => setForm({ ...blank, date_from: today, date_to: today })}>+ Add dated shift</Bt>}
      </div>
      {form && (
        <div className="p-3 rounded-xl bg-bg2 border border-b0 mb-3 grid gap-2">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div className="sm:col-span-3"><Sl options={[{ v: "", l: "Select a shift" }, ...shifts.map(s => ({ v: s.id, l: `${s.name} (${s.start_time}–${s.end_time})` }))]} value={form.shift_id} onChange={v => set("shift_id", v)} /></div>
            <div><div className="text-[12px] text-t3 mb-1">From</div><Inp type="date" value={form.date_from} onChange={v => set("date_from", v)} /></div>
            <div><div className="text-[12px] text-t3 mb-1">To</div><Inp type="date" value={form.date_to} onChange={v => set("date_to", v)} /></div>
            <div><div className="text-[12px] text-t3 mb-1">Reason (optional)</div><Inp value={form.reason} onChange={v => set("reason", v)} placeholder="e.g. as per roster" /></div>
          </div>
          <div className="flex gap-[6px]">
            <Bt s="sm" disabled={busy} onClick={save}>{busy ? "Saving…" : form.id ? "Update" : "Add"}</Bt>
            <Bt s="sm" v="secondary" disabled={busy} onClick={() => setForm(null)}>Cancel</Bt>
          </div>
        </div>
      )}
      {rows.length === 0 ? <div className="text-[13px] text-t3">No dated shifts — the default shift applies every day.</div> : (
        <div className="grid gap-[6px]">
          {rows.map(r => (
            <div key={r.id} className="flex items-center justify-between gap-2 flex-wrap p-[10px] rounded-lg bg-bg2 border border-b0">
              <div>
                <div className="text-[14px] font-semibold text-t0">{fmtDMY(r.date_from)}{r.date_to !== r.date_from ? ` – ${fmtDMY(r.date_to)}` : ""} <span className="text-t3 font-normal">·</span> {r.shift_name} <span className="text-t3 font-normal">({r.start_time}–{r.end_time})</span></div>
                <div className="text-[12px] text-t3">{r.source === "request" ? "From an approved request" : "Set by HR"}{r.reason ? ` · ${r.reason}` : ""}</div>
              </div>
              <div className="flex gap-[6px]">
                <Bt s="sm" v="secondary" onClick={() => setForm({ id: r.id, shift_id: r.shift_id, date_from: r.date_from, date_to: r.date_to, reason: r.reason || "" })}>Edit</Bt>
                <Bt s="sm" v="danger" onClick={() => remove(r)}>Remove</Bt>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── PF declarations on the employee's Basic Info ───────────────────────
// The two statutory forms a joiner fills on their joining link, shown (and
// fillable by HR) on the employee record afterwards. Rendered with the same
// FormRenderer the joining portal uses, so the wording matches exactly.
const EmployeePfForms = ({ employeeId, answers, submittedAt, canEdit, onSaved }) => {
  const { forms, loading } = useFormCatalogue();
  const [vals, setVals] = useState({});
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fieldErrs, setFieldErrs] = useState({}); // { 'form_key.field_id': message }
  const wrapRef = useRef(null);

  useEffect(() => { setVals(answers || {}); setEditing(false); setFieldErrs({}); }, [answers, employeeId]);

  const pfForms = (forms || []).filter(f => ['pf_self_declaration', 'pf_form_11'].includes(f.key));
  if ((loading && !pfForms.length) || !pfForms.length) return null;

  // File uploads live in Documents; everything else is declared here.
  const visible = (f) => ({
    ...f,
    sections: f.sections
      .map(sec => ({ ...sec, fields: sec.fields.filter(x => !x.hidden && x.type !== 'file') }))
      .filter(sec => sec.fields.length),
  });

  // The PDF is built from what is SAVED — so while editing, save first.
  const downloadPf = async (key) => {
    try { await api.download(`/api/employees/${employeeId}/pf-forms/pdf?form=${key}`, key === 'pf_form_11' ? 'Form 11.pdf' : 'PF Self Declaration.pdf'); }
    catch (e) { notify(e.message || "Could not download", { title: "Error", variant: "error" }); }
  };

  const save = async () => {
    setSaving(true);
    setFieldErrs({});
    try {
      await api.put(`/api/employees/${employeeId}/pf-forms`, { ...vals, submit: true });
      notify("PF declarations saved", { title: "Saved", variant: "success" });
      setEditing(false);
      onSaved?.();
    } catch (e) {
      // Field-level problems are shown on the boxes themselves, in red.
      if (e.data?.fieldErrors) {
        setFieldErrs(e.data.fieldErrors);
        setTimeout(() => wrapRef.current?.querySelector('[data-field-error]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
      } else notify(e.message || "Failed to save", { title: "Error", variant: "error" });
    }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-6" ref={wrapRef}>
      <div className="flex items-center justify-between gap-2 flex-wrap mb-[6px]">
        <div className="text-[14px] font-bold text-t2 uppercase tracking-wide">PF Declarations</div>
        <div className="flex items-center gap-2">
          {submittedAt && <Bg c={K.g} sm dot>Submitted {fmtDMY(submittedAt)}</Bg>}
          {answers && !editing && <>
            <Bt v="secondary" s="sm" icon={Download} onClick={() => downloadPf('pf_form_11')}>Download Form 11</Bt>
            <Bt v="secondary" s="sm" icon={Download} onClick={() => downloadPf('pf_self_declaration')}>Download Self Declaration</Bt>
          </>}
          {canEdit && !editing && <Bt v="secondary" s="sm" onClick={() => setEditing(true)}>{answers ? "Edit" : "Fill in"}</Bt>}
        </div>
      </div>
      {!answers && !editing && (
        <div className="p-3 rounded-lg bg-bg2 border border-b0 text-[14px] text-t3">
          Not submitted yet — these are collected on the joining link, or you can fill them in here.
        </div>
      )}
      {(answers || editing) && (
        <div className="grid gap-3">
          {pfForms.map(f => (
            <div key={f.key} className="p-4 rounded-xl bg-bg2 border border-b0">
              <FormRenderer
                form={visible(f)}
                values={vals[f.key] || {}}
                readOnly={!editing}
                errors={Object.fromEntries(Object.entries(fieldErrs).filter(([k]) => k.startsWith(f.key + '.')).map(([k, m]) => [k.slice(f.key.length + 1), m]))}
                onChange={(id, v) => { setFieldErrs(prev => { const k = `${f.key}.${id}`; if (!prev[k]) return prev; const n = { ...prev }; delete n[k]; return n; }); setVals(prev => ({ ...prev, [f.key]: { ...(prev[f.key] || {}), [id]: v } })); }}
              />
            </div>
          ))}
          {editing && (
            <div className="flex gap-[6px]">
              <Bt disabled={saving} onClick={save}>{saving ? "Saving…" : "Save Declarations"}</Bt>
              <Bt v="secondary" disabled={saving} onClick={() => { setVals(answers || {}); setEditing(false); }}>Cancel</Bt>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

function EmployeePhotoUploadModal({ photoCrop, setPhotoCrop, empDet, setFullEmp }) {
  const cropSize = 220;
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const cropperRef = useRef(null);
  const [camOpen, setCamOpen] = useState(false);
  const [camError, setCamError] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => () => { if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop()); }, []);

  // Non-passive wheel listener so e.preventDefault() actually stops scroll.
  useEffect(() => {
    const el = cropperRef.current;
    if (!el || !photoCrop?.src) return;
    const onWheel = (e) => {
      e.preventDefault();
      setPhotoCrop(p => clampEmpCrop({ ...p, zoom: Math.max(1, Math.min(4, (p.zoom || 1) + (e.deltaY > 0 ? -0.1 : 0.1))) }));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [photoCrop?.src]);

  const onFileSelect = (file) => { if (!file || !file.type.startsWith("image/")) return; const reader = new FileReader(); reader.onload = (ev) => { const img = new Image(); img.src = ev.target.result; img.onload = () => { const baseScale = cropSize / Math.min(img.width, img.height); setPhotoCrop(p => ({ ...p, src: ev.target.result, file, zoom: 1, offsetX: 0, offsetY: 0, imgW: img.width, imgH: img.height, baseScale })); }; }; reader.readAsDataURL(file); };

  const startCamera = async () => {
    setCamError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: { ideal: 480 }, height: { ideal: 480 } } });
      streamRef.current = stream;
      setCamOpen(true);
      setTimeout(() => { if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play(); } }, 0);
    } catch (e) { setCamError(e.name === "NotAllowedError" ? "Camera access denied. Allow camera access and try again." : "Could not access camera: " + e.message); }
  };
  const stopCamera = () => { if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; } setCamOpen(false); };
  const capturePhoto = () => {
    const v = videoRef.current; if (!v) return;
    const c = document.createElement("canvas"); c.width = v.videoWidth || 480; c.height = v.videoHeight || 480;
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    c.toBlob((blob) => { if (blob) onFileSelect(new File([blob], "camera-photo.jpg", { type: "image/jpeg" })); stopCamera(); }, "image/jpeg", 0.9);
  };

  const doUpload = async () => {
    if (!photoCrop.src) return;
    const c = document.createElement("canvas"); c.width = 300; c.height = 300;
    const ctx = c.getContext("2d");
    const img = new Image(); img.src = photoCrop.src;
    await new Promise(r => { img.onload = r; });
    const bs = photoCrop.baseScale || 1; const z = photoCrop.zoom; const totalScale = bs * z;
    const drawW = img.width * totalScale; const drawH = img.height * totalScale;
    const outScale = 300 / cropSize;
    const dx = (cropSize / 2 - drawW / 2 + photoCrop.offsetX) * outScale;
    const dy = (cropSize / 2 - drawH / 2 + photoCrop.offsetY) * outScale;
    ctx.beginPath(); ctx.arc(150, 150, 150, 0, Math.PI * 2); ctx.clip();
    ctx.drawImage(img, dx, dy, drawW * outScale, drawH * outScale);
    const faceCheck = await validateFaceDetectable(c);
    if (!faceCheck.ok) { notify(faceCheck.message, { title: "No Face Detected", variant: "error" }); return; }
    setUploading(true);
    c.toBlob(async (blob) => {
      const eid = empDet?.id;
      const fd = new FormData(); fd.append("file", blob, "profile-" + eid + ".jpg");
      try {
        fd.append("employee_id", eid); fd.append("doc_type", "profile_photo");
        const res = await fetch(BASE + "/api/documents/upload", { method: "POST", headers: { "Authorization": "Bearer " + localStorage.getItem("hrms_token") }, body: fd });
        const data = await res.json();
        if (data.file_path) { await api.put("/api/employees/" + eid, { profile_photo: data.file_path }); const d = await api.get("/api/employees/" + eid); setFullEmp(d); }
        notify('Profile photo updated', { title: 'Saved', variant: 'success' });
        setPhotoCrop(null);
      } catch (err) { notify(err.message || "Upload failed", { title: 'Error', variant: 'error' }); }
      finally { setUploading(false); }
    }, "image/jpeg", 0.9);
  };

  return <div className="fixed inset-0 animate-fadeIn bg-black/60 z-[300] flex items-center justify-center p-3" onMouseDown={() => { stopCamera(); setPhotoCrop(null); }}>
    <div className="animate-modalIn bg-bg1 rounded-[20px] p-5 sm:p-7 w-full max-w-[600px] max-h-[90vh] overflow-auto shadow-[0_24px_80px_rgba(0,0,0,0.25)]" onMouseDown={e => e.stopPropagation()}>
      <div className="flex justify-between items-center mb-5">
        <div>
          <div className="text-base font-extrabold text-t0">Upload Photo</div>
          <div className="text-[14px] text-t3">{camOpen ? "Center the face and capture" : "Drag to reposition · Scroll to zoom"}</div>
        </div>
        <button onClick={() => { stopCamera(); setPhotoCrop(null); }} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button>
      </div>
      {!photoCrop.src && !camOpen ? <div>
        <div onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = K.p; }} onDragLeave={e => { e.currentTarget.style.borderColor = K.b1; }} onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = K.b1; const f = e.dataTransfer.files[0]; if (f) onFileSelect(f); }}
          className="border-2 border-dashed border-b1 rounded-2xl p-10 text-center cursor-pointer bg-bg2 transition-colors"
          onClick={() => { const inp = document.createElement("input"); inp.type = "file"; inp.accept = ".jpg,.jpeg,.png,.webp"; inp.onchange = e => { const f = e.target.files[0]; if (f) onFileSelect(f); }; inp.click(); }}>
          <Upload size={32} color={K.p} className="mx-auto mb-3" />
          <div className="text-[15px] font-bold text-t0 mb-1">Drop the photo here</div>
          <div className="text-[14px] text-t3">or click to browse · JPG, PNG, WebP</div>
          <div className="text-[12px] text-t3 mt-2">Recommended: square image, at least 300×300px</div>
        </div>
        <div className="flex items-center gap-2 my-3"><div className="flex-1 h-px bg-b1" /><span className="text-[12px] text-t3">OR</span><div className="flex-1 h-px bg-b1" /></div>
        <Bt v="secondary" full icon={Camera} onClick={startCamera}>Take Photo</Bt>
        {camError && <div className="text-[14px] mt-2 text-center" style={{ color: K.r }}>{camError}</div>}
      </div> : camOpen ? <div className="flex flex-col items-center">
        <div className="rounded-full overflow-hidden border-[3px] border-p bg-bg2" style={{ width: cropSize, height: cropSize }}>
          <video ref={videoRef} autoPlay playsInline muted style={{ width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)" }} />
        </div>
        <div className="flex gap-2 mt-4 w-full">
          <Bt v="secondary" full onClick={stopCamera}>Cancel</Bt>
          <Bt full icon={Camera} onClick={capturePhoto}>Capture</Bt>
        </div>
      </div> : <div>
        <div className="flex flex-col items-center mb-4">
          <div
            ref={cropperRef}
            className="rounded-full overflow-hidden relative cursor-grab select-none border-[3px] border-p"
            style={{ width: cropSize, height: cropSize, boxShadow: `0 0 0 4px ${K.p}20` }}
            onMouseDown={e => { e.preventDefault(); setPhotoCrop(p => ({ ...p, dragging: true, _startX: e.clientX - p.offsetX, _startY: e.clientY - p.offsetY })); }}
            onMouseMove={e => { if (!photoCrop.dragging) return; setPhotoCrop(p => clampEmpCrop({ ...p, offsetX: e.clientX - p._startX, offsetY: e.clientY - p._startY })); }}
            onMouseUp={() => setPhotoCrop(p => ({ ...p, dragging: false }))}
            onMouseLeave={() => setPhotoCrop(p => ({ ...p, dragging: false }))}>
            {(() => { const bs = photoCrop.baseScale || 1; const z = photoCrop.zoom; const w = (photoCrop.imgW || cropSize) * bs * z; const h = (photoCrop.imgH || cropSize) * bs * z;
              return <img src={photoCrop.src} alt="crop" draggable={false} className="absolute pointer-events-none" style={{ left: cropSize / 2 - w / 2 + photoCrop.offsetX, top: cropSize / 2 - h / 2 + photoCrop.offsetY, width: w, height: h, maxWidth: 'none', maxHeight: 'none' }} />;})()}
          </div>
        </div>
        <div className="flex items-center gap-[10px] mb-4 px-5">
          <span className="text-[14px] text-t3">−</span>
          <input type="range" min="1" max="4" step="0.05" value={photoCrop.zoom} onChange={e => setPhotoCrop(p => clampEmpCrop({ ...p, zoom: parseFloat(e.target.value) }))} className="flex-1" style={{ accentColor: K.p }} />
          <span className="text-[14px] text-t3">+</span>
        </div>
        <div className="flex gap-2">
          <Bt v="secondary" full disabled={uploading} onClick={() => setPhotoCrop(p => ({ ...p, src: null, file: null, zoom: 1, offsetX: 0, offsetY: 0 }))}>Change Photo</Bt>
          <Bt full icon={CheckCircle} disabled={uploading} onClick={doUpload}>{uploading ? "Saving…" : "Save Photo"}</Bt>
        </div>
      </div>}
    </div>
  </div>;
}

// Standalone month calendar of an employee's attendance — same shape as the
// Attendance page's Team Report "View" modal (KPI cards + day-by-day list),
// reimplemented here rather than shared so the Employees page doesn't need
// to import from Attendance.jsx. Blank weekdays with no attendance_records
// row display as "Absent" (a client-side fallback, same as the Attendance
// page's version) — that's a coarser guess than the Leave/Holiday/Week-Off
// aware status shown in the "Today" indicator above, which is computed
// server-side instead.
function EmployeeAttendanceReportModal({ empId, empName, onClose }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [photoView, setPhotoView] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/api/attendance/employee/${empId}?year=${year}&month=${month}`)
      .then(d => setData(d))
      .catch(() => setData({ records: [], summary: {} }))
      .finally(() => setLoading(false));
  }, [empId, year, month]);

  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const MN = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const fmtTime = (t) => t ? new Date(t).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: (typeof window !== 'undefined' && window.__APP_TZ) || "Asia/Kolkata" }) : "";
  const fmtHM = (h) => { const n = parseFloat(h); if (isNaN(n) || n <= 0) return "—"; const m = Math.round(n * 60); const hh = Math.floor(m / 60), mm = m % 60; return hh === 0 ? `${mm}m` : mm === 0 ? `${hh}h` : `${hh}h ${mm}m`; };

  const recs = data?.records || [];
  const sm = data?.summary || {};
  const today = new Date().toISOString().slice(0, 10);
  const dim = new Date(year, month, 0).getDate();
  const days = [];
  for (let d = 1; d <= dim; d++) {
    const dt = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    const rec = recs.find(r => r.date && r.date.slice(0, 10) === dt);
    const dow = new Date(dt).getDay();
    // The week off comes from this person's own schedule (shift / weekly pattern), not a fixed Saturday + Sunday: a Saturday that is
    // a working or work-from-home day for them is a normal day, so a leave or a missing check-in on it is shown for what it is.
    const isWeekend = data?.schedule?.[dt] ? data.schedule[dt].t === 'off' : (dow === 0 || dow === 6);
    days.push({ d, dt, rec, isWeekend, isFuture: dt > today, out: dayOutcome({ rec, calendar: data?.calendar?.[dt], isWeekend }) });
  }
  // Absent / Leave counts come from the same per-day result the list shows, so the cards and the list always agree.
  const absentDays = days.filter(x => !(x.isFuture && !x.rec)).reduce((n, x) => n + absentCount(x.out), 0);
  const leaveDays = days.reduce((n, x) => n + leaveCount(x.out), 0);
  const totalHrs = recs.reduce((s, r) => s + (parseFloat(r.work_hours) || 0), 0);
  const visibleDays = days.filter(x => !(x.isFuture && !x.rec));

  return <div className="fixed inset-0 animate-fadeIn bg-black/60 z-[300] flex items-stretch justify-center" onMouseDown={onClose}>
    <div className="animate-modalIn bg-bg w-full max-w-[1160px] flex flex-col shadow-[0_24px_80px_rgba(0,0,0,0.25)] overflow-hidden" onMouseDown={e => e.stopPropagation()}>
      <div className="px-6 py-5 text-white flex-shrink-0" style={{ background: `linear-gradient(135deg,${K.p},#1E3A8A)` }}>
        <div className="flex justify-between items-start flex-wrap gap-3">
          <div className="flex items-center gap-[14px]">
            <Av nm={empName} sz={48} rn="#fff" />
            <div><div className="text-[18px] font-extrabold">{empName}</div><div className="text-[14px] opacity-80">Attendance Report</div></div>
          </div>
          <button onClick={onClose} className="bg-white/20 border-0 text-white cursor-pointer rounded-lg p-[6px]"><X size={16} /></button>
        </div>
        <div className="flex gap-4 mt-4 flex-wrap">
          {[[sm.present ?? 0, "Present", K.g], [sm.wfh ?? 0, "WFH", K.bl], [sm.late ?? 0, "Late", K.a], [leaveDays, "Leave", K.pu], [absentDays, "Absent", K.r], [totalHrs ? sm.avg_hours : "—", "Avg Hrs", K.p]].map(([v, l], i) =>
            <div key={i} className="bg-white/15 rounded-[10px] px-[14px] py-2 flex-1 text-center min-w-[100px]">
              <div className="text-[18px] font-extrabold">{v}</div>
              <div className="text-[12px] opacity-70 mt-[2px]">{l}</div>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
          <div className="text-[14px] font-semibold opacity-90">{MN[month - 1]} {year}</div>
          <div className="flex items-center gap-[6px]">
            <Sl w={90} options={MONTHS.map((m, i) => ({ v: i + 1, l: m }))} value={month} onChange={v => setMonth(parseInt(v))} />
            <Sl w={80} options={Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - 5 + i).map(y => ({ v: y, l: String(y) }))} value={year} onChange={v => setYear(parseInt(v))} />
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto py-1">
        {loading && <div className="text-center p-10 text-t3 text-[14px]">Loading…</div>}
        {!loading && days.map(({ d, dt, rec, isWeekend, isFuture, out }) => {
          if (isFuture && !rec) return null;
          // A real 'Absent' row (from the nightly auto-absent sweep) has no
          // check-in/check-out — it must render the same "Absent" callout
          // as a day with no record at all, not fall through to the normal
          // present-day layout (which just showed blank times/zone before).
          const isPresent = rec && rec.status !== 'Absent';
          const wh = isPresent ? parseFloat(rec.work_hours) || 0 : 0;
          const whClr = wh >= 8 ? K.g : wh >= 4 ? K.a : wh > 0 ? K.r : K.t3;
          const dayLabel = new Date(dt + "T12:00:00").toLocaleDateString("en-IN", { weekday: "short" });
          if (out.kind === "weekend") return <div key={dt} className="flex items-center px-6 py-[6px] opacity-35">
            <div className="w-11 flex-shrink-0"><div className="text-[12px] font-semibold text-t3">{dayLabel}</div><div className="text-[15px] font-bold text-t3">{d}</div></div>
            <div className="flex-1 h-[1px] bg-b1 mx-3" /><div className="text-[12px] text-t3">Weekend</div>
          </div>;
          // Holiday or leave: say what it is instead of calling the day Absent.
          if (out.kind === "half_leave_absent") return <div key={dt} className="flex items-center px-6 py-[10px]" style={{ background: K.rb + "40", borderBottom: `1px solid ${K.b0}30` }}>
            <div className="w-11 flex-shrink-0"><div className="text-[12px] font-semibold text-t3 uppercase">{dayLabel}</div><div className="text-[16px] font-extrabold" style={{ color: K.r }}>{d}</div></div>
            <div className="flex-1 flex items-center gap-2 pl-2 flex-wrap"><Bg c={out.pending ? K.a : K.bl} dot>{halfLeaveText(out)}</Bg><Bg c={K.r} dot>{absentOtherHalfText(out)}</Bg></div>
          </div>;
          if (out.kind === "holiday" || out.kind === "leave" || out.kind === "leave_pending") {
            const clr = out.kind === "holiday" ? K.pu : out.kind === "leave" ? K.bl : K.a;
            return <div key={dt} className="flex items-center px-6 py-[10px]" style={{ background: clr + "12", borderBottom: `1px solid ${K.b0}30` }}>
              <div className="w-11 flex-shrink-0"><div className="text-[12px] font-semibold text-t3 uppercase">{dayLabel}</div><div className="text-[16px] font-extrabold" style={{ color: clr }}>{d}</div></div>
              <div className="flex-1 flex items-center gap-2 pl-2"><Bg c={clr} dot>{dayOutcomeText(out)}</Bg></div>
            </div>;
          }
          return <div key={dt} className="flex items-center px-6 py-[10px]" style={{ background: isPresent ? "transparent" : K.rb + "40", borderBottom: `1px solid ${K.b0}30` }}>
            <div className="w-11 flex-shrink-0"><div className="text-[12px] font-semibold text-t3 uppercase">{dayLabel}</div><div className="text-[16px] font-extrabold" style={{ color: isPresent ? K.t0 : K.r }}>{d}</div></div>
            {isPresent ? <>
              <div className="w-[72px] flex-shrink-0 text-center"><div className="text-[14px] font-bold text-g">{fmtTime(rec.check_in)}</div>{rec.is_late && <div className="text-[12px] text-a font-semibold">Late {rec.late_minutes}m</div>}</div>
              <div className="flex-1 px-3">
                <div className="relative h-2 bg-bg3 rounded overflow-hidden"><div className="absolute left-0 top-0 h-full rounded" style={{ width: `${Math.min(wh / 10 * 100, 100)}%`, background: `linear-gradient(90deg,${whClr},${whClr}BB)` }} /></div>
                <div className="flex justify-between mt-[3px]">
                  <span className="text-[12px] text-t3">9AM</span>
                  <Bg c={rec.location_type === "Office" ? K.g : rec.location_type === "Home" ? K.bl : rec.location_type === "Alt Location" ? K.pu : K.a} sm style={{ fontSize: 6, padding: "1px 5px" }}>{rec.location_type}</Bg>
                  <span className="text-[12px] text-t3">6PM</span>
                </div>
                {(out.halfLeave || rec.status === "Half Day") && <div className="flex justify-center gap-1 mt-[3px] flex-wrap">
                  {out.halfLeave && <Bg c={out.halfLeave.pending ? K.a : K.bl} sm>{halfLeaveText(out.halfLeave)}</Bg>}
                  {rec.status === "Half Day" && <Bg c={K.a} sm>Half Day</Bg>}
                </div>}
              </div>
              <div className="w-[72px] flex-shrink-0 text-center"><div className="text-[14px] font-bold" style={{ color: rec.check_out ? K.bl : K.t3 }}>{fmtTime(rec.check_out) || "—"}</div></div>
              <div className="w-16 flex-shrink-0 text-center"><div className="text-[15px] font-extrabold" style={{ color: whClr }}>{fmtHM(wh)}</div></div>
              <div className="w-8 flex-shrink-0 text-center">
                {rec.check_in_photo ? <img src={uploadUrl(rec.check_in_photo)} alt="check-in selfie" title="Click to enlarge" onClick={() => setPhotoView(rec)} className="w-7 h-7 rounded-full object-cover border-2 border-g cursor-pointer" /> : <div className="w-7 h-7 rounded-full bg-bg3 flex items-center justify-center"><ScanFace size={12} color={K.t3} /></div>}
              </div>
            </> : <div className="flex-1 flex items-center gap-2 pl-2">
              <XCircle size={14} color={K.r} /><span className="text-[14px] font-semibold text-r">Absent</span>
            </div>}
          </div>;
        })}
        {!loading && visibleDays.length === 0 && <div className="text-center p-10 text-t3 text-[14px]">No attendance data for this month</div>}
      </div>
    </div>
    {photoView && (() => {
      const r = photoView;
      const mapLink = (lat, lng) => (lat != null && lng != null) ? `https://www.google.com/maps?q=${lat},${lng}` : null;
      const photos = [
        r.check_in_photo && { label: "Check-In Selfie", src: uploadUrl(r.check_in_photo), time: fmtTime(r.check_in), color: K.g, key: "in", locationType: r.location_type, mapUrl: mapLink(r.check_in_lat, r.check_in_lng) },
        r.check_out_photo && { label: "Check-Out Selfie", src: uploadUrl(r.check_out_photo), time: fmtTime(r.check_out), color: K.bl, key: "out", mapUrl: mapLink(r.check_out_lat, r.check_out_lng) },
      ].filter(Boolean);
      return <div className="fixed inset-0 animate-fadeIn bg-black/85 z-[400] flex items-center justify-center p-6" onMouseDown={e => { e.stopPropagation(); setPhotoView(null); }}>
        <button onClick={() => setPhotoView(null)} className="fixed top-4 right-4 bg-white/10 border border-white/20 text-white cursor-pointer rounded-full w-9 h-9 flex items-center justify-center"><X size={18} /></button>
        <div className="animate-modalIn flex gap-[18px] max-w-full max-h-full items-center justify-center flex-wrap" onMouseDown={e => e.stopPropagation()}>
          {photos.map(p => <div key={p.key} className="text-center">
            <img src={p.src} alt={p.label} className="max-h-[70vh] rounded-[14px] border-4 shadow-[0_20px_60px_rgba(0,0,0,0.5)] block mx-auto" style={{ maxWidth: "min(420px,90vw)", borderColor: p.color }} />
            <div className="mt-[10px] text-white"><div className="text-[15px] font-bold" style={{ color: p.color }}>{p.label}</div><div className="text-[14px] opacity-85">{empName || ""} · {fmtDMY(r.date)} · {p.time}</div>
              <div className="mt-[6px] flex items-center justify-center gap-2">
                {p.locationType && <Bg c={p.locationType === "Office" ? K.g : p.locationType === "Home" ? K.bl : p.locationType === "Alt Location" ? K.pu : K.a} sm>{p.locationType}</Bg>}
                {p.mapUrl && <a href={p.mapUrl} target="_blank" rel="noreferrer" className="text-[14px] font-semibold inline-flex items-center gap-1" style={{ color: K.bl }}><MapPin size={11} />View on map</a>}
              </div>
            </div>
          </div>)}
          {photos.length === 0 && <div className="text-white text-[15px]">No selfie attached.</div>}
        </div>
      </div>;
    })()}
  </div>;
}

export const EmployeesPage = ({ role, company, companies = [], offices = [], myEmployeeId, onRefresh, hashQuery, can = () => true }) => {
  const [employees, setEmployees] = useState([]);
  const [totalEmp, setTotalEmp] = useState(0);
  const [empStats, setEmpStats] = useState(null);
  const [depts, setDepts] = useState([]);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designationsList, setDesignationsList] = useState([]);
  const [q, setQ] = useState("");
  const [pg, setPg] = useState(1);
  const [dept, setDept] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  // Page-level filters in addition to the App-level company prop. The
  // global `company` prop still applies as the outer scope (so an admin
  // working in "Zucol" mode never sees employees outside Zucol via these
  // filters) — these refine within that scope.
  const [officeFilter, setOfficeFilter] = useState("all");
  const [companyFilter, setCompanyFilter] = useState("all");
  // Office/company FILTER OPTIONS, scoped to the viewer's own Centre/
  // Location Access (returned by GET /api/employees itself, alongside the
  // row data) — an hr_head/centre_head restricted to specific offices only
  // gets THEIR offices/companies offered here, not the whole org's list.
  // null = not loaded yet, falls back to the unscoped OFC/companies props.
  const [scopedOffices, setScopedOffices] = useState(null);
  const [scopedCompanies, setScopedCompanies] = useState(null);
  // "active" (default), "inactive", "resigned", "terminated", or "all".
  // Drives the ?status=&exit_type= query so HR can locate a deactivated
  // employee to reactivate them, or find who resigned/was terminated.
  const [statusFilter, setStatusFilter] = useState("active");
  const [showForm, setShowForm] = useState(null);
  const [newCreds, setNewCreds] = useState(null); // login details of an account just created / reset
  const [empDet, setEmpDet] = useState(null);
  const [fullEmp, setFullEmp] = useState(null);
  const [empTodayStatus, setEmpTodayStatus] = useState(null);
  const [showAttReport, setShowAttReport] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");
  const [teamNode, setTeamNode] = useState(null); // null = loading/not fetched, false = error
  useEffect(() => {
    if (activeTab !== 'team' || !empDet?.id) return;
    setTeamNode(null);
    api.get('/api/employees/' + empDet.id + '/team-node').then(d => setTeamNode(d)).catch(() => setTeamNode(false));
  }, [activeTab, empDet?.id]);
  const [railCollapsed, setRailCollapsed] = useState(false);
  const [editVals, setEditVals] = useState({});
  const [savingSection, setSavingSection] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [managersList, setManagersList] = useState([]);
  // Full active roster (for Reporting Manager / Training Manager pickers —
  // CHRO asked they be pickable from ALL employees, not just the role-band
  // manager list). Role-restricted lists power the Centre HR + Centre Head pickers.
  const [allEmployeesList, setAllEmployeesList] = useState([]);
  const [centreHrsList, setCentreHrsList] = useState([]);
  const [centreHeadsList, setCentreHeadsList] = useState([]);
  const [shiftsList, setShiftsList] = useState([]);
  const [empAccess, setEmpAccess] = useState(null);
  const [userAcct, setUserAcct] = useState(null);
  const [roleMatrix, setRoleMatrix] = useState(null);
  // Delete-info: counts of every linked-record category. Drives the
  // Danger Zone UI — Deactivate is always offered for an Active
  // employee; Permanently Delete is only offered when total_related=0.
  const [deleteInfo, setDeleteInfo] = useState(null);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeConfirm, setPurgeConfirm] = useState('');
  const [exportOpen, setExportOpen] = useState(false);
  const [photoCrop, setPhotoCrop] = useState(null);
  const [removingPhoto, setRemovingPhoto] = useState(false);
  const [viewPhoto, setViewPhoto] = useState(null);
  const [docBusy, setDocBusy] = useState(null); // doc_type of the slot currently uploading/removing/replacing
  const [expBusy, setExpBusy] = useState(false); // experience add/edit/delete in flight
  const [settingsBusy, setSettingsBusy] = useState(null); // which Settings-tab action is currently in flight
  const [removeAccessBusy, setRemoveAccessBusy] = useState(false);
  const [reactivateAccessBusy, setReactivateAccessBusy] = useState(false);
  const [restoreAccessBusy, setRestoreAccessBusy] = useState(false);

  // Builds the ?status=&exit_type= query fragment for the current filter.
  // 'Exited' alone doesn't say whether it was a resignation or a
  // termination — that split comes from the linked separation's type.
  const statusQueryParam = () => {
    switch (statusFilter) {
      case "all": return "";
      case "inactive": return "&status=Inactive";
      case "resigned": return "&status=Exited&exit_type=Resignation";
      case "terminated": return "&status=Exited&exit_type=Termination";
      default: return "&status=Active";
    }
  };

  const DEPTS = depts.length > 0 ? depts : DEPTS_DEFAULT;
  const RL = roles.length ? roles : RL_DEFAULT;
  const OFC = offices.length ? offices : OFC_DEFAULT;
  // Dropdowns for *assigning* a role/dept to an employee must hide inactive
  // entries, so admins can't re-assign to a deactivated/deleted item.
  const RL_ACTIVE = RL.filter(r => r.active !== false);
  const DEPTS_OBJS_ACTIVE = (departments.length ? departments : DEPTS_DEFAULT.map((d, i) => ({ id: i + 1, name: d }))).filter(d => d.is_active !== false);

  useEffect(() => {
    // Compose the company query: page-level filter takes precedence over
    // the App-level prop. (The App-level prop is the "global context" —
    // page filter narrows further within that.)
    const effectiveCompany = companyFilter !== "all" ? companyFilter
                           : (company && company !== "all" ? company : "");
    const cqp = effectiveCompany ? "&company=" + effectiveCompany : "";
    const oqp = officeFilter !== "all" ? "&office=" + officeFilter : "";
    const sqp = statusQueryParam();
    api.get(`/api/employees?page=${pg}&limit=20&search=${encodeURIComponent(q || "")}&dept=${dept === "all" ? "" : encodeURIComponent(dept)}${roleFilter !== "all" ? "&role=" + roleFilter : ""}${oqp}${cqp}${sqp}`)
      .then(d => { setEmployees(d.data || []); setTotalEmp(d.total || 0); setDepts(d.departments || []); setEmpStats(d.stats || null); setScopedOffices(d.offices || []); setScopedCompanies(d.companies || []); })
      .catch(console.error);
    if (!roles.length) api.get('/api/roles').then(d => setRoles(Array.isArray(d) ? d : d.data || [])).catch(console.error);
    if (!departments.length) api.get('/api/departments').then(d => setDepartments(Array.isArray(d) ? d : d.data || [])).catch(console.error);
    if (!designationsList.length) api.get('/api/designations?active=true').then(d => setDesignationsList(Array.isArray(d) ? d : [])).catch(console.error);
  }, [pg, q, dept, roleFilter, officeFilter, companyFilter, company, statusFilter]); // eslint-disable-line

  // Re-fetch the list with the CURRENT filters/page — same query the effect
  // above builds. Used after a profile-drawer save so a field that affects
  // the active filter (most commonly office, but also dept/company/status)
  // doesn't leave a now-non-matching row sitting in the list until a manual
  // page reload.
  const refetchEmployeeList = () => {
    const effectiveCompany = companyFilter !== "all" ? companyFilter
                           : (company && company !== "all" ? company : "");
    const cqp = effectiveCompany ? "&company=" + effectiveCompany : "";
    const oqp = officeFilter !== "all" ? "&office=" + officeFilter : "";
    const sqp = statusQueryParam();
    api.get(`/api/employees?page=${pg}&limit=20&search=${encodeURIComponent(q || "")}&dept=${dept === "all" ? "" : encodeURIComponent(dept)}${roleFilter !== "all" ? "&role=" + roleFilter : ""}${oqp}${cqp}${sqp}`)
      .then(d => { setEmployees(d.data || []); setTotalEmp(d.total || 0); setDepts(d.departments || []); setEmpStats(d.stats || null); setScopedOffices(d.offices || []); setScopedCompanies(d.companies || []); })
      .catch(console.error);
  };

  // Opened via a #employees?id=<empId>&tab=<tab> deep link — the header's
  // global search "Go to Profile" button, or a notification (probation
  // evaluation, training review, ...) pointing at a specific employee.
  // Look the employee up by ID — they're very likely not on the current
  // paginated/filtered page — and open their profile modal with the same
  // compact row shape a normal table-row click would produce, so the
  // sidebar summary (performance, attendance %, CTC, department) renders
  // fully instead of blank. `tab` (if given) is consumed once by the
  // [empDet] effect below via pendingTabRef, since that effect otherwise
  // always resets activeTab to "basic" on every open.
  const pendingTabRef = useRef(null);
  // The open profile (and its tab) live in the URL hash — #employees?id=..&tab=..
  // — so a refresh reopens the same profile and Back closes it instead of
  // leaving the Employees page. Deep links (notifications, global search)
  // simply set that same hash.
  const [urlEmpId, setUrlEmpId, clearUrlEmpId] = useHashParam("id");
  const [, setUrlTab] = useHashParam("tab", "basic");
  const prevEmpIdRef = useRef(null);
  useEffect(() => {
    if (!urlEmpId) {
      if (empDet) { setEmpDet(null); setFullEmp(null); setEditVals({}); }
      return;
    }
    if (empDet?.id === urlEmpId) return;
    const targetId = urlEmpId;
    pendingTabRef.current = readHashParam("tab");
    api.get(`/api/employees?search=${encodeURIComponent(targetId)}&limit=5`)
      .then(d => {
        const match = (d.data || []).find(r => r.id === targetId);
        if (match) setEmpDet(match);
        else notify(`Couldn't find employee ${targetId}`, { title: 'Not found', variant: 'error' });
      })
      .catch(() => notify('Failed to open employee profile', { title: 'Error', variant: 'error' }));
    // eslint-disable-next-line
  }, [urlEmpId]);
  // State -> URL: opening a profile pushes #..?id=, closing it undoes that.
  useEffect(() => {
    const id = empDet?.id || null;
    const prev = prevEmpIdRef.current;
    prevEmpIdRef.current = id;
    if (id) { if (urlEmpId !== id) setUrlEmpId(id); }
    else if (prev) { setUrlTab(null, { replace: true }); clearUrlEmpId(); }
    // eslint-disable-next-line
  }, [empDet?.id]);
  useEffect(() => {
    if (empDet?.id && urlEmpId === empDet.id) setUrlTab(activeTab, { replace: true });
    // eslint-disable-next-line
  }, [activeTab]);

  useEffect(() => {
    if (empDet) {
      setActiveTab(pendingTabRef.current || "basic"); pendingTabRef.current = null; setEditVals({}); setFullEmp(null); setEmpAccess(null); setSaveMsg(""); setDeleteInfo(null); setPurgeOpen(false); setPurgeConfirm(''); setEmpTodayStatus(null);
      api.get('/api/employees/' + empDet.id).then(d => setFullEmp(d)).catch(() => setFullEmp(empDet));
      // Today's check-in status — reuses the same live /api/attendance feed
      // the Attendance page's Today's Records table uses (Present/Late/WFH/
      // Half Day/Leave/Week Off/Holiday/Absent), so it agrees with that page
      // instead of re-deriving a cruder "no record = Absent" guess here.
      if (can('attendance', 'monthly', null, 'view')) {
        api.get('/api/attendance').then(d => {
          const rec = (d?.today_records || []).find(r => r.emp_code === empDet.id);
          setEmpTodayStatus(rec || { status: 'Absent' });
        }).catch(() => setEmpTodayStatus(null));
      }
      if (can('employees', 'basic', null, 'edit') || can('employees', 'settings', null, 'view')) {
        if (can('settings', 'access', 'role_module_matrix', 'view')) api.get('/api/access-control/employee/' + empDet.id).then(d => setEmpAccess(d.data || d)).catch(console.error);
        if (can('employees', 'settings', 'login_credentials', 'view')) api.get('/auth/user/' + empDet.id).then(d => setUserAcct(d.data || d)).catch(() => setUserAcct({ exists: false }));
        if (!roleMatrix && can('settings', 'access', 'role_module_matrix', 'view')) api.get('/api/access-control/roles').then(d => setRoleMatrix(d.data || d)).catch(console.error);
        if (!managersList.length) api.get('/api/employees/managers/list').then(d => setManagersList(Array.isArray(d) ? d : d.data || [])).catch(console.error);
        if (!allEmployeesList.length) api.get('/api/employees/list-all').then(d => setAllEmployeesList(Array.isArray(d) ? d : d.data || [])).catch(console.error);
        if (!shiftsList.length) api.get('/api/attendance/shifts').then(d => setShiftsList(Array.isArray(d) ? d.filter(s => s.is_active) : [])).catch(console.error);
        if (!centreHrsList.length) api.get('/api/employees/centre-hrs').then(d => setCentreHrsList(Array.isArray(d) ? d : d.data || [])).catch(console.error);
        if (!centreHeadsList.length) api.get('/api/employees/centre-heads').then(d => setCentreHeadsList(Array.isArray(d) ? d : d.data || [])).catch(console.error);
      }
      if (can('employees', 'settings', null, 'view')) {
        api.get('/api/employees/' + empDet.id + '/delete-info').then(d => setDeleteInfo(d.data || d)).catch(() => setDeleteInfo(null));
      }
    }
  }, [empDet]);

  const saveSection = async (section) => {
    if (!empDet) return; setSavingSection(true); setSaveMsg("");
    try {
      const endpoint = section === "salary" ? `/api/employees/${empDet.id}/salary` : section === "documents" ? `/api/employees/${empDet.id}/documents` : section === "emergency" ? `/api/employees/${empDet.id}/emergency` : section === "education" ? `/api/employees/${empDet.id}/education` : section === "experience" ? `/api/employees/${empDet.id}/experience` : `/api/employees/${empDet.id}`;
      let payload = { ...editVals };
      if (section === "employment" || !section) { if (payload.company_id) payload.company_id = parseInt(payload.company_id); if (payload.office_id) payload.office_id = parseInt(payload.office_id); if (payload.department_id) payload.department_id = parseInt(payload.department_id); }
      if (section === "emergency") { const raw = fullEmp || {}; const em = Array.isArray(raw.emergency) ? raw.emergency[0] || {} : raw.emergency || {}; payload = { id: em.id, contact_name: em.contact_name || em.name, ...payload }; }
      if (section === "education") { const raw = fullEmp || {}; const edu = Array.isArray(raw.education) ? raw.education[0] || {} : raw.education || {}; payload = { id: edu.id, ...payload }; }
      const res = await api.put(endpoint, payload);
      // If HR renamed the employee, the backend returns the new id in the
      // response so we can follow the record instead of 404'ing on the old
      // one. Update the drawer's target id and refresh the list.
      const newId = res?.employee_id && res.employee_id !== empDet.id ? res.employee_id : empDet.id;
      if (newId !== empDet.id) {
        setEmpDet(prev => ({ ...prev, id: newId }));
      }
      const d = await api.get('/api/employees/' + newId); setFullEmp(d); setEditVals({});
      setSaveMsg("Record Updated Successfully"); setTimeout(() => setSaveMsg(""), 3000);
      notify("Record updated successfully", { title: "Saved", variant: "success" });
      // Always refresh the list behind the drawer — a saved field (office,
      // department, company, status, ...) can change whether this row still
      // matches the current filters, so the row must not linger stale.
      refetchEmployeeList();
      if (newId !== empDet.id) {
        onRefresh?.();
      }
    } catch (e) {
      console.error(e);
      const msg = (e && e.message) || "Update failed";
      notify(msg, { title: "Error", variant: "error" });
      // When the drawer was opened before the employee's ID changed in
      // another session, saves land on a URL that no longer exists.
      // Refresh the list + close the drawer so the user can reopen the
      // current record instead of typing into a stale form.
      if (/no employee with id/i.test(msg) || /may have been renamed/i.test(msg)) {
        setSaveMsg("Error: " + msg);
        setTimeout(() => {
          setEmpDet(null); setEditVals({}); setFullEmp(null);
          setPg(1);
          api.get(`/api/employees?page=1&limit=20`).then(d => { setEmployees(d.data || []); setTotalEmp(d.total || 0); });
          setSaveMsg("");
        }, 3500);
      } else {
        setSaveMsg("Error: " + msg);
        setTimeout(() => setSaveMsg(""), 4000);
      }
    } finally { setSavingSection(false); }
  };

  const saveMsgBanner = saveMsg ? <div className={`px-3 py-2 mb-[10px] rounded-lg text-[14px] font-semibold ${saveMsg.startsWith("Error") ? "bg-rb text-r border border-rd" : "bg-gb text-g border border-gd"}`}>{saveMsg}</div> : null;
  const fieldReadOnlyCls = "px-[10px] py-[7px] bg-bg2 rounded-[10px] text-[14px] text-t0 border border-b0";

  const tp = Math.ceil(totalEmp / 20) || 1;
  // Active filter count — shown next to the totalEmp badge so HR can
  // tell at a glance whether the "14 employees" they see is the whole
  // org or a filtered subset.
  const hasFilters = q || dept !== "all" || officeFilter !== "all" || companyFilter !== "all";
  const clearFilters = () => { setQ(""); setDept("all"); setRoleFilter("all"); setOfficeFilter("all"); setCompanyFilter("all"); setPg(1); };
  return <div>
    <div className="relative overflow-hidden rounded-2xl mb-4 px-6 py-5 sm:px-7 sm:py-6" style={{ background: "linear-gradient(90deg, #0a2364 0%, #0b4d79 55%, #059669 100%)" }}>
      <div className="absolute inset-0 pointer-events-none opacity-30 mix-blend-overlay">
        <svg className="w-full h-full" fill="none" preserveAspectRatio="none" viewBox="0 0 1200 240" xmlns="http://www.w3.org/2000/svg">
          <circle cx="100" cy="220" opacity="0.25" r="180" stroke="white" strokeWidth="48" />
          <circle cx="340" cy="240" opacity="0.2" r="220" stroke="white" strokeWidth="56" />
          <path d="M750 -40C860 60 980 120 1220 100" opacity="0.15" stroke="white" strokeWidth="64" />
        </svg>
      </div>
      <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-white/[0.06] pointer-events-none" />
      <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="text-[13px] font-bold text-white bg-white/15 border border-white/20 rounded-full px-3 py-[4px] flex items-center gap-1.5">
              <span className="w-[6px] h-[6px] rounded-full bg-emerald-300 animate-pulse" />
              {totalEmp} Total Workforce
            </span>
            <span className="text-[13px] font-semibold text-white/85 bg-white/10 border border-white/15 rounded-full px-3 py-[4px]">
              {OFC.length} Location{OFC.length === 1 ? '' : 's'}
            </span>
          </div>
          <h1 className="m-0 text-white text-[26px] sm:text-[30px] font-black leading-tight tracking-tight">Employees Directory</h1>
          <p className="mt-[6px] text-white/80 text-[14px] font-medium max-w-xl">Centralized talent registry, role hierarchy, and document compliance across every office.</p>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-xl">
            <div className="bg-white/10 border border-white/15 rounded-xl px-3 py-2">
              <div className="text-[11px] font-semibold text-white/70 uppercase tracking-wide">Confirmed</div>
              <div className="flex items-baseline gap-2 mt-[2px]">
                <span className="text-[17px] font-extrabold text-white">{empStats ? empStats.confirmed : '—'}</span>
                {empStats && totalEmp > 0 && <span className="text-[11px] text-emerald-300 font-semibold">{Math.round(empStats.confirmed / totalEmp * 100)}%</span>}
              </div>
            </div>
            <div className="bg-white/10 border border-white/15 rounded-xl px-3 py-2">
              <div className="text-[11px] font-semibold text-white/70 uppercase tracking-wide">On Probation</div>
              <div className="flex items-baseline gap-2 mt-[2px]">
                <span className="text-[17px] font-extrabold text-white">{empStats ? empStats.probation : '—'}</span>
                {empStats && totalEmp > 0 && <span className="text-[11px] text-amber-300 font-semibold">{Math.round(empStats.probation / totalEmp * 100)}%</span>}
              </div>
            </div>
            <div className="bg-white/10 border border-white/15 rounded-xl px-3 py-2 col-span-2 sm:col-span-1">
              <div className="text-[11px] font-semibold text-white/70 uppercase tracking-wide">KYC Compliance</div>
              <div className="flex items-baseline gap-2 mt-[2px]">
                <span className="text-[17px] font-extrabold text-white">{empStats && totalEmp > 0 ? Math.round(empStats.docsComplete / totalEmp * 100) + '%' : '—'}</span>
                {empStats && <span className="text-[11px] text-emerald-300 font-semibold">{empStats.docsComplete}/{totalEmp} verified</span>}
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap lg:flex-col gap-[10px] items-stretch lg:min-w-[200px] justify-center flex-shrink-0">
          {can('employees', 'basic', 'create_employee', 'edit') && (
            <button onClick={() => setShowForm("employee")} className="px-4 py-[10px] bg-white hover:bg-blue-50 text-p font-bold text-[13px] rounded-xl shadow-lg flex items-center justify-center gap-2 transition-colors cursor-pointer border-0">
              <Plus size={15} /> Add New Employee
            </button>
          )}
          {can('employees', 'basic', 'create_employee', 'edit') && (
            <button onClick={() => setShowForm("bulk_employee")} className="px-4 py-[10px] bg-blue-400/20 hover:bg-blue-400/30 border border-blue-300/40 text-white font-bold text-[13px] rounded-xl flex items-center justify-center gap-2 transition-colors cursor-pointer">
              <Upload size={15} /> Bulk Data Actions
            </button>
          )}
          {can('employees', 'salary', 'edit_salary_structure', 'edit') && (
            <button onClick={() => setShowForm("bulk_salary")} className="px-4 py-[10px] bg-blue-400/20 hover:bg-blue-400/30 border border-blue-300/40 text-white font-bold text-[13px] rounded-xl flex items-center justify-center gap-2 transition-colors cursor-pointer">
              <Upload size={15} /> Bulk Salary Upload
            </button>
          )}
          {can('reports', 'employees', null, 'view') && (
            <button onClick={() => setExportOpen(true)} className="px-4 py-[10px] bg-blue-400/20 hover:bg-blue-400/30 border border-blue-300/40 text-white font-bold text-[13px] rounded-xl flex items-center justify-center gap-2 transition-colors cursor-pointer">
              <Download size={15} /> Download
            </button>
          )}
        </div>
      </div>
    </div>
    {hasFilters && <div className="flex justify-end mb-2"><button onClick={clearFilters} className="text-[14px] text-p font-semibold cursor-pointer bg-transparent border-0 underline">Clear filters</button></div>}
    <IntakePendingBanner role={role} openId={hashQuery?.tab === 'intake' ? hashQuery?.subId : null} />

    {/* One compact filter bar: search (flex-grow) + 3 filter dropdowns +
        actions. Total height ≈ 50px instead of the previous 120px. The
        Sl component is sized by its container — wrapping each in a fixed-
        width div keeps the dropdowns from sprawling on wide screens. */}
    <Cd style={{ marginBottom: 12, padding: 8 }}>
      <div className="flex gap-[10px] items-center flex-wrap">
        <Inp icon={Search} placeholder="Search name or ID…" value={q} onChange={v => { setQ(v); setPg(1); }} style={{ flex: 2, minWidth: 260 }} />
        <div style={{ flex: 1, minWidth: 170 }}>
          <Sl options={[{ v: "all", l: "All Depts" }, ...DEPTS.map(d => ({ v: d, l: d }))]} value={dept} onChange={v => { setDept(v); setPg(1); }} />
        </div>
        <div style={{ flex: 1, minWidth: 170 }}>
          <Sl options={[{ v: "all", l: "All Roles" }, ...RL_ACTIVE.map(r => ({ v: String(r.id), l: r.t || r.title })), ...[["hr_head", "HR Head"], ["admin", "Admin"], ["centre_head", "Centre Head"], ["centre_hr", "Centre HR"], ["manager", "Manager"], ["team_leader", "Team Leader"], ["team_incharge", "Team Incharge"], ["recruiter", "Recruiter"], ["accounts", "Accounts"], ["employee", "Employee"]].map(([v, l]) => ({ v: "sys:" + v, l: "Access: " + l }))]} value={roleFilter} onChange={v => { setRoleFilter(v); setPg(1); }} />
        </div>
        <div style={{ flex: 1, minWidth: 180 }}>
          <Sl options={[{ v: "all", l: "All Offices" }, ...(scopedOffices ?? OFC).map(o => ({ v: String(o.id), l: o.nm || o.name || o.city }))]} value={officeFilter} onChange={v => { setOfficeFilter(v); setPg(1); }} />
        </div>
        <div style={{ flex: 1, minWidth: 180 }}>
          <Sl options={[{ v: "all", l: "All Companies" }, ...(scopedCompanies ?? companies).map(c => ({ v: String(c.id), l: c.name }))]} value={companyFilter} onChange={v => { setCompanyFilter(v); setPg(1); }} />
        </div>
        <div style={{ flex: 1, minWidth: 180 }}>
          <Sl options={[
            { v: "active",     l: "Active only" },
            { v: "inactive",   l: "Deactivated only" },
            { v: "resigned",   l: "Resigned" },
            { v: "terminated", l: "Terminated" },
            { v: "all",        l: "Everyone" },
          ]} value={statusFilter} onChange={v => { setStatusFilter(v); setPg(1); }} />
        </div>
      </div>
    </Cd>
    {exportOpen && <ExportEmployeesModal
      companies={companies}
      offices={OFC}
      role={role}
      onClose={() => setExportOpen(false)}
    />}
    {showForm === "employee" && <FormModal title="Add New Employee" fields={[
      { key: "name", label: "Full Name *", placeholder: "Ravi Sharma" },
      { key: "email", label: "Company Email * (becomes login)", placeholder: "ravi@tc.in" },
      { key: "employee_id", label: "Employee ID (blank = auto-generate AABBBXXXXX)", placeholder: "e.g. ZUNEW00042 — leave blank for auto" },
      { key: "role_id", label: "Role *", type: "select", options: [{ v: "", l: "Select Role" }, ...RL_ACTIVE.map(r => ({ v: r.id, l: r.t || r.title }))] },
      { key: "department", label: "Department *", type: "select", options: [{ v: "", l: "Select Department" }, ...DEPTS_OBJS_ACTIVE.map(d => ({ v: d.name || d, l: d.name || d }))] },
      { key: "office_id", label: "Office / Work Location *", type: "search-select", placeholder: "Select Office…", searchPlaceholder: "Search by name or city…",
        options: OFC.map(o => ({ v: String(o.id), l: o.nm || o.name, sub: o.city || null })) },
      { key: "company_id", label: "Company *", type: "search-select", placeholder: "Select Company…", searchPlaceholder: "Search company…",
        options: companies.map(c => ({ v: String(c.id), l: c.name })) },
      { key: "join_date", label: "Join Date *", type: "date" },
      { key: "employee_type", label: "Employee Type *", type: "select", options: [{ v: "", l: "Select Type" }, { v: "Full Time", l: "Full Time" }, { v: "Part Time", l: "Part Time" }, { v: "Contract", l: "Contract" }, { v: "Intern", l: "Intern" }] },
      { key: "designation", label: "Designation", placeholder: "Sr Software Engineer" },
      { key: "gender", label: "Gender", type: "select", options: [{ v: "", l: "Select" }, { v: "M", l: "Male" }, { v: "F", l: "Female" }, { v: "O", l: "Other" }] },
      { key: "phone", label: "Phone", placeholder: "+91 98765 43210" },
      { key: "personal_email", label: "Personal Email", placeholder: "ravi@gmail.com" },
      { key: "address_current", label: "Current Address", placeholder: "123 Main St" },
      { key: "city", label: "City", placeholder: "New Delhi" },
      { key: "state", label: "State", placeholder: "Delhi" },
      { key: "pin_code", label: "PIN Code", placeholder: "110001" },
    ]} onSubmit={async v => {
      const missing = [
        ["name", "Full Name"], ["email", "Company Email"], ["role_id", "Role"],
        ["department", "Department"], ["office_id", "Office / Work Location"],
        ["company_id", "Company"], ["join_date", "Join Date"], ["employee_type", "Employee Type"],
      ].find(([k]) => !v[k] || String(v[k]).trim() === "");
      if (missing) throw new Error(`${missing[1]} is required`);
      const em = v.email.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) throw new Error("Invalid company email format");
      try {
        const created = await api.post("/api/employees", { ...v, email: em, office_id: parseInt(v.office_id), company_id: parseInt(v.company_id) });
        notify("Employee added successfully", { title: "Saved", variant: "success" });
        const c = credsFrom(created, v.name); if (c) setNewCreds([c]);
        setPg(1); api.get(`/api/employees?page=1&limit=20`).then(d => { setEmployees(d.data || []); setTotalEmp(d.total || 0); });
      } catch (e) { notify(e.message || "Failed to add employee", { title: "Error", variant: "error" }); throw e; }
    }} onClose={() => setShowForm(null)} />}
    {newCreds && <CredentialsModal items={newCreds} onClose={() => setNewCreds(null)} />}
    {showForm === "bulk_salary" && <BulkSalaryUploadModal onClose={() => setShowForm(null)} />}
    {showForm === "bulk_employee" && <BulkEmployeeModal roles={RL} offices={OFC} departments={departments.length ? departments : DEPTS_DEFAULT.map((d, i) => ({ id: i + 1, name: d }))} companies={companies} onDone={() => { setPg(1); api.get(`/api/employees?page=1&limit=20`).then(d => { setEmployees(d.data || []); setTotalEmp(d.total || 0); }); }} onClose={() => setShowForm(null)} />}
    <Tbl cols={[
      { k: "id", l: "ID", r: r => <span className="font-mono text-[12px] font-bold text-t1">{r.id}</span> },
      { k: "nm", l: "Employee", r: r => <div className="flex items-center gap-[6px] cursor-pointer" onClick={() => setEmpDet(r)}><Av nm={r.nm} sz={26} rn={r.rl?.c} src={r.pp} /><div><div className="font-semibold text-[14px] text-pl">{r.nm}</div><div className="text-[12px] text-t3">{r.em}</div></div></div> },
      { k: "rl", l: "Role", r: r => <Bg c={r.rl?.c || K.t3} sm>{r.rl?.t}</Bg> },
      { k: "d", l: "Dept", r: r => <span className="text-[14px] text-t2">{r.dept}</span> },
      { k: "o", l: "Office", r: r => <span className="text-[14px] text-t2">{r.ofc?.city || ""}</span> },
      { k: "co", l: "Company", r: r => <span className="text-[14px] text-t2">{r.company?.name || "—"}</span> },
      { k: "p", l: "Status", r: r => <Bg c={r.prob === "Confirmed" ? K.g : K.a} sm dot>{r.prob}</Bg> },
      { k: "dc", l: "Docs", r: r => <span style={{ color: r.docs >= 4 ? K.g : K.a }}>{r.docs}/4</span> },
      { k: "act", l: "Action", r: r => <Bt v="ghost" s="xs" icon={Eye} onClick={() => setEmpDet(r)}>View</Bt> },
    ]} data={employees} />
    <div className="flex justify-center gap-1 mt-3">
      <Bt v="ghost" s="xs" icon={ChevronLeft} onClick={() => setPg(Math.max(1, pg - 1))} disabled={pg === 1} />
      <span className="text-[14px] text-t2 self-center">{pg}/{tp}</span>
      <Bt v="ghost" s="xs" icon={ChevronRight} onClick={() => setPg(Math.min(tp, pg + 1))} disabled={pg === tp} />
    </div>
    {empDet && <div className="fixed inset-0 animate-fadeIn bg-black/70 z-[200] flex items-center justify-center p-4" onMouseDown={() => { setEmpDet(null); setFullEmp(null); setEditVals({}); }}>
      <div className="animate-modalIn bg-bg1 border border-b1 w-full max-w-[1720px] h-[95vh] max-h-[95vh] rounded-[16px] flex flex-col shadow-[0_24px_70px_rgba(0,0,0,0.25)] overflow-hidden" onMouseDown={e => e.stopPropagation()}>
        <div className="relative overflow-hidden px-6 pt-4 pb-14 flex-shrink-0" style={{ background: "linear-gradient(90deg, #0a2364 0%, #0b4d79 55%, #059669 100%)" }}>
          <div className="absolute inset-0 pointer-events-none opacity-30 mix-blend-overlay">
            <svg className="w-full h-full" fill="none" preserveAspectRatio="none" viewBox="0 0 1200 160" xmlns="http://www.w3.org/2000/svg">
              <circle cx="80" cy="140" opacity="0.25" r="120" stroke="white" strokeWidth="36" />
              <path d="M750 -20C860 40 980 80 1220 70" opacity="0.15" stroke="white" strokeWidth="48" />
            </svg>
          </div>
          <div className="relative flex justify-between items-start gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-[6px]">
                <span className="text-[11px] font-bold text-white/90 bg-white/15 border border-white/20 rounded-full px-[10px] py-[3px] uppercase tracking-wide">Employee Profile</span>
                <span className="text-[11px] font-semibold text-white/80 bg-white/10 border border-white/15 rounded-full px-[10px] py-[3px]">EMP ID: {empDet.id}</span>
                {empDet.company?.name && <span className="text-[11px] font-semibold text-white/80 bg-white/10 border border-white/15 rounded-full px-[10px] py-[3px]">{empDet.company.name}</span>}
                <span className="text-[11px] font-bold text-white bg-emerald-500/25 border border-emerald-300/40 rounded-full px-[10px] py-[3px] flex items-center gap-1">
                  <span className="w-[6px] h-[6px] rounded-full bg-emerald-300" />
                  {(fullEmp?.employment?.employee_type || '—')}{empDet.prob === 'Confirmed' ? ' · Confirmed' : empDet.prob ? ` · ${empDet.prob}` : ''}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {empDet.id !== myEmployeeId && (() => {
                const empStatus = fullEmp?.employment?.status || empDet.st;
                const loginActive = fullEmp?.employment?.login_active !== false;
                const canActivate = can('employees', 'settings', 'activate_employee', 'edit');
                const canRemove = can('employees', 'settings', 'remove_access', 'edit');
                if (!loginActive) {
                  // Reactivate (flips status back to Active too) only applies
                  // to a plain Inactive employee — backend guard on
                  // /reactivate 400s for anything else. For an Exited
                  // employee (access removed as part of a completed
                  // separation) or any other non-Inactive status, Restore
                  // Access covers the gap: it re-enables login with a fresh
                  // password without touching their HR status — rehiring
                  // (status back to Active) is a distinct, bigger decision
                  // this button isn't meant to make.
                  return <>
                    <span className="text-[11px] font-bold text-white bg-red-500/25 border border-red-300/40 rounded-full px-[10px] py-[3px]">Access Removed</span>
                    {empStatus === 'Inactive' && canActivate && (
                      <Bt v="success" s="sm" icon={Shield} disabled={reactivateAccessBusy} onClick={async () => {
                        if (!(await confirmDialog(`Reactivate ${empDet.nm} (${empDet.id})? This restores their login and flips them back to Active.`))) return;
                        setReactivateAccessBusy(true);
                        try {
                          await api.put(`/api/employees/${empDet.id}/reactivate`, {});
                          notify(`${empDet.nm} has been reactivated.`, { title: 'Reactivated', variant: 'success' });
                          const d = await api.get('/api/employees/' + empDet.id);
                          setFullEmp(d);
                          refetchEmployeeList();
                        } catch (e) { notify(e.message || 'Failed to reactivate', { title: 'Error', variant: 'error' }); }
                        finally { setReactivateAccessBusy(false); }
                      }}>{reactivateAccessBusy ? "Reactivating…" : "Reactivate"}</Bt>
                    )}
                    {empStatus !== 'Inactive' && canRemove && (
                      <Bt v="secondary" s="sm" icon={KeyRound} disabled={restoreAccessBusy} onClick={async () => {
                        if (!(await confirmDialog(`Restore HRMS login for ${empDet.nm} (${empDet.id})?\n\nThis issues a new temporary password (emailed to them) and lets them sign in again. Their HR status stays "${empStatus}" — this does not rehire them or flip them back to Active.`))) return;
                        setRestoreAccessBusy(true);
                        try {
                          const r = await api.put(`/api/employees/${empDet.id}/restore-access`, {});
                          notify(r.emailed_to ? `New login sent to ${r.emailed_to}.` : 'Access restored.', { title: 'Access Restored', variant: 'success' }); { const c = credsFrom(r, empDet.nm); if (c) setNewCreds([c]); }
                          const d = await api.get('/api/employees/' + empDet.id);
                          setFullEmp(d);
                          refetchEmployeeList();
                        } catch (e) { notify(e.message || 'Failed to restore access', { title: 'Error', variant: 'error' }); }
                        finally { setRestoreAccessBusy(false); }
                      }}>{restoreAccessBusy ? "Restoring…" : "Restore Access"}</Bt>
                    )}
                  </>;
                }
                if (!canRemove) return null;
                return (
                  <Bt v="danger" s="sm" icon={ShieldOff} disabled={removeAccessBusy} onClick={async () => {
                    if (!(await confirmDialog(`Remove HRMS access for ${empDet.nm} (${empDet.id}) now?\n\nThis immediately logs them out of every device, blocks sign-in, and resets their password to a random string nobody is given — this cannot be undone from here except by resetting their password again. Their records (leaves, attendance, payslips, etc.) are kept.`))) return;
                    setRemoveAccessBusy(true);
                    try {
                      await api.put(`/api/employees/${empDet.id}/remove-access`, {});
                      notify(`${empDet.nm}'s access has been removed.`, { title: 'Access Removed', variant: 'success' });
                      const d = await api.get('/api/employees/' + empDet.id);
                      setFullEmp(d);
                      refetchEmployeeList();
                    } catch (e) { notify(e.message || 'Failed to remove access', { title: 'Error', variant: 'error' }); }
                    finally { setRemoveAccessBusy(false); }
                  }}>{removeAccessBusy ? "Removing…" : "Remove Access"}</Bt>
                );
              })()}
              <button onClick={() => { setEmpDet(null); setFullEmp(null); setEditVals({}); }} className="bg-white/15 hover:bg-white/25 border border-white/20 rounded-lg p-[6px] text-white cursor-pointer"><X size={16} /></button>
            </div>
          </div>
        </div>
        <div className="flex flex-col flex-1 min-h-0">
          <div className="relative z-10 -mt-10 w-full border-b border-b0 bg-s rounded-t-2xl px-6 pt-3 pb-4 flex flex-col md:flex-row md:items-center gap-4 md:gap-7 flex-shrink-0">
          <div className="flex items-center gap-4 flex-shrink-0">
            <div className="flex flex-col items-center gap-[6px]">
              <div className="relative -mt-8 rounded-[16px] ring-4 ring-s shadow-[0_4px_14px_rgba(0,0,0,0.18)]">
                {(() => {
                  const photo = fullEmp?.basic?.profile_photo || fullEmp?.profile_photo || empDet.pp;
                  return <div className={photo ? "cursor-pointer" : ""} onClick={() => photo && setViewPhoto({ src: photo, nm: empDet.nm })}>
                    <Av nm={empDet.nm} sz={72} rn={empDet.rl?.c} src={photo} />
                  </div>;
                })()}
                {can('employees', 'basic', null, 'edit') && <button onClick={() => setPhotoCrop({ src: null, zoom: 1, offsetX: 0, offsetY: 0, dragging: false, file: null })} className="absolute -bottom-[2px] -right-[2px] w-[26px] h-[26px] rounded-full bg-p flex items-center justify-center cursor-pointer p-0 border-2 border-s shadow-[0_2px_6px_rgba(0,0,0,0.2)]">
                  <Upload size={12} color="#fff" />
                </button>}
              </div>
              {viewPhoto && <div className="fixed inset-0 animate-fadeIn bg-black/85 z-[400] flex items-center justify-center p-6" onMouseDown={() => setViewPhoto(null)}>
                <button onClick={() => setViewPhoto(null)} className="fixed top-4 right-4 bg-white/10 border border-white/20 text-white cursor-pointer rounded-full w-9 h-9 flex items-center justify-center"><X size={18} /></button>
                <img src={viewPhoto.src.startsWith("http") ? viewPhoto.src : uploadUrl(viewPhoto.src)} alt={viewPhoto.nm} className="animate-modalIn max-w-[90vw] max-h-[85vh] rounded-2xl object-contain shadow-2xl" onMouseDown={e => e.stopPropagation()} />
              </div>}
              {!fullEmp?.basic?.profile_photo && !fullEmp?.profile_photo && !empDet.pp && can('employees', 'basic', null, 'edit') && <div className="text-[12px] text-a cursor-pointer" onClick={() => setPhotoCrop({ src: null, zoom: 1, offsetX: 0, offsetY: 0, dragging: false, file: null })}>Upload photo</div>}
              {(fullEmp?.basic?.profile_photo || fullEmp?.profile_photo || empDet.pp) && can('employees', 'basic', null, 'edit') && <div className={`text-[12px] text-r ${removingPhoto ? "" : "cursor-pointer"}`} onClick={removingPhoto ? undefined : async () => { if (!(await confirmDialog("Delete profile photo for " + empDet.nm + "?"))) return; setRemovingPhoto(true); try { await api.del("/api/employees/" + empDet.id + "/profile-photo"); const d = await api.get("/api/employees/" + empDet.id); setFullEmp(d); notify("Profile photo removed", { title: "Removed", variant: "success" }); } catch (e) { notify(e.message || "Failed to remove photo", { title: "Error", variant: "error" }); } finally { setRemovingPhoto(false); } }}>{removingPhoto ? "Removing…" : "Remove photo"}</div>}
              {photoCrop && <EmployeePhotoUploadModal photoCrop={photoCrop} setPhotoCrop={setPhotoCrop} empDet={empDet} setFullEmp={setFullEmp} />}
              {showAttReport && <EmployeeAttendanceReportModal empId={empDet.id} empName={empDet.nm} onClose={() => setShowAttReport(false)} />}
            </div>
            <div>
              {can('attendance', 'monthly', null, 'view') && empTodayStatus && (() => {
                const st = empTodayStatus.status;
                const c = st === "Present" ? K.g : st === "WFH" ? K.bl : st === "Late" ? K.a : st === "Half Day" ? K.pu : st === "Absent" ? K.r : st === "Leave" ? K.cy : st === "Holiday" ? K.tl : K.t3;
                const detail = empTodayStatus.check_in
                  ? `Checked in ${new Date(empTodayStatus.check_in).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`
                  : (st === "Leave" && empTodayStatus.leave_type) ? empTodayStatus.leave_type
                  : st === "Absent" ? "Not checked in yet" : "";
                return <div className="flex items-center gap-[6px] flex-wrap mb-1">
                  <Bg c={c} dot sm>{st}</Bg>
                  {detail && <span className="text-[12px] text-t3">{detail}</span>}
                </div>;
              })()}
              <div className="text-[19px] font-extrabold text-t0 leading-tight">{empDet.nm}</div>
              {(fullEmp?.employment?.designation || empDet.desg) && <div className="text-[14px] text-t1 mt-[2px]">{fullEmp?.employment?.designation || empDet.desg}</div>}
              <div className="flex items-center gap-2 mt-[6px] flex-wrap">
                <Bg c={empDet.rl?.c}>{empDet.rl?.t}</Bg>
                <span className="text-[14px] text-t3">{empDet.dept} · {empDet.ofc?.city || ""}</span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 md:border-l md:border-b0 md:pl-6 flex-1">
            {(() => {
              // Treat null AND 0 as "no data" for perf/attendance — the list mapper
              // coerces NULL → 0, and on a 1–5 / 1–100 scale a real value would be > 0.
              const pfEmpty  = !empDet.pf  || empDet.pf  <= 0;
              const attEmpty = !empDet.att || empDet.att <= 0;
              const salEmpty = !empDet.sal || empDet.sal <= 0;
              return [
                ["ID",          empDet.id,                                                          false],
                ["Performance", pfEmpty  ? "Not reviewed" : (empDet.pf  + "/5"),                    pfEmpty],
                ["Attendance",  attEmpty ? "No data"      : (empDet.att + "%"),                     attEmpty],
                ["CTC",         salEmpty ? "Not set"      : ("₹" + (empDet.sal / 1e5).toFixed(1) + "L/yr"), salEmpty],
              ];
            })().map(([l, v, muted]) => <div key={l} className="flex flex-col gap-[2px]">
              <span className="text-[12px] text-t3 uppercase tracking-wide">{l}</span>
              <span className={`text-[14px] font-semibold ${muted ? "text-t3 italic" : "text-t0"}`}>{v}</span>
            </div>)}
            <div className="ml-auto">
              <ReportingManager
                id={fullEmp?.employment?.manager_id}
                name={fullEmp?.employment?.manager}
                photo={fullEmp?.employment?.manager_photo}
                designation={fullEmp?.employment?.manager_designation}
                compact
                onClick={fullEmp?.employment?.manager_id ? () => setEmpDet({
                  id: fullEmp.employment.manager_id,
                  nm: fullEmp.employment.manager,
                  pp: fullEmp.employment.manager_photo,
                }) : undefined}
              />
            </div>
          </div>
          <div className="flex flex-col gap-2 md:border-l md:border-b0 md:pl-6 md:min-w-[240px] flex-shrink-0">
            <div className="flex items-center justify-between gap-3">
              <div className="text-[12px] font-semibold text-t0">Leave Balance</div>
              <div className="flex gap-2">
                {can('leaves', 'team', 'edit_leave_balance', 'edit') && (
                  <Bt v="secondary" s="xs" icon={Edit} onClick={() => setEditVals(prev => ({ ...prev, _adjustLB: empDet.id }))}>Adjust</Bt>
                )}
                {can('attendance', 'monthly', null, 'view') && (
                  <Bt v="secondary" s="xs" icon={ClipboardList} onClick={() => setShowAttReport(true)}>Attendance</Bt>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {(() => {
                const lb = fullEmp?.leave_balance || {};
                const getLB = (t) => {
                  const tl = t.toLowerCase();
                  const v = lb[t];
                  // Detailed object from /employees/:id
                  if (v && typeof v === 'object' && (v.balance != null || v.used != null)) {
                    return { balance: Number(v.balance) || 0, used: Number(v.used) || 0, exists: true };
                  }
                  // Summary from the list mapper (empDet.lb is a flat map of allocations)
                  if (empDet.lb && empDet.lb[tl] != null) {
                    return { balance: Number(empDet.lb[tl]) || 0, used: Number(empDet.lu?.[tl]) || 0, exists: true };
                  }
                  return { balance: 0, used: 0, exists: false };
                };
                return ["CL", "SL", "EL", "WFH"].map(t => ({ t, ...getLB(t) }));
              })().map(({ t, balance, used, exists }) => {
                if (!exists) {
                  return <div key={t} className="flex items-center gap-1">
                    <span className="text-[12px] text-t3">{t}</span>
                    <span className="text-[12px] text-t3 italic">—</span>
                  </div>;
                }
                // `balance` is the total entitlement (e.g. CL=12/year); `used`
                // is days consumed against it — same model the "Adjust Leave
                // Balance" screen and the backend both use. Remaining =
                // balance - used, and CAN legitimately go negative (over-drawn),
                // which is real information, not a display bug to hide.
                const remaining = balance - used;
                return <div key={t} className="flex items-center gap-1">
                  <span className="text-[12px] text-t3">{t}</span>
                  <div className="w-[36px]"><Pr value={remaining} max={balance || 1} c={remaining > 0 ? K.g : K.r} h={3} /></div>
                  <span className="text-[12px] font-bold" style={{ color: remaining > 0 ? K.g : K.r }}>{remaining}/{balance}</span>
                </div>;
              })}
            </div>
          </div>
          </div>
          <div className="flex-1 flex flex-row overflow-hidden min-w-0">
            <div className="order-2 flex-1 overflow-y-auto p-5 min-w-0">
              {(() => {
                const raw = fullEmp || {}; const b = raw.basic || raw; const emp = raw.employment || raw;
                const fe = { ...b, ...emp, salary: raw.salary || {}, documents: raw.documents || [], emergency: Array.isArray(raw.emergency) ? raw.emergency[0] || {} : raw.emergency || {}, education: Array.isArray(raw.education) ? raw.education[0] || {} : raw.education || {}, experience: raw.experience || [], leave_balance: raw.leave_balance || {}, system_role: raw.system_role || "employee", profile_photo: raw.basic?.profile_photo || raw.profile_photo || null, home_address: raw.home_address || null, home_latitude: raw.home_latitude || null, home_longitude: raw.home_longitude || null, home_radius_meters: raw.home_radius_meters || null, attendance_policy: raw.attendance_policy || 'office_only', alt_location_address: raw.alt_location_address || null, alt_location_lat: raw.alt_location_lat || null, alt_location_lng: raw.alt_location_lng || null, alt_location_radius_meters: raw.alt_location_radius_meters || null };

                if (activeTab === "basic") {
                  const canEdit = can('employees', 'basic', null, 'edit');
                  const bf = (label, k, value, type, options, fieldId = k) => {
                    const fView = can('employees', 'basic', fieldId, 'view');
                    const fEdit = can('employees', 'basic', fieldId, 'edit');
                    if (!fView) return null;
                    const rawVal = editVals[k] !== undefined ? editVals[k] : value;
                    return <div key={k}>
                      <div className="text-[12px] text-t3 uppercase mb-[3px]">{label}</div>
                      {!fEdit ? <div className={fieldReadOnlyCls}>{type === "date" ? (fmtDMY(rawVal) || "—") : (rawVal || "—")}</div>
                        : type === "select" ? <Sl options={options} value={editVals[k] !== undefined ? editVals[k] : value || ""} onChange={v => setEditVals(prev => ({ ...prev, [k]: v }))} />
                          : <Inp value={editVals[k] !== undefined ? editVals[k] : value || ""} onChange={v => setEditVals(prev => ({ ...prev, [k]: v }))} type={type || "text"} />}
                    </div>;
                  };
                  const ageFromDob = (d) => { if (!d) return ''; const b = new Date(d); if (isNaN(b)) return ''; const t = new Date(); let a = t.getFullYear() - b.getFullYear(); if (t.getMonth() < b.getMonth() || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate())) a--; return a >= 0 && a < 130 ? `${a} yr` : ''; };
                  return <div>{saveMsgBanner}
                    {canEdit && <IntakeInviteCard employeeId={empDet.id} employeeName={fe.name || fe.first_name || 'this employee'} />}
                    <div className="text-[14px] font-bold text-t2 uppercase tracking-wide mb-[6px]">Basic Information</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-[10px]">
                      {bf("Employee ID", "employee_id", fe.employee_id || empDet.id || "")}
                      {bf("Salutation", "salutation", fe.salutation || "", "select", [{ v: "", l: "Select" }, ...["Mr.", "Ms.", "Mrs.", "Dr.", "Prof."].map(s => ({ v: s, l: s }))])}
                      {bf("First Name", "first_name", fe.first_name || "")}
                      {bf("Last Name", "last_name", fe.last_name || "")}
                      {bf("Pronouns", "pronouns", fe.pronouns || "", "select", [{ v: "", l: "Select" }, { v: "he/him", l: "he/him" }, { v: "she/her", l: "she/her" }, { v: "they/them", l: "they/them" }, { v: "other", l: "Other" }])}
                      {bf("Date of Birth", "dob", (fe.dob || "").slice(0, 10), "date")}
                      <div><div className="text-[12px] text-t3 uppercase mb-[3px]">Age</div><div className={fieldReadOnlyCls}>{ageFromDob(editVals.dob !== undefined ? editVals.dob : fe.dob) || "—"}</div></div>
                      {bf("Gender", "gender", fe.gender || "", "select", [{ v: "", l: "Select" }, { v: "M", l: "Male" }, { v: "F", l: "Female" }, { v: "O", l: "Other" }])}
                      {bf("Marital Status", "marital_status", fe.marital_status || "", "select", [{ v: "", l: "Select" }, { v: "Single", l: "Single" }, { v: "Married", l: "Married" }, { v: "Divorced", l: "Divorced" }, { v: "Widowed", l: "Widowed" }])}
                      {bf("Father's Name", "father_name", fe.father_name || "")}
                      {(() => { const v = editVals.email !== undefined ? editVals.email : fe.email || fe.em || empDet.em || ""; const valid = !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v); return <div key="email"><div className="text-[12px] text-t3 uppercase mb-[3px]">Official Email Address</div>{!canEdit ? <div className={fieldReadOnlyCls}>{v || "—"}</div> : <><input type="email" value={v} onChange={e => setEditVals(prev => ({ ...prev, email: e.target.value }))} className="w-full px-[10px] py-[7px] bg-bg2 rounded-[10px] text-t0 text-[14px] outline-none" style={{ border: `1px solid ${valid ? K.b0 : K.r}` }} />{v && !valid && <div className="text-[12px] text-r mt-[2px]">Invalid email format</div>}</>}</div>; })()}
                      {bf("Blood Group", "blood_group", fe.blood_group || "", "select", [{ v: "", l: "Select" }, { v: "A+", l: "A+" }, { v: "A-", l: "A-" }, { v: "B+", l: "B+" }, { v: "B-", l: "B-" }, { v: "O+", l: "O+" }, { v: "O-", l: "O-" }, { v: "AB+", l: "AB+" }, { v: "AB-", l: "AB-" }])}
                      {bf("Nationality", "nationality", fe.nationality || "Indian")}
                    </div>

                    <div className="text-[14px] font-bold text-t2 uppercase tracking-wide mb-[6px] mt-4">Contact Details</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-[10px]">
                      {(() => { const v = editVals.work_phone !== undefined ? editVals.work_phone : fe.work_phone || ""; const valid = !v || /^[\d\s+\-()]+$/.test(v); return <div key="work_phone"><div className="text-[12px] text-t3 uppercase mb-[3px]">Work Phone</div>{!canEdit ? <div className={fieldReadOnlyCls}>{v || "—"}</div> : <><input type="tel" value={v} onChange={e => { const num = e.target.value.replace(/[^0-9+\-() ]/g, ""); setEditVals(prev => ({ ...prev, work_phone: num })); }} className="w-full px-[10px] py-[7px] bg-bg2 rounded-[10px] text-t0 text-[14px] outline-none" style={{ border: `1px solid ${valid ? K.b0 : K.r}` }} />{!valid && <div className="text-[12px] text-r mt-[2px]">Numbers only</div>}</>}</div>; })()}
                      {(() => {
                        // Seating Location is multi-select and drives check-in
                        // eligibility — employees who split time across more
                        // than one office (e.g. AIPL + Emaar) get every office
                        // they sit in selected here, and the check-in geo-fence
                        // (server/routes/attendance.js) accepts any of them,
                        // not just their single assigned Location (Employment
                        // tab, unrelated — that one stays a plain single pick
                        // for org/reporting purposes).
                        const selectedIds = editVals.checkin_office_ids !== undefined ? editVals.checkin_office_ids : (fe.checkin_office_ids || []);
                        const seatingOptions = OFC.map(o => ({ v: o.id, l: (o.nm || o.name) + (o.city ? " — " + o.city : "") }));
                        const selectedNames = OFC.filter(o => selectedIds.includes(o.id)).map(o => o.nm || o.name).join(', ');
                        return <div>
                          <div className="text-[12px] text-t3 uppercase mb-[3px]">Seating Location</div>
                          {!canEdit ? <div className={fieldReadOnlyCls}>{selectedNames || "—"}</div>
                            : <MultiSelect options={seatingOptions} value={selectedIds} onChange={(ids) => setEditVals(prev => ({ ...prev, checkin_office_ids: ids }))} placeholder="Select Seating Location(s)" searchPlaceholder="Search offices…" />}
                        </div>;
                      })()}
                      {(() => { const v = editVals.phone !== undefined ? editVals.phone : fe.phone || fe.ph || empDet.ph || ""; const valid = !v || /^[\d\s+\-()]+$/.test(v); return <div key="phone"><div className="text-[12px] text-t3 uppercase mb-[3px]">Personal Mobile</div>{!canEdit ? <div className={fieldReadOnlyCls}>{v || "—"}</div> : <><input type="tel" value={v} onChange={e => { const num = e.target.value.replace(/[^0-9+\-() ]/g, ""); setEditVals(prev => ({ ...prev, phone: num })); }} className="w-full px-[10px] py-[7px] bg-bg2 rounded-[10px] text-t0 text-[14px] outline-none" style={{ border: `1px solid ${valid ? K.b0 : K.r}` }} />{!valid && <div className="text-[12px] text-r mt-[2px]">Numbers only</div>}</>}</div>; })()}
                      {(() => { const v = editVals.personal_email !== undefined ? editVals.personal_email : fe.personal_email || ""; const valid = !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v); return <div key="personal_email"><div className="text-[12px] text-t3 uppercase mb-[3px]">Personal Email</div>{!canEdit ? <div className={fieldReadOnlyCls}>{v || "—"}</div> : <><input type="email" value={v} onChange={e => setEditVals(prev => ({ ...prev, personal_email: e.target.value }))} className="w-full px-[10px] py-[7px] bg-bg2 rounded-[10px] text-t0 text-[14px] outline-none" style={{ border: `1px solid ${valid ? K.b0 : K.r}` }} />{v && !valid && <div className="text-[12px] text-r mt-[2px]">Invalid email format</div>}</>}</div>; })()}
                      {bf("Present Address", "address_current", fe.address_current || "")}
                      {bf("Permanent Address", "address_permanent", fe.address_permanent || "")}
                      {bf("City", "city", fe.city || "")}
                      {bf("State", "state", fe.state || "")}
                      {bf("Country", "country", fe.country || "India")}
                      {bf("PIN Code", "pin_code", fe.pin_code || "")}
                    </div>
                    {canEdit && <Bt style={{ marginTop: 12 }} onClick={() => {
                      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                      if (editVals.email && !emailRegex.test(editVals.email)) { setSaveMsg("Error: Invalid company email format"); setTimeout(() => setSaveMsg(""), 3000); return; }
                      if (editVals.personal_email && !emailRegex.test(editVals.personal_email)) { setSaveMsg("Error: Invalid personal email format"); setTimeout(() => setSaveMsg(""), 3000); return; }
                      if (editVals.phone && !/^[\d\s+\-()]+$/.test(editVals.phone)) { setSaveMsg("Error: Invalid phone number format"); setTimeout(() => setSaveMsg(""), 3000); return; }
                      saveSection("basic");
                    }} disabled={savingSection}>{savingSection ? "Saving..." : "Save Changes"}</Bt>}
                    {!canEdit && <div className="mt-3 text-[14px] text-t3">Contact HR to update your personal information.</div>}

                    <EmployeePfForms
                      employeeId={empDet.id}
                      answers={fullEmp?.pf_forms || null}
                      submittedAt={fullEmp?.pf_submitted_at || null}
                      canEdit={canEdit}
                      onSaved={() => api.get('/api/employees/' + empDet.id).then(d => setFullEmp(d)).catch(() => {})}
                    />
                  </div>;
                }

                if (activeTab === "employment") {
                  const canEdit = can('employees', 'employment', null, 'edit'); const ofc = OFC;
                  // Reporting-Manager + Training-Manager pickers pull from the
                  // whole active roster (CHRO request — anyone should be
                  // pickable as a mentor / dotted-line manager). Falls back
                  // to the old role-band list if /list-all isn't loaded yet.
                  const mgrList = managersList.length ? managersList : employees.filter(x => x.lv <= 6);
                  const allEmpList = allEmployeesList.length ? allEmployeesList : mgrList;
                  const ef = (label, k, value, type, options, fieldId = k) => {
                    const fView = can('employees', 'employment', fieldId, 'view');
                    const fEdit = can('employees', 'employment', fieldId, 'edit');
                    if (!fView) return null;
                    const rawVal = editVals[k] !== undefined ? editVals[k] : value;
                    return <div key={k}>
                      <div className="text-[12px] text-t3 uppercase mb-[3px]">{label}</div>
                      {!fEdit ? <div className={fieldReadOnlyCls}>{type === "date" ? (fmtDMY(rawVal) || "—") : (rawVal || "—")}</div>
                        : type === "search-select" ? <SearchSelect options={options} value={editVals[k] !== undefined ? editVals[k] : value || ""} onChange={v => setEditVals(prev => ({ ...prev, [k]: v }))} />
                        : type === "select" ? <Sl options={options} value={editVals[k] !== undefined ? editVals[k] : value || ""} onChange={v => setEditVals(prev => ({ ...prev, [k]: v }))} />
                          : <Inp value={editVals[k] !== undefined ? editVals[k] : value || ""} onChange={v => setEditVals(prev => ({ ...prev, [k]: v }))} type={type || "text"} />}
                    </div>;
                  };
                  const mgrOptions = mgrList.map(m => ({ v: m.id, l: m.nm, sub: [m.id, m.dept].filter(Boolean).join(' · ') }));
                  // Full active roster — for Reporting Manager & Training Manager.
                  const allEmpOptions = allEmpList.map(m => ({ v: m.id, l: m.nm, sub: [m.id, m.dept || m.rl].filter(Boolean).join(' · ') }));
                  // Centre HR only — employees whose system_role is centre_hr.
                  const centreHrOptions = [{ v: '', l: '— Select Centre HR —' }, ...centreHrsList.map(m => ({ v: m.id, l: m.nm, sub: [m.id, m.office || m.dept].filter(Boolean).join(' · ') }))];
                  // Centre Head only — employees whose system_role is centre_head.
                  const centreHeadOptions = [{ v: '', l: '— Select Centre Head —' }, ...centreHeadsList.map(m => ({ v: m.nm, l: m.nm, sub: [m.id, m.office || m.dept].filter(Boolean).join(' · ') }))];
                  const currentExpText = (() => { const jd = editVals.join_date !== undefined ? editVals.join_date : fe.join_date; if (!jd) return ''; const j = new Date(jd); if (isNaN(j)) return ''; const t = new Date(); const yrs = (t - j) / (365.25 * 86400000); return yrs >= 0 ? yrs.toFixed(1) + ' yr' : ''; })();
                  return <div>{saveMsgBanner}
                    <div className="text-[14px] font-bold text-t2 uppercase tracking-wide mb-[6px]">Employment</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-[10px]">
                      {ef("Company", "company_id", fe.company_id || empDet.company?.id || "", "select", [{ v: "", l: "Select Company" }, ...companies.map(c => ({ v: String(c.id), l: c.name }))])}
                      {ef("Division", "division", fe.division || "")}
                      {ef("Department", "department_id", fe.department_id || fe.dept_id || "", "select", [{ v: "", l: "Select Department" }, ...DEPTS_OBJS_ACTIVE.map(d => ({ v: String(d.id || d), l: d.name || d }))])}
                      {ef("Location", "office_id", fe.office_id || fe.o_id || "", "select", [{ v: "", l: "Select Location" }, ...ofc.map(o => ({ v: String(o.id), l: (o.nm || o.name) + " — " + (o.city || "") }))])}
                      {(() => {
                        const currentDesig = (editVals.designation !== undefined ? editVals.designation : fe.designation || "").trim();
                        const activeTitles = designationsList.map(d => d.title);
                        // Keep the employee's existing value selectable even if it's since
                        // been deactivated/removed from the catalog, so the field never
                        // silently shows blank for a real, already-saved value.
                        const options = [{ v: "", l: "Select Designation" }, ...designationsList.map(d => ({ v: d.title, l: d.title }))];
                        if (currentDesig && !activeTitles.includes(currentDesig)) options.push({ v: currentDesig, l: currentDesig + " (inactive/legacy)" });
                        return ef("Designation", "designation", currentDesig, "select", options);
                      })()}
                      {ef("Role", "role_id", fe.role_id || fe.r_id || "", "select", [{ v: "", l: "Select Role" }, ...RL_ACTIVE.map(r => ({ v: r.id, l: r.t || r.title }))])}
                      {ef("Employment Type", "employee_type", fe.employee_type || "Full Time", "select", [{ v: "Full Time", l: "Full Time" }, { v: "Part Time", l: "Part Time" }, { v: "Contract", l: "Contract" }, { v: "Intern", l: "Intern" }])}
                      {ef("Employee Status", "status", fe.status || "Active", "select", [{ v: "Active", l: "Active" }, { v: "On Leave", l: "On Leave" }, { v: "Notice", l: "Notice" }, { v: "Exited", l: "Exited" }])}
                      {ef("Source of Hire", "source_of_hire", fe.source_of_hire || "")}
                      {ef("Date of Joining", "join_date", (fe.join_date || "").slice(0, 10), "date")}
                      <div><div className="text-[12px] text-t3 uppercase mb-[3px]">Current Experience</div><div className={fieldReadOnlyCls}>{currentExpText || "—"}</div></div>
                      {ef("Centre Head", "hr_ops_group", fe.hr_ops_group || "", "select", centreHeadOptions)}
                      {ef("Centre HR", "centre_hr_id", fe.centre_hr_id || "", "search-select", centreHrOptions)}
                      {ef("Work Mode", "work_mode", fe.work_mode || "Onsite", "select", [{ v: "Onsite", l: "Onsite" }, { v: "Remote", l: "Remote" }, { v: "Hybrid", l: "Hybrid" }])}
                    </div>

                    <div className="text-[14px] font-bold text-t2 uppercase tracking-wide mb-[6px] mt-4">Hierarchy</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-[10px]">
                      {/* CHRO wants ALL employees pickable — not only role-band-6 managers. Uses allEmpOptions. */}
                      {ef("Reporting Manager", "manager_id", fe.manager_id || empDet.mgr || "", "search-select", allEmpOptions)}
                    </div>

                    {/* Training Details — 7d/15d picklist. End date auto-computes
                        from (start + duration) on change; HR can still override
                        manually and the override wins on next render. */}
                    {(() => {
                      // Date math in UTC and formatted by hand — toISOString()
                      // on a local-midnight Date shifts back a day in any
                      // positive-offset timezone (IST is +5:30), which made
                      // "start + 7 days" land on day 6.
                      const addDays = (iso, n) => {
                        if (!iso || !n) return "";
                        const [y, m, dd] = String(iso).slice(0, 10).split("-").map(Number);
                        if (!y || !m || !dd) return "";
                        const d = new Date(Date.UTC(y, m - 1, dd));
                        if (isNaN(d)) return "";
                        d.setUTCDate(d.getUTCDate() + Number(n));
                        const pad = (x) => String(x).padStart(2, "0");
                        return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
                      };
                      const setAutoTraining = (patch) => {
                        const dur = patch.training_duration_days ?? (editVals.training_duration_days ?? fe.training_duration_days);
                        const start = patch.training_start_date ?? (editVals.training_start_date ?? (fe.training_start_date || "").slice(0, 10));
                        const computed = addDays(start, dur);
                        setEditVals(prev => ({ ...prev, ...patch, ...(computed ? { training_end_date: computed } : {}) }));
                      };
                      const setAutoProbation = (patch) => {
                        const dur = patch.probation_duration_days ?? (editVals.probation_duration_days ?? fe.probation_duration_days);
                        const start = patch.probation_start_date ?? (editVals.probation_start_date ?? (fe.probation_start_date || "").slice(0, 10));
                        const computed = addDays(start, dur);
                        setEditVals(prev => ({ ...prev, ...patch, ...(computed ? { probation_end_date: computed } : {}) }));
                      };
                      const trDur = editVals.training_duration_days !== undefined ? editVals.training_duration_days : (fe.training_duration_days || "");
                      const trStart = editVals.training_start_date !== undefined ? editVals.training_start_date : (fe.training_start_date || "").slice(0, 10);
                      const trEnd = editVals.training_end_date !== undefined ? editVals.training_end_date : (fe.training_end_date || "").slice(0, 10);
                      const pbDur = editVals.probation_duration_days !== undefined ? editVals.probation_duration_days : (fe.probation_duration_days || "");
                      const pbStart = editVals.probation_start_date !== undefined ? editVals.probation_start_date : (fe.probation_start_date || "").slice(0, 10);
                      const pbEnd = editVals.probation_end_date !== undefined ? editVals.probation_end_date : (fe.probation_end_date || "").slice(0, 10);
                      return <>
                        <div className="text-[14px] font-bold text-t2 uppercase tracking-wide mb-[6px] mt-4">Training Details</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-[10px]">
                          {ef("Training Manager", "training_manager_id", fe.training_manager_id || "", "search-select", allEmpOptions)}
                          <div>
                            <div className="text-[12px] text-t3 uppercase mb-[3px]">Training Duration</div>
                            {!canEdit ? <div className={fieldReadOnlyCls}>{trDur ? trDur + " days" : "—"}</div>
                              : <Sl options={[{ v: "", l: "— Select —" }, { v: "7", l: "7 Days" }, { v: "15", l: "15 Days" }]}
                                    value={String(trDur || "")}
                                    onChange={v => setAutoTraining({ training_duration_days: v ? parseInt(v) : null })} />}
                          </div>
                          <div>
                            <div className="text-[12px] text-t3 uppercase mb-[3px]">Training Start Date</div>
                            {!canEdit ? <div className={fieldReadOnlyCls}>{fmtDMY(trStart) || "—"}</div>
                              : <Inp type="date" value={trStart} onChange={v => setAutoTraining({ training_start_date: v || null })} />}
                          </div>
                          <div>
                            <div className="text-[12px] text-t3 uppercase mb-[3px]">Training End Date <span className="text-t3 normal-case">(auto)</span></div>
                            {!canEdit ? <div className={fieldReadOnlyCls}>{fmtDMY(trEnd) || "—"}</div>
                              : <Inp type="date" value={trEnd} onChange={v => setEditVals(prev => ({ ...prev, training_end_date: v || null }))} />}
                          </div>
                        </div>

                        <div className="text-[14px] font-bold text-t2 uppercase tracking-wide mb-[6px] mt-4">Probation Details</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-[10px]">
                          {ef("Probation Status", "probation", fe.probation || fe.status || empDet.prob || "", "select", [{ v: "Probation", l: "Probation" }, { v: "Confirmed", l: "Confirmed" }, { v: "Notice Period", l: "Notice Period" }, { v: "Terminated", l: "Terminated" }])}
                          <div>
                            <div className="text-[12px] text-t3 uppercase mb-[3px]">Probation Duration</div>
                            {!canEdit ? <div className={fieldReadOnlyCls}>{pbDur ? pbDur + " days" : "—"}</div>
                              : <Sl options={[{ v: "", l: "— Select —" }, { v: "30", l: "1 month (30 days)" }, { v: "90", l: "3 months (90 days)" }, { v: "180", l: "6 months (180 days)" },
                                  // keep an existing value that isn't one of the standard periods selectable
                                  ...(pbDur && !["30", "90", "180"].includes(String(pbDur)) ? [{ v: String(pbDur), l: `${pbDur} days` }] : [])]}
                                    value={String(pbDur || "")}
                                    onChange={v => setAutoProbation({ probation_duration_days: v ? parseInt(v) : null })} />}
                          </div>
                          <div>
                            <div className="text-[12px] text-t3 uppercase mb-[3px]">Probation Start Date</div>
                            {!canEdit ? <div className={fieldReadOnlyCls}>{fmtDMY(pbStart) || "—"}</div>
                              : <Inp type="date" value={pbStart} onChange={v => setAutoProbation({ probation_start_date: v || null })} />}
                          </div>
                          <div>
                            <div className="text-[12px] text-t3 uppercase mb-[3px]">Probation End Date <span className="text-t3 normal-case">(auto)</span></div>
                            {!canEdit ? <div className={fieldReadOnlyCls}>{fmtDMY(pbEnd) || "—"}</div>
                              : <Inp type="date" value={pbEnd} onChange={v => setEditVals(prev => ({ ...prev, probation_end_date: v || null }))} />}
                          </div>
                          {ef("Probation Review Decision", "probation_review_decision", fe.probation_review_decision || "", "select", [{ v: "", l: "Select" }, { v: "Confirmed", l: "Confirmed" }, { v: "Extended", l: "Extended" }, { v: "Terminated", l: "Terminated" }, { v: "Pending", l: "Pending" }])}
                          {ef("Extended Probation Review Due", "extended_probation_review_due", (fe.extended_probation_review_due || "").slice(0, 10), "date")}
                          {ef("Date of Confirmation", "date_of_confirmation", (fe.date_of_confirmation || "").slice(0, 10), "date")}
                        </div>

                        {/* Surface the actual Training Review + Probation Evaluation
                            forms right here too (not just their own separate tabs)
                            so admin/hr_head see and can edit everything for this
                            employee's training & probation in one place. */}
                        <div className="text-[14px] font-bold text-t2 uppercase tracking-wide mb-[6px] mt-4">Training Review Forms</div>
                        <EmployeeTrainingReviewTab employeeId={empDet.id} role={role} myEmployeeId={myEmployeeId} />

                        <div className="text-[14px] font-bold text-t2 uppercase tracking-wide mb-[6px] mt-4">Probation Evaluation Forms</div>
                        <EmployeeProbationTab employeeId={empDet.id} role={role} myEmployeeId={myEmployeeId} />
                      </>;
                    })()}
                    {canEdit && <Bt style={{ marginTop: 12 }} onClick={() => saveSection("employment")} disabled={savingSection}>{savingSection ? "Saving..." : "Save Changes"}</Bt>}
                  </div>;
                }

                if (activeTab === "salary") {
                  const sal = fe.salary || {}; const canEdit = can('employees', 'salary', 'edit_salary_details', 'edit');
                  const sf = (label, k, value, type, options) => <div key={k}>
                    <div className="text-[12px] text-t3 uppercase mb-[3px]">{label}</div>
                    {!canEdit ? <div className={fieldReadOnlyCls}>{editVals[k] !== undefined ? editVals[k] : value || "—"}</div>
                      : type === "select" ? <Sl options={options} value={editVals[k] !== undefined ? editVals[k] : value || ""} onChange={v => setEditVals(prev => ({ ...prev, [k]: v }))} />
                        : <Inp value={editVals[k] !== undefined ? editVals[k] : value || ""} onChange={v => setEditVals(prev => ({ ...prev, [k]: v }))} type={type || "text"} />}
                  </div>;
                  return <div>{saveMsgBanner}
                    {canEdit && (
                      <div className="mb-5 pb-5 border-b border-b0">
                        <EmployeeSalaryStructure employeeId={empDet.id} canEditCtc={can('employees', 'salary', 'edit_ctc', 'edit')} canEditPf={can('employees', 'salary', 'edit_pf_percent', 'edit')} canEditEsi={can('employees', 'salary', 'edit_esi_percent', 'edit')} canEditComponents={can('settings', 'salary', 'salary_components', 'edit')} canDeleteComponents={can('settings', 'salary', 'salary_components', 'delete')} canRaiseDeduction={can('hrcorner', 'deductions', 'raise_deduction', 'edit')} />
                      </div>
                    )}
                    <EmployeeSalarySheet employeeId={empDet.id} />
                    <div className="text-[14px] font-bold text-t0 mb-2">Statutory IDs <span className="text-[12px] text-t3 font-normal">(only the identifiers — applicability + amounts are driven by the salary structure)</span></div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[10px] mb-4">
                      {sf("UAN Number", "uan_number", sal.uan_number || "")}
                      {sf("PF Number", "pf_number", sal.pf_number || "")}
                      {sf("ESI Number", "esi_number", sal.esi_number || "")}
                      {sf("GHI Policy Number", "ghi_number", sal.ghi_number || "")}
                    </div>
                    <div className="text-[14px] font-bold text-t0 mb-2">Tax</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[10px] mb-4">
                      {sf("PAN Number", "pan_number", sal.pan_number || "")}
                      {sf("Aadhaar Number", "aadhar_number", sal.aadhar_number || "")}
                      {sf("Tax Regime", "tax_regime", sal.tax_regime || "New", "select", [{ v: "New", l: "New Regime" }, { v: "Old", l: "Old Regime" }])}
                    </div>
                    <div className="text-[14px] font-bold text-t0 mb-2">Bank Details</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[10px]">
                      {sf("Bank Name", "bank_name", sal.bank_name || "")}
                      {sf("Account Number", "account_number", sal.account_number || "")}
                      {sf("IFSC Code", "ifsc_code", sal.ifsc_code || "")}
                      {sf("Payment Mode", "payment_mode", sal.payment_mode || "Bank Transfer", "select", [{ v: "Bank Transfer", l: "Bank Transfer" }, { v: "Cheque", l: "Cheque" }, { v: "Cash", l: "Cash" }])}
                    </div>
                    {canEdit && <Bt style={{ marginTop: 14 }} onClick={() => saveSection("salary")} disabled={savingSection}>{savingSection ? "Saving..." : "Save Changes"}</Bt>}
                    {!canEdit && <div className="mt-3 text-[14px] text-t3">Contact HR or Accounts to update salary details.</div>}
                  </div>;
                }

                if (activeTab === "payroll") {
                  return <EmployeePayrollTab employeeId={empDet.id} role={role} />;
                }

                if (activeTab === "ghi") {
                  return <EmployeeGhiTab employeeId={empDet.id} role={role} canPortal={can('employees', 'ghi', 'portal_login', 'edit')} canSendActivation={can('employees', 'ghi', 'send_activation', 'edit')} />;
                }

                if (activeTab === "training_review") {
                  return <EmployeeTrainingReviewTab employeeId={empDet.id} role={role} myEmployeeId={myEmployeeId} />;
                }

                if (activeTab === "probation_eval") {
                  return <EmployeeProbationTab employeeId={empDet.id} role={role} myEmployeeId={myEmployeeId} />;
                }

                if (activeTab === "documents") {
                  const docs = fe.documents || []; const canUpload = can('employees', 'documents', null, 'edit'); const canDeleteDoc = can('employees', 'documents', 'delete_document', 'edit');
                  const sub = editVals._docSub || 'required';
                  // Required documents — the default checklist HR must collect
                  // for every employee, regardless of department or seniority.
                  const REQUIRED_SLOTS = [
                    'Offer Letter', 'Bank Statements (Last 3 Months)', 'Salary Slips (Last 3 Months)',
                    'Appraisal Letter / Internship Letter (If any)', 'Aadhar Card (Both Sides)',
                    'Resignation Acceptance/Experience Letter',
                  ];
                  const PERSONAL_SLOTS = [
                    'Passport-Size Photo', 'Aadhaar Card', 'PAN Card',
                    '10th Marksheet', '12th Marksheet', 'Highest Degree',
                    'Salary Slip (Last 3 Months)', 'Bank Statement', 'UAN Screenshot',
                  ];
                  // Slots are matched by name, so no name may repeat across the tabs — otherwise a file
                  // uploaded in one tab also shows as uploaded in the other ('Offer Letter' is Required).
                  const HR_SLOTS = ['HR Offer Letter', 'Contract', 'NDA'];
                  // Exit documents — issued when an employee leaves. Every slot
                  // is auto-generated by the separation lifecycle (see
                  // server/services/exitDocs.js): Resignation Letter fires on
                  // separation create, Relieving Letter on 'Exit In Progress',
                  // Experience Letter on 'Completed', F&F Settlement on FNF
                  // approval, and the Exit Interview Form the moment HR marks
                  // exit_interview_done true. HR can still Replace / Delete
                  // from these slots if a manual edit is needed.
                  // "No Dues Certificate" intentionally omitted — F&F covers
                  // the same acknowledgment.
                  const EXIT_SLOTS = [
                    'Resignation Letter', 'Relieving Letter', 'Experience Letter (Exit)',
                    'Full & Final Settlement', 'Exit Interview Form',
                  ];
                  const slots = sub === 'hr' ? HR_SLOTS : sub === 'exit' ? EXIT_SLOTS : sub === 'personal' ? PERSONAL_SLOTS : REQUIRED_SLOTS;
                  const isRequiredTab = sub === 'required';
                  const docBySlot = (name) => docs.find(d => (d.doc_type || '').toLowerCase() === name.toLowerCase());
                  const ALL_NAMED = [...REQUIRED_SLOTS, ...PERSONAL_SLOTS, ...HR_SLOTS, ...EXIT_SLOTS];
                  const other = docs.filter(d => !ALL_NAMED.some(s => s.toLowerCase() === (d.doc_type || '').toLowerCase()));

                  const uploadForSlot = async (slotName, file) => {
                    const fd = new FormData(); fd.append("file", file); fd.append("employee_id", empDet.id); fd.append("doc_type", slotName);
                    try {
                      const upRes = await fetch(BASE + "/api/documents/upload", { method: "POST", headers: { "Authorization": "Bearer " + localStorage.getItem("hrms_token") }, body: fd });
                      const upData = await upRes.json();
                      if (upData.file_path) { await api.post('/api/employees/' + empDet.id + '/documents', { doc_type: slotName, file_path: upData.file_path, file_name: upData.file_name || file.name }); }
                      const d = await api.get('/api/employees/' + empDet.id); setFullEmp(d);
                      notify(slotName + " uploaded", { title: "Saved", variant: "success" });
                    } catch (err) { notify("Upload failed: " + (err.message || "Unknown error"), { title: "Error", variant: "error" }); }
                  };
                  const removeDoc = async (d) => {
                    if (!(await confirmDialog("Remove " + d.doc_type + "?"))) return;
                    try { await api.del(`/api/employees/${empDet.id}/documents/${d.id}`); const nd = await api.get('/api/employees/' + empDet.id); setFullEmp(nd); notify(d.doc_type + " removed", { title: "Removed", variant: "success" }); }
                    catch (e) { notify(e.message || "Delete failed", { title: "Error", variant: "error" }); }
                  };
                  const subTabCls = (k) => `px-3 py-[6px] text-[14px] cursor-pointer bg-transparent border-0 ${sub === k ? 'border-b-2 border-p text-p font-semibold' : 'border-b-2 border-transparent text-t2 font-medium'}`;

                  return <div>
                    <div className="flex gap-0 mb-3 border-b border-b0">
                      <button className={subTabCls('required')} onClick={() => setEditVals(p => ({ ...p, _docSub: 'required' }))}>Required Documents</button>
                      <button className={subTabCls('personal')} onClick={() => setEditVals(p => ({ ...p, _docSub: 'personal' }))}>Personal Documents</button>
                      <button className={subTabCls('hr')} onClick={() => setEditVals(p => ({ ...p, _docSub: 'hr' }))}>HR Documents</button>
                      <button className={subTabCls('exit')} onClick={() => setEditVals(p => ({ ...p, _docSub: 'exit' }))}>Exit Documents</button>
                    </div>

                    <div className="grid gap-2">
                      {slots.map(slot => {
                        const d = docBySlot(slot);
                        return <div key={slot} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-bg2 border border-b0 flex-wrap">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <FileText size={13} color={d ? K.g : (isRequiredTab ? K.a : K.t3)} />
                              <span className="text-[14px] font-bold text-t0">{slot}{isRequiredTab && <span className="text-r ml-1">*</span>}</span>
                              {d ? <Bg c={K.g} sm dot>Uploaded</Bg> : (isRequiredTab ? <Bg c={K.r} sm>Required — not uploaded</Bg> : <Bg c={K.t3} sm>Not uploaded</Bg>)}
                            </div>
                            {d && <div className="text-[12px] text-t3 mt-[2px]">{d.file_name || d.file_path} · {fmtDMY(d.uploaded_at)}</div>}
                          </div>
                          <div className="flex items-center gap-1">
                            {d && d.file_path && <Bt v="ghost" s="xs" icon={Download} onClick={() => window.open(uploadUrl(d.file_path), '_blank')}>View</Bt>}
                            {canUpload && (
                              <label className={`inline-flex items-center gap-1 px-2 py-[3px] rounded bg-pg border border-p/40 text-[14px] font-semibold text-p ${docBusy === slot ? "opacity-60" : "cursor-pointer"}`}>
                                <Upload size={11} />{docBusy === slot ? (d ? 'Replacing…' : 'Uploading…') : (d ? 'Replace' : 'Upload')}
                                <input type="file" disabled={docBusy === slot} accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" className="hidden" onChange={async e => { const f = e.target.files[0]; if (!f) return; setDocBusy(slot); try { if (d) { await removeDoc(d); } await uploadForSlot(slot, f); } finally { setDocBusy(null); } e.target.value = ''; }} />
                              </label>
                            )}
                            {canDeleteDoc && d && <Bt v="danger" s="xs" icon={X} disabled={docBusy === slot} onClick={async () => { setDocBusy(slot); try { await removeDoc(d); } finally { setDocBusy(null); } }} />}
                          </div>
                        </div>;
                      })}
                    </div>
                    {isRequiredTab && <div className="text-[12px] text-t3 mt-2">Required documents (*) apply to every employee by default — HR should collect and upload all six.</div>}

                    {other.length > 0 && (
                      <div className="mt-4">
                        <div className="text-[14px] font-bold text-t2 uppercase tracking-wide mb-2">Other Documents</div>
                        <div className="grid gap-1">
                          {other.map(d => <div key={d.id} className="flex items-center justify-between p-2 rounded bg-bg2 border border-b0">
                            <div className="flex items-center gap-2 min-w-0"><FileText size={12} color={K.bl} /><span className="text-[14px] font-semibold truncate">{d.doc_type}</span><span className="text-[12px] text-t3 truncate">{d.file_name}</span></div>
                            <div className="flex items-center gap-1">
                              {d.file_path && <Bt v="ghost" s="xs" icon={Download} onClick={() => window.open(uploadUrl(d.file_path), '_blank')} />}
                              {canDeleteDoc && <Bt v="danger" s="xs" icon={X} disabled={docBusy === d.id} onClick={async () => { setDocBusy(d.id); try { await removeDoc(d); } finally { setDocBusy(null); } }} />}
                            </div>
                          </div>)}
                        </div>
                      </div>
                    )}

                    {!canUpload && docs.length === 0 && <div className="text-[14px] text-t3 mt-3 text-center">Contact HR to upload your documents.</div>}
                  </div>;
                }

                if (activeTab === "emergency") {
                  const em = fe.emergency || {};
                  return <div>{saveMsgBanner}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-[10px]">
                      {[["Contact Name", "contact_name", em.contact_name || em.name || ""], ["Relation", "relation", em.relation || ""], ["Address", "address", em.address || ""]].map(([label, key, value]) => <div key={key}><div className="text-[12px] text-t3 uppercase mb-[3px]">{label}</div><Inp value={editVals[key] !== undefined ? editVals[key] : value || ""} onChange={v => setEditVals(prev => ({ ...prev, [key]: v }))} /></div>)}
                      <div key="phone"><div className="text-[12px] text-t3 uppercase mb-[3px]">Phone</div><Inp value={editVals.phone !== undefined ? editVals.phone : em.phone || ""} onChange={v => { const num = v.replace(/[^0-9+\- ]/g, ""); setEditVals(prev => ({ ...prev, phone: num })); }} type="tel" /></div>
                    </div>
                    <Bt style={{ marginTop: 12 }} onClick={() => saveSection("emergency")} disabled={savingSection}>{savingSection ? "Saving..." : "Save Changes"}</Bt>
                  </div>;
                }

                if (activeTab === "education") {
                  const edu = fe.education || {}; const fields = [["Highest Qualification", "highest_qualification", edu.highest_qualification || ""], ["Degree", "degree", edu.degree || ""], ["Specialization", "specialization", edu.specialization || ""], ["University / College", "university", edu.university || ""], ["Passing Year", "passing_year", edu.passing_year || ""], ["Percentage / CGPA", "percentage_cgpa", edu.percentage_cgpa || ""], ["Certifications", "certifications", edu.certifications || ""], ["Skills", "skills", edu.skills || ""]];
                  return <div>{saveMsgBanner}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-[10px]">{fields.map(([label, key, value]) => <div key={key}><div className="text-[12px] text-t3 uppercase mb-[3px]">{label}</div><Inp value={editVals[key] !== undefined ? editVals[key] : value || ""} onChange={v => setEditVals(prev => ({ ...prev, [key]: v }))} /></div>)}</div>
                    <Bt style={{ marginTop: 12 }} onClick={() => saveSection("education")} disabled={savingSection}>{savingSection ? "Saving..." : "Save Changes"}</Bt>
                  </div>;
                }

                if (activeTab === "experience") {
                  const exps = fe.experience || []; const canEdit = can('employees', 'experience', 'manage_experience', 'edit');
                  const reloadEmp = async () => { const d = await api.get('/api/employees/' + empDet.id); setFullEmp(d); };
                  const deleteExp = async (expId) => {
                    if (!(await confirmDialog("Delete this experience entry?"))) return;
                    setExpBusy(true);
                    try { await api.del('/api/employees/' + empDet.id + '/experience/' + expId); await reloadEmp(); notify("Experience entry deleted", { title: "Deleted", variant: "success" }); }
                    catch (e) { notify(e.message || "Delete failed", { title: "Error", variant: "error" }); }
                    finally { setExpBusy(false); }
                  };
                  const calcDuration = (sd, ed) => { if (!sd || !ed) return ""; const s = new Date(sd), e = new Date(ed); if (isNaN(s) || isNaN(e) || s > e) return ""; let yrs = e.getFullYear() - s.getFullYear(); let mos = e.getMonth() - s.getMonth(); if (mos < 0) { yrs--; mos += 12; } return yrs > 0 ? (mos > 0 ? yrs + " Year" + (yrs > 1 ? "s" : "") + " " + mos + " Month" + (mos > 1 ? "s" : "") : yrs + " Year" + (yrs > 1 ? "s" : "")) : (mos > 0 ? mos + " Month" + (mos > 1 ? "s" : "") : "Less than a month"); };
                  const expDispDuration = (ex) => { const sd = (ex.start_date || "").slice(0, 10), ed = (ex.end_date || "").slice(0, 10); const calc = calcDuration(sd, ed); return calc || ex.total_experience || ""; };
                  return <div>{exps.length > 0 ? exps.map((ex, idx) => <Cd key={ex.id || idx} style={{ marginBottom: 8, padding: 14 }}>
                    <div className="flex justify-between items-start flex-wrap gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="text-[15px] font-bold text-t0">{ex.company_name || ex.company || "—"}</div>
                          {ex.is_relevant === true  && <Bg c={K.g} sm>Relevant</Bg>}
                          {ex.is_relevant === false && <Bg c={K.t3} sm>Not Relevant</Bg>}
                        </div>
                        <div className="text-[14px] text-t2 mt-[2px]">{ex.designation || "—"}</div>
                        <div className="flex gap-3 mt-[6px] flex-wrap">
                          <div><div className="text-[12px] text-t3">FROM</div><div className="text-[14px] text-t1">{fmtDMY(ex.start_date) || "—"}</div></div>
                          <div><div className="text-[12px] text-t3">TO</div><div className="text-[14px] text-t1">{fmtDMY(ex.end_date) || "Present"}</div></div>
                          {ex.last_salary && <div><div className="text-[12px] text-t3">LAST CTC</div><div className="text-[14px] font-semibold text-t0">₹{Number(ex.last_salary).toLocaleString('en-IN')}</div></div>}
                          <div><div className="text-[12px] text-t3">DURATION</div><div className="text-[14px] text-t1">{expDispDuration(ex) || "—"}</div></div>
                        </div>
                        {ex.job_description && <div className="mt-[6px]"><div className="text-[12px] text-t3">JOB DESCRIPTION</div><div className="text-[14px] text-t1 whitespace-pre-wrap">{ex.job_description}</div></div>}
                        {ex.experience_letter && <div className="mt-[6px]"><Bt v="ghost" s="xs" icon={Download} onClick={() => window.open(uploadUrl(ex.experience_letter), '_blank')}>Experience Letter</Bt></div>}
                      </div>
                      {canEdit && <div className="flex gap-1">
                        <Bt v="ghost" s="xs" icon={Edit} disabled={expBusy} onClick={() => { setEditVals({ _expId: ex.id, company_name: ex.company_name || ex.company || "", designation: ex.designation || "", start_date: (ex.start_date || "").slice(0, 10), end_date: (ex.end_date || "").slice(0, 10), last_salary: ex.last_salary || "", total_experience: ex.total_experience || "", job_description: ex.job_description || "", is_relevant: ex.is_relevant == null ? "" : (ex.is_relevant ? 'yes' : 'no'), experience_letter: ex.experience_letter || "" }); setShowForm("edit_experience"); }}>Edit</Bt>
                        <Bt v="ghost" s="xs" icon={X} disabled={expBusy} onClick={() => deleteExp(ex.id)}>{expBusy ? "Deleting…" : "Delete"}</Bt>
                      </div>}
                    </div>
                  </Cd>) : <div className="text-center p-5 text-t3">No experience records</div>}
                    {canEdit && <Bt icon={Plus} style={{ marginTop: 12 }} onClick={() => { setEditVals({}); setShowForm("experience"); }}>Add Experience</Bt>}
                    {(showForm === "experience" || showForm === "edit_experience") && (() => {
                      const isEdit = showForm === "edit_experience"; const ev = editVals; const sd = ev.start_date || ""; const ed = ev.end_date || ""; const dur = calcDuration(sd, ed); const dateErr = sd && ed && new Date(sd) > new Date(ed) ? "Start Date cannot be after End Date" : "";
                      return <div className="fixed inset-0 animate-fadeIn bg-black/35 z-[200] flex items-center justify-center p-3" onMouseDown={() => { setShowForm(null); setEditVals({}); }}>
                        <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-4 sm:p-6 w-full max-w-[712px] max-h-[85vh] overflow-auto shadow-[0_20px_60px_rgba(0,0,0,0.15)]" onMouseDown={e => e.stopPropagation()}>
                          <div className="flex justify-between mb-4"><span className="text-base font-extrabold text-t0">{isEdit ? "Edit Experience" : "Add Experience"}</span><button onClick={() => { setShowForm(null); setEditVals({}); }} className="bg-transparent border-0 text-t3 cursor-pointer"><X size={18} /></button></div>
                          <div className="mb-[10px]"><div className="text-[14px] text-t2 mb-[3px]">Company Name</div><Inp placeholder="TCS" value={ev.company_name || ""} onChange={v => setEditVals(prev => ({ ...prev, company_name: v }))} /></div>
                          <div className="mb-[10px]"><div className="text-[14px] text-t2 mb-[3px]">Designation</div><Inp placeholder="Software Engineer" value={ev.designation || ""} onChange={v => setEditVals(prev => ({ ...prev, designation: v }))} /></div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-[10px] mb-[10px]">
                            <div><div className="text-[14px] text-t2 mb-[3px]">Start Date</div><input type="date" value={ev.start_date || ""} onChange={e => setEditVals(prev => { const n = { ...prev, start_date: e.target.value }; if (prev.end_date && e.target.value > prev.end_date) n.end_date = e.target.value; const d = calcDuration(e.target.value, n.end_date || ""); if (d) n.total_experience = d; return n; })} className="w-full px-[10px] py-[7px] bg-bg2 border border-b0 rounded-[10px] text-t0 text-[14px] outline-none" /></div>
                            <div><div className="text-[14px] text-t2 mb-[3px]">End Date</div><input type="date" min={ev.start_date || ""} value={ev.end_date || ""} onChange={e => setEditVals(prev => { const n = { ...prev, end_date: e.target.value }; const d = calcDuration(prev.start_date || "", e.target.value); if (d) n.total_experience = d; return n; })} className="w-full px-[10px] py-[7px] bg-bg2 border border-b0 rounded-[10px] text-t0 text-[14px] outline-none" /></div>
                          </div>
                          {dateErr && <div className="text-[14px] text-r mb-2">{dateErr}</div>}
                          {dur && <div className="text-[14px] text-g mb-[10px] px-[10px] py-[6px] bg-gb rounded-lg border border-gd">Duration: <strong>{dur}</strong></div>}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-[10px] mb-[10px]">
                            <div><div className="text-[14px] text-t2 mb-[3px]">Last CTC (Annual ₹)</div><Inp placeholder="800000" value={ev.last_salary || ""} onChange={v => { const num = v.replace(/[^0-9.]/g, ""); setEditVals(prev => ({ ...prev, last_salary: num })); }} /></div>
                            <div><div className="text-[14px] text-t2 mb-[3px]">Total Experience</div><div className={`${fieldReadOnlyCls} min-h-[16px]`}>{ev.total_experience || dur || "Auto-calculated from dates"}</div></div>
                          </div>
                          <div className="mb-[10px]">
                            <div className="text-[14px] text-t2 mb-[3px]">Job Description</div>
                            <textarea value={ev.job_description || ""} onChange={e => setEditVals(prev => ({ ...prev, job_description: e.target.value }))} placeholder="Responsibilities, projects, tech stack…" className="w-full min-h-[70px] px-[10px] py-[7px] bg-bg2 border border-b0 rounded-[10px] text-t0 text-[14px] outline-none resize-y" />
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-[10px] mb-[10px]">
                            <div>
                              <div className="text-[14px] text-t2 mb-[3px]">Relevant to Current Role</div>
                              <Sl options={[{ v: "", l: "—" }, { v: "yes", l: "Yes" }, { v: "no", l: "No" }]} value={ev.is_relevant || ""} onChange={v => setEditVals(prev => ({ ...prev, is_relevant: v }))} />
                            </div>
                            <div>
                              <div className="text-[14px] text-t2 mb-[3px]">Experience Letter</div>
                              {ev.experience_letter && <div className="text-[12px] text-t2 mb-1 truncate">Current: {ev.experience_letter}</div>}
                              <label className="flex items-center gap-2 px-3 py-2 rounded-[10px] border-2 border-dashed border-b1 bg-bg2 cursor-pointer text-t2 text-[14px]">
                                <Upload size={12} color={K.p} />{ev._letterFile ? ev._letterFile.name : (ev.experience_letter ? "Replace file" : "Attach PDF / image")}
                                <input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg" className="hidden" onChange={e => { const f = e.target.files[0]; if (f) setEditVals(prev => ({ ...prev, _letterFile: f })); }} />
                              </label>
                            </div>
                          </div>
                          <div className="flex gap-[6px] mt-3">
                            <Bt full disabled={!!dateErr || expBusy} onClick={async () => {
                              if (!ev.company_name) { notify("Company Name is required", { title: "Error", variant: "error" }); return; }
                              if (dateErr) return;
                              setExpBusy(true);
                              try {
                                let letterPath = ev.experience_letter || null;
                                if (ev._letterFile) {
                                  try {
                                    const fd = new FormData(); fd.append('file', ev._letterFile); fd.append('employee_id', empDet.id); fd.append('doc_type', 'Experience Letter');
                                    const upRes = await fetch(BASE + '/api/documents/upload', { method: 'POST', headers: { Authorization: 'Bearer ' + localStorage.getItem('hrms_token') }, body: fd });
                                    const upData = await upRes.json();
                                    if (upData.file_path) letterPath = upData.file_path;
                                  } catch (err) { notify('Experience letter upload failed: ' + (err.message || 'unknown'), { title: "Error", variant: "error" }); return; }
                                }
                                const payload = { company_name: ev.company_name, designation: ev.designation, start_date: ev.start_date || null, end_date: ev.end_date || null, last_salary: parseFloat(ev.last_salary) || 0, total_experience: ev.total_experience || dur || "", job_description: ev.job_description || null, is_relevant: ev.is_relevant === 'yes' ? true : ev.is_relevant === 'no' ? false : null, experience_letter: letterPath };
                                try { if (isEdit) { await api.put(`/api/employees/${empDet.id}/experience/${ev._expId}`, payload); } else { await api.post(`/api/employees/${empDet.id}/experience`, payload); } await reloadEmp(); notify(isEdit ? "Experience updated" : "Experience added", { title: "Saved", variant: "success" }); setShowForm(null); setEditVals({}); } catch (err) { notify(err.message || "Save failed", { title: "Error", variant: "error" }); }
                              } finally { setExpBusy(false); }
                            }}>{expBusy ? (isEdit ? "Updating…" : "Saving…") : (isEdit ? "Update" : "Save")}</Bt>
                            <Bt v="secondary" full onClick={() => { setShowForm(null); setEditVals({}); }}>Cancel</Bt>
                          </div>
                        </div>
                      </div>;
                    })()}
                  </div>;
                }

                if (activeTab === "team") {
                  if (teamNode === null) return <div className="text-center p-10 text-t3 text-[14px]">Loading team…</div>;
                  if (teamNode === false) return <div className="text-center p-10 text-t3 text-[14px]">Couldn't load this person's team.</div>;
                  return <EmployeeTeamTree teamNode={teamNode} empName={empDet?.nm} onOpen={p => setEmpDet({ id: p.id, nm: p.name, pp: p.photo })} />;
                }

                if (activeTab === "separation") {
                  const s = fullEmp?.separation;
                  if (!s) return <div className="text-center p-5 text-t3 text-[14px]">No separation record on file.</div>;
                  const F = ({ l, v }) => <div><div className="text-[12px] text-t3 uppercase mb-[3px]">{l}</div><div className={fieldReadOnlyCls}>{v || "—"}</div></div>;
                  const dt = (d) => fmtDMY(d);
                  return <div>
                    <div className="mb-3 p-3 rounded-lg bg-ab border border-ad text-[14px] text-t0 flex items-start gap-2">
                      <AlertTriangle size={13} color={K.a} />
                      <div>
                        <div className="font-bold">Separation Status: <span className="text-a">{s.status || '—'}</span></div>
                        <div className="text-[14px] text-t3 mt-[2px]">Details are maintained on the Separation module; this panel is read-only.</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-[10px]">
                      <F l="Separation Type" v={s.type} />
                      <F l="Reason Category" v={s.reason_category} />
                      <F l="Date of Resignation" v={dt(s.resignation_date)} />
                      <F l="Date of Exit / Last Working Day" v={dt(s.last_working_day)} />
                      <F l="Notice Period (days)" v={s.notice_period_days != null ? String(s.notice_period_days) : ''} />
                      <F l="Notice Served (days)" v={s.notice_served_days != null ? String(s.notice_served_days) : ''} />
                      <F l="F&F Status" v={s.fnf_status} />
                      <F l="F&F Due Date" v={dt(s.fnf_due_date)} />
                      <F l="Exit Interview Date" v={dt(s.exit_interview_date)} />
                      <F l="Initiated On" v={dt(s.created_at)} />
                    </div>
                    {s.reason_detail && <div className="mt-3">
                      <div className="text-[12px] text-t3 uppercase mb-[3px]">Exit Remarks / Reason Detail</div>
                      <div className="text-[14px] text-t1 whitespace-pre-wrap p-3 rounded-lg bg-bg2 border border-b0">{s.reason_detail}</div>
                    </div>}
                  </div>;
                }

                if (activeTab === "regularization" && can('employees', 'regularization', null, 'view')) {
                  return <div><EmpRegHistoryCard empId={empDet.id} /></div>;
                }

                if (activeTab === "activity_log" && can('employees', 'activity_log', 'view_employee_activity', 'view')) {
                  return <EmpActivityLogCard empId={empDet.id} />;
                }

                if (activeTab === "settings" && (can('employees', 'settings', null, 'view') && !(role === "hr_head" && (fullEmp?.system_role || 'employee') === 'admin'))) {
                  const canLogin = can('employees', 'settings', 'login_credentials', 'view');
                  const canResetPwd = can('employees', 'settings', 'reset_password', 'edit');
                  const canChangeRole = can('employees', 'settings', 'change_system_role', 'edit');
                  const canCentreAccess = can('employees', 'settings', 'centre_access', 'edit');
                  const saveHome = async () => {
                    setSettingsBusy('home');
                    try {
                      await api.put(`/api/employees/${empDet.id}/home-location`, {
                        home_address: editVals._homeAddr || fe.home_address,
                        home_latitude: editVals._homeLat || fe.home_latitude,
                        home_longitude: editVals._homeLng || fe.home_longitude,
                        home_radius_meters: parseInt(editVals._homeRad) || fe.home_radius_meters,
                        attendance_policy: editVals._attPolicy || fe.attendance_policy || 'office_only',
                        alt_location_address: editVals._altAddr ?? fe.alt_location_address,
                        alt_location_lat: editVals._altLat || fe.alt_location_lat,
                        alt_location_lng: editVals._altLng || fe.alt_location_lng,
                        alt_location_radius_meters: parseInt(editVals._altRad) || fe.alt_location_radius_meters
                      }); const d = await api.get('/api/employees/' + empDet.id); setFullEmp(d); setEditVals({});
                      notify('Attendance policy & home location saved', { title: 'Saved', variant: 'success' });
                    } catch (e) { notify(e.message || 'Save failed', { title: 'Error', variant: 'error' }); }
                    finally { setSettingsBusy(null); }
                  };
                  const resetPwd = async () => {
                    setSettingsBusy('pwd');
                    try { const r = await api.put('/auth/reset-password/' + empDet.id, {}); notify(r.emailed_to ? `Password reset — new login emailed to ${r.emailed_to}.` : 'Password reset.', { title: 'Reset', variant: 'success' }); const c = credsFrom(r, empDet.nm); if (c) setNewCreds([c]); setUserAcct(prev => ({ ...prev })); }
                    catch (e) { notify(e.message || 'Password reset failed', { title: 'Error', variant: 'error' }); }
                    finally { setSettingsBusy(null); }
                  };
                  const changeRole = async (newRole) => {
                    setSettingsBusy('role');
                    try { await api.put('/auth/change-role/' + empDet.id, { system_role: newRole }); const d = await api.get('/auth/user/' + empDet.id); setUserAcct(d.data || d); const emp = await api.get('/api/employees/' + empDet.id); setFullEmp(emp); notify('System role updated to ' + newRole, { title: 'Saved', variant: 'success' }); }
                    catch (e) { notify(e.message || 'Role change failed', { title: 'Error', variant: 'error' }); }
                    finally { setSettingsBusy(null); }
                  };
                  const hardDeleteEmp = async () => {
                    const nm = fe.name || fe.nm || empDet.nm || empDet.id;
                    if (!(await confirmDialog(`Permanently delete employee "${nm}" (${empDet.id})?\n\nThis WIPES the employee, their login, leave balances, attendance history, leave requests, expenses, assets, payroll slips, tickets, and documents.\n\nThis cannot be undone.`))) return;
                    const confirm2 = await promptDialog(`Type "${empDet.id}" to confirm permanent deletion:`, { title: 'Confirm deletion', variant: 'error', okLabel: 'Delete', placeholder: empDet.id });
                    if (confirm2 !== empDet.id) { notify("Confirmation text did not match. Cancelled."); return; }
                    try {
                      await api.del('/api/employees/' + empDet.id);
                      setEmpDet(null);
                      setPg(1);
                      api.get(`/api/employees?page=1&limit=20`).then(d => { setEmployees(d.data || []); setTotalEmp(d.total || 0); });
                    } catch (e) { notify(e.message || 'Delete failed'); }
                  };
                  return <div>
                    {canLogin && <Cd style={{ marginBottom: 12 }}>
                      <Sec icon={Lock} title="Login Credentials" />
                      {userAcct?.exists ? <div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-[10px] mb-[10px]">
                          <div><div className="text-[12px] text-t3">Username</div><div className="px-[10px] py-[7px] bg-bg2 rounded-[10px] text-[14px] font-semibold text-t0 border border-b0">{userAcct.username}</div><div className="text-[12px] text-t3 mt-[3px]">Auto-synced with company email{fe.email && userAcct.username !== fe.email.toLowerCase().trim() ? <span className="text-a"> · pending sync to {fe.email.toLowerCase().trim()} (save Basic Info)</span> : ""}</div></div>
                          <div><div className="text-[12px] text-t3">System Role</div>
                            <Sl options={[{ v: "employee", l: "Employee" }, { v: "centre_head", l: "Centre Head" }, { v: "centre_hr", l: "Centre HR" }, { v: "manager", l: "Manager" }, { v: "team_leader", l: "Team Leader" }, { v: "team_incharge", l: "Team Incharge" }, { v: "recruiter", l: "Recruiter" }, { v: "accounts", l: "Accounts" }, { v: "hr_head", l: "HR Head" }, ...(role === "admin" ? [{ v: "admin", l: "Admin" }] : [])]} disabled={!canChangeRole} value={editVals._sysRole ?? (userAcct.system_role || "employee")} onChange={v => setEditVals(prev => ({ ...prev, _sysRole: v }))} />
                            {editVals._sysRole && editVals._sysRole !== (userAcct.system_role || "employee") && <div className="flex items-center gap-2 mt-2">
                              <Bt v="success" s="xs" icon={CheckCircle} disabled={settingsBusy === 'role'} onClick={async () => {
                                const newRole = editVals._sysRole;
                                if (!(await confirmDialog(`Change ${empDet.nm}'s system role to "${newRole}"? This takes effect immediately and changes what they can access on next login.`))) return;
                                await changeRole(newRole);
                                setEditVals(prev => { const n = { ...prev }; delete n._sysRole; return n; });
                              }}>{settingsBusy === 'role' ? 'Saving…' : 'Save Role'}</Bt>
                              <button onClick={() => setEditVals(prev => { const n = { ...prev }; delete n._sysRole; return n; })} className="text-[14px] text-t3 bg-transparent border-0 cursor-pointer">Cancel</button>
                              <span className="text-[12px] text-a">Unsaved change</span>
                            </div>}
                          </div>
                        </div>
                        <div className="flex gap-2 items-center flex-wrap">
                          {canResetPwd && <Bt v="secondary" s="sm" icon={Lock} disabled={settingsBusy === 'pwd'} onClick={resetPwd}>{settingsBusy === 'pwd' ? 'Resetting…' : 'Reset Password'}</Bt>}
                          {canResetPwd && <span className="text-[12px] text-t3">A new random password is generated and emailed</span>}
                        </div>
                        {userAcct.last_login && <div className="text-[12px] text-t3 mt-[6px]">Last login: {fmtDMY(userAcct.last_login)}, {new Date(userAcct.last_login).toLocaleTimeString('en-IN')}</div>}
                      </div> : <div>
                        <div className="p-3 bg-ab rounded-lg border border-ad mb-2">
                          <div className="flex items-center gap-[6px]"><AlertTriangle size={12} color={K.a} /><span className="text-[14px] font-semibold text-a">No login account exists for this employee</span></div>
                          {fe.email ? <div className="text-[12px] text-t2 mt-1">Username will be: <strong>{fe.email.toLowerCase().trim()}</strong> (company email)</div>
                            : <div className="text-[12px] text-r mt-1">Set a Company Email in Basic Info first — username is derived from it.</div>}
                        </div>
                        <Bt s="sm" disabled={!fe.email || settingsBusy === 'createAcct'} onClick={async () => {
                          if (!fe.email) { notify('Set a Company Email in Basic Info first.', { title: 'Error', variant: 'error' }); return; }
                          const username = fe.email.toLowerCase().trim();
                          setSettingsBusy('createAcct');
                          try {
                            const hash = await fetch(BASE + '/auth/create-user', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + localStorage.getItem('hrms_token') }, body: JSON.stringify({ employee_id: empDet.id, username }) }).then(r => r.json());
                            if (hash.created) { notify(hash.emailed_to ? `Account created — login emailed to ${hash.emailed_to}.` : 'Account created.', { title: 'Saved', variant: 'success' }); const c = credsFrom({ ...hash, username }, empDet.nm); if (c) setNewCreds([c]); const d = await api.get('/auth/user/' + empDet.id); setUserAcct(d.data || d); }
                            else notify(hash.error || 'Failed to create account', { title: 'Error', variant: 'error' });
                          } catch (e) { notify(e.message || 'Failed to create account', { title: 'Error', variant: 'error' }); }
                          finally { setSettingsBusy(null); }
                        }}>{settingsBusy === 'createAcct' ? 'Creating…' : 'Create Login Account'}</Bt>
                      </div>}
                    </Cd>}
                    <Cd>
                      <Sec icon={MapPin} title="Attendance & Home Location" />
                      <div className="text-[14px] text-t2 mb-[10px]">Controls where this employee can check in from. Home coordinates are used when policy is Office + Home. The Additional Location below (optional) is a third check-in zone that works alongside whatever policy is set.</div>
                      {can('employees', 'settings', 'home_location', 'edit') ? <div className="mb-3 p-[10px] bg-bg2 rounded-lg border border-b0">
                        <div className="text-[12px] text-t3 mb-1 font-semibold uppercase tracking-[0.03em]">Attendance Policy</div>
                        <Sl options={[{ v: "office_only", l: "Office Only — must be inside office radius" }, { v: "office_home", l: "Office + Home — office OR registered home/WFH location" }, { v: "any_location", l: "Any Location — no geo-fence (sales, field, directors)" }]} value={editVals._attPolicy || fe.attendance_policy || "office_only"} onChange={v => setEditVals(prev => ({ ...prev, _attPolicy: v }))} />
                        {(editVals._attPolicy || fe.attendance_policy) === "any_location" && <div className="text-[12px] text-a mt-[6px] flex items-center gap-1"><AlertTriangle size={10} />No location restriction. GPS coordinates still saved for audit.</div>}
                        {(editVals._attPolicy || fe.attendance_policy) === "office_home" && !fe.home_address && <div className="text-[12px] text-a mt-[6px] flex items-center gap-1"><AlertTriangle size={10} />Set a home address below so WFH check-ins can be verified.</div>}
                      </div> : <div className="mb-3 p-2 bg-bg2 rounded-lg border border-b0 text-[14px] text-t2">
                        <strong>Current policy:</strong> {fe.attendance_policy === "any_location" ? "Any Location" : fe.attendance_policy === "office_home" ? "Office + Home" : "Office Only"} <span className="text-t3">(Admin/HR can change)</span>
                      </div>}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-[10px]">
                        <div className="md:col-span-2"><div className="text-[12px] text-t3 mb-[3px]">Home Address</div><Inp value={editVals._homeAddr ?? fe.home_address ?? ""} onChange={v => setEditVals(prev => ({ ...prev, _homeAddr: v }))} placeholder="Full home address" /></div>
                        <div><div className="text-[12px] text-t3 mb-[3px]">Latitude</div><Inp value={editVals._homeLat ?? fe.home_latitude ?? ""} onChange={v => setEditVals(prev => ({ ...prev, _homeLat: v }))} placeholder="28.6350" /></div>
                        <div><div className="text-[12px] text-t3 mb-[3px]">Longitude</div><Inp value={editVals._homeLng ?? fe.home_longitude ?? ""} onChange={v => setEditVals(prev => ({ ...prev, _homeLng: v }))} placeholder="77.2200" /></div>
                        <div><div className="text-[12px] text-t3 mb-[3px]">Allowed Radius (meters)</div><Inp value={editVals._homeRad ?? fe.home_radius_meters ?? "200"} onChange={v => setEditVals(prev => ({ ...prev, _homeRad: v }))} placeholder="200" /></div>
                      </div>
                      {fe.home_address && <div className="mt-[10px] p-2 bg-gb rounded-lg border border-gd">
                        <div className="flex items-center gap-[6px]"><MapPin size={12} color={K.g} /><span className="text-[14px] font-semibold text-g">Home Location Set</span></div>
                        <div className="text-[14px] text-t1 mt-[3px]">{fe.home_address}</div>
                        <div className="text-[12px] text-t3 mt-[2px]">Lat: {fe.home_latitude} · Lng: {fe.home_longitude} · Radius: {fe.home_radius_meters || 200}m</div>
                      </div>}
                      {!fe.home_address && <div className="mt-[10px] p-2 bg-ab rounded-lg border border-ad">
                        <div className="flex items-center gap-[6px]"><AlertTriangle size={12} color={K.a} /><span className="text-[14px] font-semibold text-a">Home location not set — WFH attendance cannot be verified</span></div>
                      </div>}

                      <div className="mt-[16px] pt-[14px] border-t border-b0">
                        <div className="text-[12px] text-t3 mb-1 font-semibold uppercase tracking-[0.03em]">Additional Check-in Location (optional)</div>
                        <div className="text-[12px] text-t3 mb-[8px]">A third fixed spot — a client site, second office, or field location — this employee can also check in/out from, regardless of the policy above.</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-[10px]">
                          <div className="md:col-span-2"><div className="text-[12px] text-t3 mb-[3px]">Address / Label</div><Inp value={editVals._altAddr ?? fe.alt_location_address ?? ""} onChange={v => setEditVals(prev => ({ ...prev, _altAddr: v }))} placeholder="e.g. Client site — Sector 44 Gurgaon" /></div>
                          <div><div className="text-[12px] text-t3 mb-[3px]">Latitude</div><Inp value={editVals._altLat ?? fe.alt_location_lat ?? ""} onChange={v => setEditVals(prev => ({ ...prev, _altLat: v }))} placeholder="28.6350" /></div>
                          <div><div className="text-[12px] text-t3 mb-[3px]">Longitude</div><Inp value={editVals._altLng ?? fe.alt_location_lng ?? ""} onChange={v => setEditVals(prev => ({ ...prev, _altLng: v }))} placeholder="77.2200" /></div>
                          <div><div className="text-[12px] text-t3 mb-[3px]">Allowed Radius (meters)</div><Inp value={editVals._altRad ?? fe.alt_location_radius_meters ?? "200"} onChange={v => setEditVals(prev => ({ ...prev, _altRad: v }))} placeholder="200" /></div>
                        </div>
                        {fe.alt_location_address && <div className="mt-[10px] p-2 bg-pub rounded-lg" style={{ border: `1px solid ${K.pu}22` }}>
                          <div className="flex items-center gap-[6px]"><MapPin size={12} color={K.pu} /><span className="text-[14px] font-semibold" style={{ color: K.pu }}>Additional Location Set</span></div>
                          <div className="text-[14px] text-t1 mt-[3px]">{fe.alt_location_address}</div>
                          <div className="text-[12px] text-t3 mt-[2px]">Lat: {fe.alt_location_lat} · Lng: {fe.alt_location_lng} · Radius: {fe.alt_location_radius_meters || 200}m</div>
                        </div>}
                      </div>

                      <Bt style={{ marginTop: 12 }} disabled={settingsBusy === 'home'} onClick={saveHome}>{settingsBusy === 'home' ? 'Saving…' : 'Save Attendance Policy & Home Location'}</Bt>
                    </Cd>
                    <Cd style={{ marginTop: 12 }}>
                      <Sec icon={Calendar} title="Work Schedule" />
                      <div className="flex justify-between items-center flex-wrap gap-2">
                        <div><div className="text-[14px] font-semibold text-t0">Saturday Working</div><div className="text-[12px] text-t3">Enable for 6-day work week (Monday to Saturday)</div></div>
                        <button disabled={settingsBusy === 'saturday'} onClick={async () => { const nv = !(fullEmp?.saturday_working); setSettingsBusy('saturday'); try { await api.put(`/api/employees/${empDet.id}/saturday-working`, { saturday_working: nv }); const d = await api.get('/api/employees/' + empDet.id); setFullEmp(d); notify('Saturday working ' + (nv ? 'enabled' : 'disabled'), { title: 'Saved', variant: 'success' }); } catch (e) { notify(e.message || 'Failed to update Saturday working', { title: 'Error', variant: 'error' }); } finally { setSettingsBusy(null); } }} className="w-11 h-6 rounded-xl border-0 cursor-pointer relative transition-colors" style={{ background: fullEmp?.saturday_working ? K.g : K.b1 }}>
                          <div className="w-[18px] h-[18px] rounded-full bg-white absolute top-[3px] transition-[left] shadow-[0_1px_3px_rgba(0,0,0,0.2)]" style={{ left: fullEmp?.saturday_working ? 23 : 3 }} />
                        </button>
                      </div>
                      <div className="flex justify-between items-center flex-wrap gap-2 mt-3 pt-3 border-t border-b0">
                        <div>
                          <div className="text-[14px] font-semibold text-t0">Shift</div>
                          <div className="text-[12px] text-t3">Overrides the org-wide check-in reminder time and late/half-day/absent thresholds for this employee. No shift = org defaults (Attendance &gt; Settings &amp; Geo-fences).</div>
                        </div>
                        <div className="w-[220px] flex-shrink-0">
                          <Sl
                            options={[{ v: "", l: "No shift (org defaults)" }, ...shiftsList.map(s => ({ v: s.id, l: `${s.name} (${s.start_time}–${s.end_time})` }))]}
                            value={fullEmp?.shift_id || ""}
                            onChange={async (v) => {
                              setSettingsBusy('shift');
                              try {
                                await api.put(`/api/employees/${empDet.id}/shift`, { shift_id: v || null });
                                const d = await api.get('/api/employees/' + empDet.id);
                                setFullEmp(d);
                                notify(v ? 'Shift assigned' : 'Shift cleared — using org defaults', { title: 'Saved', variant: 'success' });
                              } catch (e) { notify(e.message || 'Failed to update shift', { title: 'Error', variant: 'error' }); }
                              finally { setSettingsBusy(null); }
                            }}
                          />
                        </div>
                      </div>
                      {can('attendance', 'shift_requests', 'manage_dates', 'edit') && <EmployeeShiftDates employeeId={empDet.id} shifts={shiftsList} />}
                    </Cd>
                    {canCentreAccess && <Cd style={{ marginTop: 12 }}>
                      <Sec icon={Building} title="Centre / Location Access" />
                      <div className="text-[14px] text-t2 mb-[10px]">Restrict which offices/companies this employee can see data for. Leave empty for all centres.</div>
                      {(() => {
                        const reloadAccess = () => api.get('/api/access-control/employee/' + empDet.id).then(d => setEmpAccess(d.data || d)).catch(console.error);
                        const addCentre = async (type, id) => {
                          const payload = type === "office" ? { office_id: parseInt(id) } : { company_id: parseInt(id) };
                          setSettingsBusy('centreAdd');
                          try { await api.post('/api/access-control/employee/' + empDet.id + '/centres', payload); await reloadAccess(); notify('Centre access added', { title: 'Saved', variant: 'success' }); }
                          catch (e) { notify(e.message || 'Failed to add centre access', { title: 'Error', variant: 'error' }); }
                          finally { setSettingsBusy(null); }
                        };
                        const removeCentre = async (id) => {
                          setSettingsBusy('centreRemove:' + id);
                          try { await api.del('/api/access-control/employee/' + empDet.id + '/centres/' + id); await reloadAccess(); notify('Centre access removed', { title: 'Saved', variant: 'success' }); }
                          catch (e) { notify(e.message || 'Failed to remove centre access', { title: 'Error', variant: 'error' }); }
                          finally { setSettingsBusy(null); }
                        };
                        const clearAll = async () => {
                          setSettingsBusy('centreClear');
                          try { await api.del('/api/access-control/employee/' + empDet.id + '/centres'); await reloadAccess(); notify('Reset to all centres', { title: 'Saved', variant: 'success' }); }
                          catch (e) { notify(e.message || 'Failed to reset centre access', { title: 'Error', variant: 'error' }); }
                          finally { setSettingsBusy(null); }
                        };
                        return <div>
                          {empAccess?.allCentres && <Bg c={K.g} sm dot style={{ marginBottom: 8 }}>All Centres — Full Access</Bg>}
                          {empAccess?.centres?.length > 0 && <div className="mb-2">
                            {empAccess.centres.map(c => <div key={c.id} className="inline-flex items-center gap-1 px-2 py-[3px] bg-pg rounded-md border border-pb mr-1 mb-1">
                              <span className="text-[14px] text-p">{c.officeName || c.companyName || "Centre"}{c.city ? " — " + c.city : ""}</span>
                              <button disabled={settingsBusy === 'centreRemove:' + c.id} onClick={() => removeCentre(c.id)} className="bg-transparent border-0 text-r cursor-pointer text-[14px]">×</button>
                            </div>)}
                            <Bt v="ghost" s="xs" disabled={settingsBusy === 'centreClear'} onClick={clearAll}>{settingsBusy === 'centreClear' ? 'Resetting…' : 'Reset to All'}</Bt>
                          </div>}
                          <div className="flex gap-[6px] flex-wrap">
                            <Sl options={[{ v: "", l: settingsBusy === 'centreAdd' ? "Adding…" : "Add Office..." }, ...OFC.map(o => ({ v: String(o.id), l: (o.nm || o.name) + " — " + (o.city || "") }))]} value="" onChange={v => { if (v) addCentre("office", v); }} />
                            <Sl options={[{ v: "", l: settingsBusy === 'centreAdd' ? "Adding…" : "Add Company..." }, ...companies.map(c => ({ v: String(c.id), l: c.name }))]} value="" onChange={v => { if (v) addCentre("company", v); }} />
                          </div>
                        </div>;
                      })()}
                    </Cd>}
                  </div>;
                }

                return null;
              })()}
              {activeTab === "settings" && (can('employees', 'settings', null, 'view') && !(role === "hr_head" && (fullEmp?.system_role || 'employee') === 'admin')) && (() => {
                // `fe` is the merged employee record. The big sibling IIFE
                // above declares its own `fe` from `fullEmp`, but its
                // scope ends with the IIFE — so we have to recompute it
                // here. (Previously this block referenced `fe` without
                // defining it, which threw "ReferenceError: fe is not
                // defined" the moment the user clicked the Settings tab,
                // crashing the whole panel render.)
                const canHardDelete = can('employees', 'settings', 'delete_employee', 'delete');
                const canPurge = can('employees', 'settings', 'purge_employee', 'edit');
                const raw = fullEmp || {};
                const b = raw.basic || raw;
                const emp = raw.employment || raw;
                const fe = { ...b, ...emp, status: raw.status || b.status || emp.status };
                const nm = fe.name || fe.nm || empDet.nm || empDet.id;
                const di = deleteInfo;
                const refreshList = () => { setSettingsBusy(null); setEmpDet(null); setPg(1); api.get(`/api/employees?page=1&limit=20`).then(d => { setEmployees(d.data || []); setTotalEmp(d.total || 0); }); };
                const isInactive = (di?.employee?.status || fe.status) === 'Inactive';

                const onPurge = async () => {
                  setSettingsBusy('purge');
                  try {
                    const r = await api.post(`/api/employees/${empDet.id}/purge`, { confirmation: empDet.id });
                    notify(`${nm} purged. Backup: ${r.backup}`, { title: 'Purged', variant: 'success' });
                    setPurgeOpen(false);
                    refreshList();
                  } catch (e) { notify(e.message || 'Purge failed', { title: 'Error', variant: 'error' }); setSettingsBusy(null); }
                };
                const onDeactivate = async () => {
                  if (!(await confirmDialog(`Deactivate ${nm} (${empDet.id})?\n\nThis sets the employee to Inactive status and disables their login. All their existing records (leaves, attendance, payslips, etc.) are preserved. You can re-activate them later from Employees → filter "Inactive only".`))) return;
                  setSettingsBusy('deactivate');
                  try {
                    await api.put(`/api/employees/${empDet.id}/deactivate`, {});
                    notify(`${nm} has been deactivated.`, { title: 'Deactivated', variant: 'success' });
                    refreshList();
                  } catch (e) { notify(e.message || 'Deactivate failed', { title: 'Error', variant: 'error' }); setSettingsBusy(null); }
                };
                const onReactivate = async () => {
                  if (!(await confirmDialog(`Reactivate ${nm} (${empDet.id})?\n\nThis flips them back to Active status and re-enables their login. They'll show up in the standard employee list again and payroll will process them from the next run.`))) return;
                  setSettingsBusy('reactivate');
                  try {
                    await api.put(`/api/employees/${empDet.id}/reactivate`, {});
                    notify(`${nm} has been reactivated.`, { title: 'Reactivated', variant: 'success' });
                    refreshList();
                  } catch (e) { notify(e.message || 'Reactivate failed', { title: 'Error', variant: 'error' }); setSettingsBusy(null); }
                };
                const onDelete = async () => {
                  if (!(await confirmDialog(`Permanently delete ${nm} (${empDet.id})?\n\nThis employee has no related records, so the row + login can be hard-deleted safely. This cannot be undone.`))) return;
                  setSettingsBusy('delete');
                  try {
                    await api.del('/api/employees/' + empDet.id);
                    notify(`${nm} permanently deleted.`, { title: 'Deleted', variant: 'success' });
                    refreshList();
                  } catch (e) { notify(e.message || 'Delete failed', { title: 'Error', variant: 'error' }); setSettingsBusy(null); }
                };

                return <>{can('employees', 'settings', 'send_employee_docs', 'edit') && <EmployeeDocsSend empId={empDet.id} name={nm} />}<Cd style={{ marginTop: 12, borderLeft: `3px solid ${K.r}` }}>
                  <Sec icon={Trash2} title="Danger Zone" />

                  {!di && <div className="text-[14px] text-t3">Loading delete info…</div>}

                  {di && !isInactive && di.can_delete && <div>
                    <div className="text-[14px] text-t2 mb-[10px]">
                      {canHardDelete
                        ? <><span className="text-g font-semibold">No related records</span> — this employee row was never used. Permanent delete will remove the employee + their login. Cannot be undone.</>
                        : <>This employee row has no linked records yet, but you do not have permission to delete permanently. You can deactivate the account (login off, records preserved).</>}
                    </div>
                    {can('employees', 'settings', 'deactivate_employee', 'edit') && <Bt v="danger" s="sm" icon={X} disabled={!!settingsBusy} onClick={onDeactivate} style={{ marginRight: 8 }}>{settingsBusy === 'deactivate' ? 'Deactivating…' : 'Deactivate Employee'}</Bt>}
                    {canHardDelete && <Bt v="danger" s="sm" icon={Trash2} disabled={!!settingsBusy} onClick={onDelete}>{settingsBusy === 'delete' ? 'Deleting…' : 'Permanently Delete Employee'}</Bt>}
                  </div>}

                  {di && !isInactive && !di.can_delete && <div>
                    <div className="text-[14px] text-t1 mb-2">
                      <span className="font-bold">Permanent delete is not available</span> — this employee has <span className="font-bold">{di.total_related}</span> related record{di.total_related === 1 ? '' : 's'} that would be lost:
                    </div>
                    <div className="text-[14px] text-t2 mb-[10px] grid grid-cols-2 gap-x-3 gap-y-1">
                      {di.blockers.map(b => <div key={b.key}>· {b.label}: <span className="font-semibold text-t1">{b.count}</span></div>)}
                    </div>
                    <div className="text-[14px] text-t3 italic mb-3">
                      Deactivating sets the employee to Inactive (no login, no payroll), preserves all history, and you can re-activate later. For a legitimate departure, use the Separation module instead.
                    </div>
                    {can('employees', 'settings', 'deactivate_employee', 'edit') && <Bt v="danger" s="sm" icon={X} disabled={!!settingsBusy} onClick={onDeactivate}>{settingsBusy === 'deactivate' ? 'Deactivating…' : 'Deactivate Employee'}</Bt>}
                  </div>}

                  {di && isInactive && <div>
                    <div className="text-[14px] text-t2 mb-[10px]">
                      <span className="text-a font-semibold">Currently Inactive</span> — login disabled, hidden from payroll runs and the default employee list. Choose how to proceed:
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {can('employees', 'settings', 'activate_employee', 'edit') && <Bt v="success" s="sm" icon={CheckCircle} disabled={!!settingsBusy} onClick={onReactivate}>{settingsBusy === 'reactivate' ? 'Reactivating…' : 'Reactivate Employee'}</Bt>}
                      {canPurge && <Bt v="danger" s="sm" icon={Trash2} disabled={!!settingsBusy} onClick={() => { setPurgeOpen(true); setPurgeConfirm(''); }}>Purge Employee (backup + hard delete)</Bt>}
                    </div>
                    <div className="text-[14px] text-t3 italic mt-2">
                      {canPurge
                        ? <>Reactivate = puts them back into Active. Purge = writes a JSON backup to <code className="bg-bg2 px-1 rounded">server/backups/purged-employees/</code>, then hard-deletes the employee + every related record. Use Purge for genuinely junk data only.</>
                        : <>Reactivate = puts them back into Active. You do not have permission to purge.</>}
                    </div>
                  </div>}
                </Cd>
                {purgeOpen && (
                  <div className="fixed inset-0 animate-fadeIn bg-black/50 z-[300] flex items-center justify-center p-3" onMouseDown={() => setPurgeOpen(false)}>
                    <div className="animate-modalIn bg-bg1 border border-r/40 rounded-2xl p-5 w-full max-w-[768px] shadow-[0_20px_60px_rgba(200,0,0,0.25)]" onMouseDown={e => e.stopPropagation()}>
                      <div className="flex items-center gap-2 mb-2">
                        <Trash2 size={18} className="text-r" />
                        <span className="text-[17px] font-extrabold text-r">Purge {nm}?</span>
                      </div>
                      <div className="text-[14px] text-t1 leading-[1.6] mb-3">
                        This will <b className="text-r">permanently delete</b> the employee row, their login, and every related record (attendance, payslips, leaves, expenses, appraisals, documents, tickets — everything). A JSON backup of all rows is written to <code className="bg-bg2 px-1 rounded text-[14px]">server/backups/purged-employees/</code> before the delete runs, so you have a paper trail if you need to restore.
                      </div>
                      <div className="text-[14px] text-t2 mb-1">
                        Type <b className="text-t0 font-mono">{empDet.id}</b> to confirm:
                      </div>
                      <input
                        type="text"
                        value={purgeConfirm}
                        onChange={e => setPurgeConfirm(e.target.value)}
                        placeholder={empDet.id}
                        className="w-full bg-bg2 border border-b1 rounded-lg px-3 py-2 text-[15px] font-mono text-t0 focus:outline-none focus:border-r mb-3"
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <Bt v="secondary" full disabled={settingsBusy === 'purge'} onClick={() => setPurgeOpen(false)}>Cancel</Bt>
                        <Bt v="danger" full disabled={purgeConfirm !== empDet.id || settingsBusy === 'purge'} onClick={onPurge}>
                          {settingsBusy === 'purge' ? 'Purging…' : 'Purge permanently'}
                        </Bt>
                      </div>
                    </div>
                  </div>
                )}
                </>;
              })()}
            </div>
            <div className={`relative order-1 flex flex-col border-r border-b0 flex-shrink-0 bg-bg2 transition-all duration-200 ${railCollapsed ? "w-[52px] min-w-[52px] p-[6px]" : "w-[204px] min-w-[204px] p-2"}`}>
              <button
                onClick={() => setRailCollapsed(v => !v)}
                title={railCollapsed ? "Expand" : "Collapse"}
                className="absolute top-[12px] -right-[15px] z-20 w-[30px] h-[30px] rounded-full flex items-center justify-center border-2 border-bg1 cursor-pointer shadow-[0_2px_8px_rgba(0,0,0,0.22)] transition-transform duration-150 hover:scale-110 active:scale-90"
                style={{ background: K.p }}
              >
                {railCollapsed ? <ChevronRight size={16} color="#0B1220" strokeWidth={2.75} /> : <ChevronLeft size={16} color="#0B1220" strokeWidth={2.75} />}
              </button>
              {/* Scrolls independently of the outer rail div — keeping
                  overflow-y-auto off that outer div is what lets the toggle
                  button above poke past the rail's right edge instead of
                  getting clipped (overflow-y non-visible forces overflow-x
                  to a clipping value too, per spec). */}
              <div key={railCollapsed ? "c" : "e"} className="flex flex-col gap-[2px] mt-[36px] overflow-y-auto flex-1">
                {[["basic", "Basic Info", CircleUser], ["employment", "Employment", Briefcase], ["team", "Team", Users],
                  ...(empDet?.id === myEmployeeId || can('employees', 'salary', null, 'view') ? [["salary", "Salary Structure", Wallet]] : []),
                  ...(empDet?.id === myEmployeeId || can('employees', 'payroll', null, 'view') ? [["payroll", "Payroll", Wallet]] : []),
                  ...(empDet?.id === myEmployeeId || can('employees', 'documents', null, 'view') ? [["documents", "Documents", FolderOpen]] : []),
                  ...((empDet?.id === myEmployeeId || can('employees', 'ghi', null, 'view')) ? [["ghi", "GHI", Heart]] : []),
                  ...((empDet?.id === myEmployeeId || can('employees', 'training_review', null, 'view') || fullEmp?.employment?.manager_id === myEmployeeId || fullEmp?.employment?.training_manager_id === myEmployeeId || fullEmp?.employment?.training_review_access) ? [["training_review", "Training Review", ClipboardList]] : []),
                  ...((empDet?.id === myEmployeeId || can('employees', 'probation_eval', null, 'view') || fullEmp?.employment?.manager_id === myEmployeeId) ? [["probation_eval", "Probation Evaluation", ClipboardCheck]] : []),
                  ["emergency", "Emergency", Phone], ["education", "Education", GraduationCap], ["experience", "Experience", Building], ...(fullEmp?.separation ? [["separation", "Separation", FileText]] : []), ...(can('employees', 'regularization', null, 'view') ? [["regularization", "Regularization History", FileText]] : []),
                  ...((can('employees', 'settings', null, 'view') && !(role === "hr_head" && (fullEmp?.system_role || 'employee') === 'admin')) ? [["settings", "Settings", Settings]] : []),
                  ...(can('employees', 'activity_log', 'view_employee_activity', 'view') ? [["activity_log", "Activity Log", History]] : [])].map(([tid, tl, Tic], i) =>
                  <button
                    key={tid}
                    onClick={() => { setActiveTab(tid); setEditVals({}); }}
                    title={railCollapsed ? tl : undefined}
                    style={{ animationDelay: `${i * 25}ms` }}
                    className={`animate-fadeUp flex items-center gap-[10px] rounded-[9px] border-0 bg-transparent text-left text-[14px] cursor-pointer transition-colors py-[9px] ${railCollapsed ? "justify-center px-0" : "w-full px-[10px]"} ${activeTab === tid ? "bg-pg text-p font-bold" : "text-t2 font-medium hover:bg-bg3 hover:text-t0"}`}
                  ><Tic size={14} className="flex-shrink-0" />{!railCollapsed && tl}</button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>}
    {editVals._adjustLB && <LeaveBalanceAdjustModal
      employeeId={editVals._adjustLB}
      currentBalances={fullEmp?.leave_balance || {}}
      onClose={() => setEditVals(prev => { const n = { ...prev }; delete n._adjustLB; return n; })}
      onSaved={async () => {
        setEditVals(prev => { const n = { ...prev }; delete n._adjustLB; return n; });
        const d = await api.get('/api/employees/' + empDet.id);
        setFullEmp(d);
      }}
    />}
  </div>;
};

// LeaveBalanceAdjustModal — admin/hr_head. Loads active leave types +
// current balances, lets the caller type in the new (balance, used) per
// type, requires a reason (>=5 chars — enforced server-side too),
// posts to PUT /api/leaves/balance/:eid. Full audit trail lives in
// leave_balance_adjustments, viewable via GET /:eid/history.
const LeaveBalanceAdjustModal = ({ employeeId, currentBalances, onClose, onSaved }) => {
  const [types, setTypes] = React.useState([]);
  const [values, setValues] = React.useState({}); // { CL: { balance, used } }
  const [reason, setReason] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [history, setHistory] = React.useState([]);
  const [showHistory, setShowHistory] = React.useState(false);

  React.useEffect(() => {
    // Load active leave types + seed values from current balances so
    // admin only edits the deltas.
    api.get('/api/leaves/types').then(d => {
      const list = (d.data || d || []).filter(t => t.active !== false);
      setTypes(list);
      const seeded = {};
      list.forEach(t => {
        const cb = currentBalances[t.cd] || {};
        seeded[t.cd] = {
          balance: cb.balance != null ? String(cb.balance) : String(t.bal || 0),
          used:    cb.used    != null ? String(cb.used)    : '0',
        };
      });
      setValues(seeded);
    }).catch(() => setTypes([]));
    api.get('/api/leaves/balance/' + employeeId + '/history').then(d => setHistory(d.rows || [])).catch(() => setHistory([]));
  }, [employeeId]); // eslint-disable-line

  const setField = (code, key, v) => setValues(p => ({ ...p, [code]: { ...(p[code] || {}), [key]: v } }));

  const submit = async () => {
    if (reason.trim().length < 5) { setErr('Reason is required (min 5 chars).'); return; }
    const adjustments = Object.entries(values).map(([leave_type, v]) => ({
      leave_type,
      balance: parseFloat(v.balance),
      used:    parseFloat(v.used),
    })).filter(a => Number.isFinite(a.balance) && Number.isFinite(a.used));
    if (!adjustments.length) { setErr('Nothing to adjust.'); return; }
    setBusy(true); setErr('');
    try {
      await api.put('/api/leaves/balance/' + employeeId, { adjustments, reason: reason.trim() });
      notify('Leave balance adjusted', { title: 'Saved', variant: 'success' });
      onSaved();
    } catch (e) { setErr(e.message || 'Save failed'); notify(e.message || 'Save failed', { title: 'Error', variant: 'error' }); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/40 z-[300] flex items-center justify-center p-3" onMouseDown={() => !busy && onClose()}>
      <div className="animate-modalIn bg-bg1 border border-b1 rounded-2xl p-5 w-full max-w-[936px] max-h-[90vh] overflow-auto shadow-[0_20px_60px_rgba(0,0,0,0.15)]" onMouseDown={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[17px] font-extrabold text-t0">Adjust Leave Balance</div>
            <div className="text-[14px] text-t3 mt-[2px]">Employee: <span className="font-mono">{employeeId}</span> · Admin only · Every change lands in the audit trail with your username + reason.</div>
          </div>
          <button onClick={onClose} className="bg-transparent border-0 text-t3 cursor-pointer" disabled={busy}><X size={18} /></button>
        </div>

        <div className="mb-3 p-2 bg-bg2 border border-b0 rounded text-[14px] text-t2">
          Use this to sync balances from the previous HRMS (e.g. Zoho) or to correct a bad accrual. Values are <b className="text-t0">absolute</b>, not deltas — enter the final number you want on the record.
        </div>

        <div className="mb-3">
          <div className="grid grid-cols-[80px_1fr_1fr] gap-2 text-[12px] font-semibold text-t3 uppercase mb-1 px-1">
            <div>Type</div><div>Balance (allocated)</div><div>Used</div>
          </div>
          {types.length === 0 && <div className="text-[14px] text-t3 py-3 text-center">Loading leave types…</div>}
          {types.map(t => (
            <div key={t.cd} className="grid grid-cols-[80px_1fr_1fr] gap-2 items-center mb-[6px]">
              <div className="text-[14px] font-semibold text-t0">
                {t.cd}
                <div className="text-[12px] text-t3 font-normal truncate">{t.nm}</div>
              </div>
              <input type="number" step="0.5" min="0" value={values[t.cd]?.balance ?? ''} onChange={e => setField(t.cd, 'balance', e.target.value)}
                className="w-full px-2 py-[6px] bg-bg2 border border-b0 rounded text-t0 text-[14px] text-right outline-none" />
              <input type="number" step="0.5" min="0" value={values[t.cd]?.used ?? ''} onChange={e => setField(t.cd, 'used', e.target.value)}
                className="w-full px-2 py-[6px] bg-bg2 border border-b0 rounded text-t0 text-[14px] text-right outline-none" />
            </div>
          ))}
        </div>

        <div className="mb-3">
          <div className="text-[14px] text-t3 mb-[3px]">Reason * <span className="text-t3">({reason.trim().length}/5 min)</span></div>
          <textarea rows={2} value={reason} onChange={e => setReason(e.target.value)}
            placeholder="e.g. Zoho migration — synced opening balances as of 1-Jul-2026"
            className="w-full p-2 bg-bg2 border border-b0 rounded text-t0 text-[14px] outline-none resize-y" />
        </div>

        {history.length > 0 && (
          <div className="mb-3">
            <button onClick={() => setShowHistory(v => !v)} className="text-[14px] text-p font-semibold bg-transparent border-0 cursor-pointer">
              {showHistory ? 'Hide' : 'Show'} adjustment history ({history.length})
            </button>
            {showHistory && (
              <div className="mt-2 max-h-[160px] overflow-auto text-[14px] bg-bg2 border border-b0 rounded p-2">
                {history.map(h => (
                  <div key={h.id} className="py-[3px] border-b border-b0 last:border-0">
                    <span className="font-mono text-t0">{h.leave_type}</span> <span className="text-t3">{h.before_balance ?? '—'}/{h.before_used ?? '—'} → {h.after_balance}/{h.after_used}</span>
                    <div className="text-[12px] text-t3">by {h.adjuster_name || h.adjusted_by} · {fmtDMY(h.adjusted_at)}, {new Date(h.adjusted_at).toLocaleTimeString('en-IN')} — {h.reason}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {err && <div className="text-[14px] text-r bg-rb border border-rd rounded-lg px-3 py-2 mb-3">{err}</div>}
        <div className="flex gap-2">
          <Bt v="secondary" full onClick={onClose} disabled={busy}>Cancel</Bt>
          <Bt full onClick={submit} disabled={busy}>{busy ? 'Saving…' : 'Save Adjustments'}</Bt>
        </div>
      </div>
    </div>
  );
};
